/**
 * Polish messages. The default locale and the complete one.
 *
 * Plural keys use i18next's suffixes, which follow CLDR — and **Polish has four
 * plural categories** (`one`, `few`, `many`, `other`), against English's two.
 * That is the reason this app uses i18next rather than a hand-rolled lookup: a
 * naive `count === 1 ? singular : plural` is wrong for 2, 5 and 22 in Polish,
 * and wrong in a way a Polish speaker notices immediately.
 *
 *   1 pracownik  ·  2 pracowników … no: 2 pracowników is `few` → "pracowników"
 *   1 osoba (one) · 2–4 osoby (few) · 5+ osób (many) · 1,5 osoby (other)
 */
export const pl = {
  app: {
    name: 'Overseer',
    tagline: 'Czas pracy i obecność',
  },
  access: {
    historySince: 'Dane od {{date}}.',
  },
  nav: {
    label: 'Nawigacja główna',
    dashboard: 'Pulpit',
    timesheet: 'Ewidencja',
    employees: 'Pracownicy',
    workLogs: 'Odczyty',
    daysOff: 'Nieobecności',
    cardReaders: 'Czytniki',
    referenceData: 'Słowniki',
    payroll: 'Wypłaty',
    reports: 'Raporty',
    users: 'Użytkownicy',
    roles: 'Role',
    companies: 'Firmy',
    sources: 'Źródła danych',
    logs: 'Logi',
  },
  auth: {
    signInTitle: 'Zaloguj się',
    signInSubtitle: 'Użyj adresu e-mail i hasła',
    email: 'Adres e-mail',
    password: 'Hasło',
    newPassword: 'Nowe hasło',
    confirmNewPassword: 'Powtórz nowe hasło',
    signIn: 'Zaloguj',
    signOut: 'Wyloguj',
    signingIn: 'Logowanie…',
    newPasswordRequired: 'Ustaw nowe hasło',
    newPasswordRequiredHint: 'To pierwsze logowanie na tym koncie. Ustaw własne hasło.',
    setPassword: 'Ustaw hasło',
    passwordsDoNotMatch: 'Hasła nie są identyczne',
    errors: {
      credentials: 'Nieprawidłowy adres e-mail lub hasło.',
      passwordPolicy: 'Hasło nie spełnia wymagań.',
      tooManyRequests: 'Zbyt wiele prób. Odczekaj chwilę.',
      network: 'Brak połączenia z serwerem uwierzytelniania.',
      unknown: 'Nie udało się zalogować. Spróbuj ponownie.',
    },
  },
  dashboard: {
    title: 'Pulpit',
    asOf: 'Stan na {{time}}',
    inNow: 'Obecnie w pracy',
    inNowEmpty: 'Nikt nie jest teraz w pracy.',
    closedToday: 'Zakończone dziś',
    staleOpen: 'Niezamknięte dni',
    staleOpenEmpty: 'Brak niezamkniętych dni. ',
    staleOpenCorrectHint:
      'Dni z wejściem, ale bez wyjścia, z wcześniejszych dat. Wybierz wiersz, aby poprawić.',
    inactive: {
      title: 'Potencjalnie nieaktywni',
      description: 'Brak godzin, nieobecności i wypłat między {{from}} a {{to}}, ani później.',
      empty: 'Wszyscy zatrudnieni byli ostatnio aktywni.',
      lastWorkDay: 'Ostatni dzień pracy',
      lastPaid: 'Ostatnia wypłata',
      deactivate: 'Dezaktywuj',
      deactivated: 'Pracownik {{name}} został dezaktywowany.',
      confirmTitle: 'Dezaktywować: {{name}}?',
      confirmWithAgreement:
        'Pracownik zostanie oznaczony jako nieaktywny, a jego bieżąca umowa zostanie zakończona w podanym dniu.',
      confirmNoAgreement:
        'Pracownik zostanie oznaczony jako nieaktywny. Nie ma obowiązującej umowy, więc nic więcej się nie zmieni.',
      endDate: 'Data zakończenia umowy',
      endDateHint: 'Ostatni dzień obowiązywania umowy. Nie może być wcześniejszy niż jej początek.',
    },
    employee: 'Pracownik',
    since: 'Od',
    elapsed: 'Czas',
    date: 'Data',
    start: 'Wejście',
    end: 'Wyjście',
    worked: 'Przepracowano',
    closedTodayEmpty: 'Nikt jeszcze nie zakończył dziś zmiany.',
    peopleIn_one: '{{count}} osoba w pracy',
    peopleIn_few: '{{count}} osoby w pracy',
    peopleIn_many: '{{count}} osób w pracy',
    peopleIn_other: '{{count}} osoby w pracy',
    peopleFinished_one: '{{count}} osoba zakończyła pracę',
    peopleFinished_few: '{{count}} osoby zakończyły pracę',
    peopleFinished_many: '{{count}} osób zakończyło pracę',
    peopleFinished_other: '{{count}} osoby zakończyły pracę',
  },
  reports: {
    title: 'Raporty',
    subtitle: 'Koszt pracowników i liczba osób w działach. Zamknięte miesiące z listy płac, kolejne 12 miesięcy jako prognoza.',
    subtitleHeadcount: 'Liczba osób w działach. Zamknięte miesiące z listy płac, kolejne 12 miesięcy jako prognoza.',
    notAvailable: 'Nie masz uprawnień do raportów kadrowych.',
    period: 'Okres raportu',
    previousYear: 'Poprzedni rok',
    nextYear: 'Następny rok',
    range: {
      label: 'Zakres',
      month: 'Miesiąc',
      ytd: 'Od początku roku',
    },
    monthPicker: 'Miesiąc',
    monthState: {
      closed: '{{month}} — zamknięty, z listy płac',
      forecast: '{{month}} — prognoza',
      forecastChecked: '{{month}} — prognoza; na otwartej liście płac sprawdzono {{checked}} z {{people}} osób',
      beyond: '{{month}} — poza horyzontem prognozy',
    },
    noClosedMonth: 'W {{year}} nie ma zamkniętego ani prognozowanego miesiąca.',
    unassigned: 'Bez działu',
    mom: 'm/m',
    yoy: 'r/r',
    comparedWith: 'wobec: {{period}}',
    comparedWithClosed: 'wobec: {{period}} — ostatni zamknięty miesiąc',
    versus: 'vs {{month}}',
    comparedYearAgo: 'wobec tego samego okresu rok wcześniej',
    noComparison: 'Poprzedni miesiąc nie jest zamknięty — bez porównania.',
    yoyMissing: 'Ten sam okres rok wcześniej nie jest zamknięty na liście płac — bez porównania.',
    tiles: {
      cost: 'Koszt razem · {{period}}',
      people: 'Osoby na liście płac',
      peopleAverage: 'Średnio osób w miesiącu',
      perHead: 'Koszt na osobę',
      perHeadMonth: 'Koszt na osobę / miesiąc',
      budget: 'Budżet · {{period}}',
      budgetNote: 'Cel kosztów — do ustawienia wkrótce',
      budgetTarget: 'Cel',
      budgetUsed: 'Wykonanie',
      soon: 'Wkrótce',
      monthly: 'Średnio miesięcznie',
      payA: 'Wypł. A',
      payB: 'Wypł. B',
      joined: 'Przyszło',
      left: 'Odeszło',
      formsA: 'UoP · Zlec.',
      formsB: 'B2B · Inne',
      distinct: 'Różnych osób',
      months: 'Miesięcy',
      forecastOf: 'w tym prognoza',
    },
    forecast: {
      chip: 'Prognoza',
      chipMixed: 'Z prognozą',
      title: 'Prognoza — {{period}}',
      titleMixed: 'Okres zawiera prognozę — {{months}}',
      text: 'To szacunek, nie lista płac. Liczby zmienią się, kiedy miesiąc zostanie zamknięty.',
      textMixed: '{{closed}} z listy płac, {{forecast}} to szacunek. Suma zmieni się, kiedy te miesiące zostaną zamknięte.',
      method:
        'Każda osoba z umową w mocy: jej wypłaty z ostatnich zamkniętych miesięcy, przesunięte przez stawkę z umowy, dni miesiąca i sezon z ewidencji rok wcześniej.',
      base: 'Podstawa: {{months}} z listy płac',
      checked: '{{month}}: {{checked}} z {{people}} osób już sprawdzonych na liście płac',
      horizon: 'Horyzont: do {{month}}',
      band: 'Możliwy błąd: ±{{percent}}',
      range: 'Zakres: {{low}} – {{high}}',
      rangeTitle: 'Część prognozowana może się mylić o tyle w każdą stronę — tyle wyniósł największy błąd w teście na zamkniętych miesiącach.',
      split: '{{paid}} zamknięte · ≈ {{forecast}} prognoza',
      about: 'Jak liczona jest prognoza',
      checkedShort: '{{checked}} z {{people}} sprawdzonych',
    },
    table: {
      title: 'Działy',
      subtitle: '{{period}} · kliknij dział, aby zobaczyć osoby według stanowisk',
      expandAll: 'Rozwiń wszystkie',
      collapseAll: 'Zwiń wszystkie',
      about: 'Co liczy tabela',
      fullScreen: 'Pełny ekran',
      hoursChange: 'Godziny miesiąc wcześniej, z nieobecnościami: {{hours}}',
      cost: 'Koszt',
      people: 'Ludzie',
      department: 'Dział',
      total: 'Razem',
      payA: 'Wypłata A',
      payB: 'Wypłata B',
      headcount: 'Osoby',
      headcountAverage: 'Śr. osób',
      perHead: 'Na osobę',
      company: 'Cała firma',
      noPosition: 'Bez stanowiska',
      hours: 'Godziny przepracowane, z ewidencji',
      hoursForecast: 'Godziny — prognoza',
      hoursWithAbsence: 'Przepracowane {{worked}} i nieobecności {{absent}}, z ewidencji',
      absenceTitle: 'Nieobecność — wliczona w godziny',
      absence: {
        holiday: 'urlop {{hours}}',
        sick: 'L4 {{hours}}',
        care: 'opieka {{hours}}',
      },
      checkedTitle: 'Wiersz sprawdzony na otwartej liście płac — kwota z listy, nie z prognozy',
      fromAgreement: 'z umowy',
      fromAgreementTitle: 'Jeszcze bez wypłaty — kwota z umowy i działu',
      new: 'nowy',
      movedFrom: 'z: {{department}}',
      monthsOf: '{{count}} z {{total}} mies.',
      movedTo: 'do: {{department}}',
      left: 'odejście',
      leftPaid: 'Miesiąc wcześniej: {{amount}}',
      splitOthers_one: '+{{count}} dział',
      splitOthers_few: '+{{count}} działy',
      splitOthers_many: '+{{count}} działów',
      splitOthers_other: '+{{count}} działu',
      splitTitle: 'Podział między działy: {{parts}}',
      splitHidden: 'pozostałe działy',
    },
    footnote:
      'Osoby — ci, którym zamknięta lista płac miesiąca wypłaciła więcej niż 0 zł. Wypłata A: koszt pracodawcy bez zwrotów, z fakturami; wypłata B: wynagrodzenie B, premia B i Service Charge.',
    footnoteHeadcount: 'Osoby — ci, którym zamknięta lista płac miesiąca wypłaciła więcej niż 0 zł.',
    footnoteForecast:
      'W prognozie osoby to ci, którzy według swoich ostatnich miesięcy dostaną wypłatę; kto na otwartej liście płac jest już sprawdzony, ma kwotę z listy.',
  },
  timesheet: {
    employee: 'Pracownik',
    total: 'Razem',
    dayHours: 'Godziny dziennie',
    empty: 'Brak zapisów w tym miesiącu dla wybranych filtrów.',
    today: 'Dziś',
    previousMonth: 'Poprzedni miesiąc',
    nextMonth: 'Następny miesiąc',
    showCosts: 'Pokaż koszty',
    showHours: 'Pokaż godziny',
    // Arkusz zawiera wyłącznie godziny — stąd etykieta mówi wprost, czego
    // dotyczy. „Eksport”, który po cichu pomija koszty, wygląda na zepsuty.
    export: {
      open: 'Eksportuj godziny',
      running: 'Przygotowuję…',
    },
    // Ten sam miesiąc, tylko w pieniądzach. Widoczne wyłącznie dla kadr.
    openPayroll: 'Wypłaty',
    sortBy: 'Sortuj',
    // Dwie opcje zamiast jednej „Nazwisko”, która sortowała po imieniu.
    sortLastName: 'Nazwisko',
    sortFirstName: 'Imię',
    sortHours: 'Godziny',
    sortWarnings: 'Do poprawy',
    /** Filtr listy obecności: czy czas pracy tej osoby jest w ogóle rejestrowany. */
    countsHours: 'Godziny',
    hoursCounted: 'Rejestrowane',
    hoursNotCounted: 'Nierejestrowane',
    clearFilters: 'Wyczyść',
    company: 'Firma',
    department: 'Dział',
    position: 'Stanowisko',
    all: 'Wszystkie',
    positionUnavailable: '{{name}} (nie w tym dziale)',
    inactive: 'Już nie pracuje',
    gridLabel: 'Ewidencja miesięczna — pracownicy w wierszach, dni w kolumnach',
    noRecord: 'Brak zapisu',
    addRecord: 'Brak zapisu — kliknij, aby dodać',
    /*
     * Miesiąc zamknięty przez kadry. Komunikat mówi, co jest teraz niemożliwe i
     * co trzeba zrobić, żeby było — samo „miesiąc zamknięty” zostawia człowieka
     * z wygaszonym ekranem i bez następnego kroku.
     *
     * „Zablokowany” byłoby mylące: blokada kojarzy się z awarią albo z brakiem
     * uprawnień, a to jest normalny stan miesiąca po zamknięciu wypłat.
     */
    lock: {
      closed: 'Miesiąc jest zamknięty — zapisy są tylko do odczytu. Aby coś zmienić, otwórz miesiąc ponownie.',
      // Imię i nazwisko z odpowiedzi serwera; interpolowane, a nie sklejane,
      // bo w angielskiej wersji ta sama informacja stoi w innym miejscu zdania.
      closedBy:
        'Miesiąc zamknął(-ęła) {{name}} — zapisy są tylko do odczytu. Aby coś zmienić, otwórz miesiąc ponownie.',
      panelReadOnly: 'Miesiąc zamknięty. Tego dnia nie można edytować ani usunąć.',
      panelEnded: 'Miesiąc się skończył. Ten dzień poprawia teraz tylko administrator HR.',
    },
    absenceRange: 'od {{from}} do {{to}}',
    summary: {
      hours: 'Godziny',
      daysWorked: 'Dni pracy',
      daysOff: 'Nieobecności',
    },
    totals: {
      worked: 'Praca',
      /**
       * Nagłówek jednej kolumny podsumowania: godziny pracy i nieobecności w dniach.
       *
       * Cztery kolumny zwinięto w jedną, więc `workedShort` samo w sobie już nie
       * opisuje tego, co jest pod nagłówkiem. Pełne nazwy obu liczb są w tooltipie.
       */
      summaryShort: 'P / N',
      /** Nieobecności w dniach — pod godzinami w kolumnie podsumowania. */
      dayCount_one: '{{count}} dzień',
      dayCount_few: '{{count}} dni',
      dayCount_many: '{{count}} dni',
      dayCount_other: '{{count}} dnia',
      /**
       * Tooltip belki pod sumą dnia. `hours` to odchyłka, `average` to
       * średnia, od której belka jest rysowana — obie jako `H:MM`, jak
       * każda inna długość na tym ekranie.
       */
      aboveAverage: '{{hours}} powyżej średniej {{average}}',
      belowAverage: '{{hours}} poniżej średniej {{average}}',
      atAverage: 'równo ze średnią {{average}}',
    },
    costs: {
      title: 'Koszty',
      gross: 'Brutto',
      employerCost: 'Koszt pracodawcy',
      toDate: 'Do dnia {{day}}',
      forecast: 'Prognoza miesiąca',
      byDepartment: 'Według działu',
      // Which figure the reader is looking at. An estimate presented as settled is
      // worse than no figure, so the source is always named.
      source: {
        estimate: 'Szacunek na podstawie umów i zapisanych godzin. Miesiąc nie jest zamknięty.',
        payroll: 'Dane z systemu kadrowego. Miesiąc zamknięty.',
      },
    },
    type: {
      auto: 'Z czytnika',
      manual: 'Wpisane ręcznie',
      holiday: 'Urlop',
      sick: 'Zwolnienie lekarskie',
      care: 'Opieka',
    },
    typeShort: {
      auto: 'A',
      manual: 'M',
      holiday: 'U',
      sick: 'L4',
      care: 'OP',
    },
    logType: {
      entry: 'wejście',
      exit: 'wyjście',
    },
    panel: {
      close: 'Zamknij',
      start: 'Wejście',
      end: 'Wyjście',
      duration: 'Czas pracy',
      startDate: 'Od dnia',
      endDate: 'Do dnia',
      type: 'Typ',
      /**
       * Typ nie jest polem do wyboru — ustawia go serwer. Te dwa zdania mówią,
       * skąd się wziął, bo bez nich pole tylko pokazuje słowo bez znaczenia.
       */
      typeHint: {
        auto: 'Dzień z czytnika. Przeliczenie miesiąca może go zmienić lub usunąć.',
        manual: 'Wpis ręczny. Przeliczenie miesiąca go nie zmieni.',
      },
      absence: 'Nieobecność',
      workLocked: 'Aby wpisać godziny pracy, usuń tę nieobecność i dodaj dzień pracy.',
      /**
       * Rodzaj edytowanego wpisu, jako nagłówek.
       *
       * Oba tryby nie mają już wspólnych pól, więc trzeba powiedzieć, który jest
       * otwarty: nieobecność bez godzin i dzień pracy bez przycisków
       * nieobecności wyglądają jak ten sam formularz z brakującą połową.
       */
      modeWork: 'Godziny pracy',
      modeAbsence: 'Nieobecność',
      /** Dwie zakładki okna dodawania. Te same rodzaje, tyle że do wyboru. */
      tabWork: 'Godziny pracy',
      tabAbsence: 'Nieobecność',
      saved: 'Dzień pracy zapisany',
      absenceSaved: 'Nieobecność zapisana',
      created: 'Dzień pracy dodany',
      absenceCreated: 'Nieobecność dodana',
      deleted: 'Dzień pracy usunięty',
      missingAbsenceType: 'Wybierz rodzaj nieobecności.',
      description: 'Opis',
      noEnd: 'brak',
      save: 'Zapisz',
      saving: 'Zapisywanie…',
      delete: 'Usuń',
      confirmDelete: 'Usunąć ten dzień pracy? Tego nie można cofnąć.',
      history: 'Historia zmian',
      noHistory: 'Brak zapisanych zmian.',
      auditedFrom: 'Historia zmian jest zapisywana od {{date}}.',
      auditingNotStarted: 'Historia zmian jeszcze nie zawiera żadnych wpisów.',
      workLogs: 'Odczyty karty',
      noWorkLogs: 'Brak odczytów — dzień wpisany ręcznie.',
      opens: 'wejście dnia',
      closes: 'wyjście dnia',
      badTime: 'Godzina musi mieć format GG:MM.',
      badDates: 'Data końca nie może być wcześniejsza niż data początku.',
      missingDate: 'Podaj datę początku i końca nieobecności.',
      nextDay: '+1 dzień',
      nextDayHint: 'Wyjście wypada następnego dnia — zmiana nocna.',
      action: {
        create: 'Utworzono',
        update: 'Zmieniono',
        delete: 'Usunięto',
      },
    },
  },
  /*
   * Wypłaty.
   *
   * `field` to nagłówek kolumny, `fieldLong` — pełna nazwa w podpowiedzi i w
   * nazwie dostępnej. Kiedyś w `field` stały nazwy pól z kodu (`podSpol`,
   * `zusSuma`), bo miały odpowiadać arkuszowi kadr jeden do jednego. Nie
   * odpowiadały: `podSpol` nie jest ani nazwą z arkusza, ani polskim słowem.
   *
   * Teraz każdy nagłówek jest słowem. Kolumna ma 116 px, więc dłuższe nazwy są
   * skracane kropką — tak, jak skraca się po polsku — a całość jest w `fieldLong`.
   * `FP`, `FGŚP` i `ZUS` zostają: to nazwy ustawowe, nie skróty do rozszyfrowania.
   */
  payroll: {
    employee: 'Pracownik',
    totals: 'Razem',
    empty: 'Brak wierszy wypłat w tym miesiącu. Zaimportuj plik z kadr.',
    emptyFiltered: 'Żaden wiersz nie pasuje do wybranych filtrów.',
    filteredCount: 'Widocznych {{shown}} z {{total}}',
    clearFilters: 'Wyczyść filtry',
    scopedNote: 'Widzisz wiersze swoich działów. Sumy i liczba sprawdzonych dotyczą tylko ich.',
    companyHint: 'Wypłata dotyczy firmy {{company}} — ten pracownik ma w tym miesiącu więcej niż jeden wiersz.',
    sort: {
      label: 'Sortowanie',
      lastName: 'Nazwisko A–Z',
      lastNameDesc: 'Nazwisko Z–A',
      firstName: 'Imię A–Z',
      firstNameDesc: 'Imię Z–A',
      total: 'Razem rosnąco',
      totalDesc: 'Razem malejąco',
      ready: 'Najpierw niegotowe',
      readyDesc: 'Najpierw gotowe',
    },
    today: 'Dziś',
    previousMonth: 'Poprzedni miesiąc',
    nextMonth: 'Następny miesiąc',
    openTimesheet: 'Ewidencja',
    ready: 'Gotowe',
    readyLong: 'Wiersz sprawdzony przez człowieka',
    /**
     * Stan wiersza. Wyliczany z `importedAt` i `isReady`, nie przechowywany —
     * czwarty stan jest więc niemożliwy.
     *
     * `column` to nagłówek kolumny, pozostałe trzy to treść plakietki.
     */
    rowStatus: {
      column: 'Stan',
      todo: 'Do zrobienia',
      imported: 'Z importu',
      approved: 'Zatwierdzone',
    },
    /**
     * Norma czasu pracy z art. 130 Kodeksu pracy.
     *
     * Tylko dla umów o pracę: zlecenie rozlicza się z godzin faktycznych, a B2B
     * godzin nie ma. Gdy tabela świąt się skończy, nie pokazujemy nic — liczba
     * wzięta z powietrza wygląda na ekranie identycznie jak prawdziwa.
     */
    norm: 'Norma: {{hours}} h',
    normLong: 'Wymiar czasu pracy w tym miesiącu — art. 130 Kodeksu pracy',
    readyFor: 'Oznacz jako sprawdzone: {{name}}',
    payment: {
      column: 'Forma płatności',
      long: 'Jak wypłacane jest wynagrodzenie netto: przelewem albo gotówką z kasy A firmy. Wybierz przed oznaczeniem Gotowe — dopiero Gotowe wysyła wypłatę gotówkową do kasy. Nowy wiersz przejmuje formę z poprzedniego miesiąca.',
      transfer: 'Przelew',
      cash: 'Gotówka',
      for: 'Forma płatności: {{name}}',
    },
    tableLabel: 'Wypłaty za miesiąc — pracownicy w wierszach, składniki w kolumnach',
    cellLabel: '{{field}} — {{name}}',
    // Rozbieżność z importem. Zdanie mówi, co było w pliku, bo sama ramka wokół
    // pola informuje tylko, że coś się nie zgadza, a nie z czym.
    diverged: '{{field}} — {{name}}: zmieniono ręcznie. Import podał {{imported}}.',
    bailiffHint: 'Zajęcie komornicze {{amount}} — netto to pełna kwota; komornik zabiera tę część.',
    // Zasiłek z ZUS. Firma go tylko wypłaca i dostaje zwrot, więc nie jest kosztem
    // zatrudnienia — zdanie musi to powiedzieć, bo sama kwota tego nie mówi.
    sickBenefitHint: 'Zasiłek z ZUS {{amount}} — firma wypłaca, ZUS zwraca, więc kwota jest odjęta od kosztu pracodawcy.',
    // Wynajem. Suma wiersza jest zwykle ujemna i bez tego zdania wygląda na błąd.
    rentedEmployeeHint: 'Wynajęty z marżą {{margin}} — firma wynajmująca zwraca koszt wraz z marżą ({{amount}}), więc suma tego wiersza jest zwykle ujemna.',
    field: {
      grossPay: 'Brutto',
      socialBase: 'Podst. społ.',
      pensionEmployer: 'Emeryt.',
      disabilityEmployer: 'Rentowa',
      accidentEmployer: 'Wypadk.',
      labourFund: 'FP',
      guaranteedBenefitsFund: 'FGŚP',
      contributionsTotal: 'Składki ZUS',
      employerCost: 'Koszt prac.',
      // Znak w nagłówku, bo kolumna dodatnich liczb, która pomniejsza sumę, jest
      // tu najłatwiejsza do przeczytania na odwrót.
      benefitsReimbursed: 'Zasiłki (−)',
      invoices: 'Faktury',
      rentedMargin: 'Marża',
      // Etykieta na karcie wynajmu w rozwiniętym wierszu. „Zwrot", nie „Faktura",
      // bo w tym miejscu chodzi o to, skąd bierze się ujemna kwota obok.
      rentedReimbursement: 'Zwrot od firmy wynajmującej',
      payB: 'Wynagr. B',
      payBBonus: 'Premia B',
      serviceCharge: 'Service Charge',
      hoursWorkedPayroll: 'Godziny (kadry)',
      hoursWorkedTimesheet: 'Godziny (RCP)',
      payrollTotal: 'Suma',
      netPay: 'Netto',
      netCash: 'Gotówka',
      netTransfer: 'ROR',
      pensionEmployee: 'Emeryt. (pr.)',
      disabilityEmployee: 'Rentowa (pr.)',
      sicknessEmployee: 'Chorobowe',
      healthContribution: 'Zdrowotna',
      taxAdvance: 'Zal. pod.',
      taxDeductibleCosts: 'Koszty',
      taxRelief: 'Ulga',
      taxRelinquished: 'Zan. pod.',
    },
    payBLock: 'Zablokuj Wynagr. B',
    payBUnlock: 'Odblokuj Wynagr. B',
    invoicesLock: 'Zablokuj Faktury',
    invoicesUnlock: 'Odblokuj Faktury',
    fieldLong: {
      grossPay: 'Wynagrodzenie brutto',
      // Podstawa, nie kwota — dlatego nie ma jej w podsumowaniu.
      socialBase: 'Podstawa społeczna — baza do składek, nie sumuje się',
      pensionEmployer: 'Składka emerytalna pracodawcy',
      disabilityEmployer: 'Składka rentowa pracodawcy',
      accidentEmployer: 'Składka wypadkowa',
      labourFund: 'Fundusz Pracy',
      guaranteedBenefitsFund: 'Fundusz Gwarantowanych Świadczeń Pracowniczych',
      contributionsTotal: 'Składki ZUS razem — emerytalna, rentowa, wypadkowa, FP i FGŚP',
      employerCost: 'Koszt pracodawcy — brutto powiększone o składki',
      // Zwrot, nie obciążenie — dlatego odejmuje się od sumy. Bez tego zdania
      // ujemna suma wygląda na błąd rachunku.
      benefitsReimbursed: 'Zasiłki — kwota zwracana, pomniejsza sumę',
      invoices: 'Faktury B2B',
      // Puste pole to „nie wynajęty", a nie zero procent — to dwa różne stany i
      // zdanie musi je rozróżnić, bo ta sama komórka wyraża oba.
      rentedMargin:
        'Marża przy wynajmie pracownika — cały koszt zwraca inna firma, powiększony o ten procent. Puste pole oznacza brak wynajmu.',
      payB: 'Wynagrodzenie B — kwota własna firmy',
      payBBonus: 'Premia do wynagrodzenia B — kwota własna firmy',
      serviceCharge: 'Service Charge — kwota własna firmy',
      hoursWorkedPayroll: 'Godziny wypracowane wg importu z kadr',
      hoursWorkedTimesheet: 'Godziny z ewidencji czasu, zaokrąglone do pełnych godzin',
      payrollTotal: 'Suma wynagrodzenia — wyliczana, nie do edycji',
      netPay: 'Do wypłaty — netto',
      netCash: 'Część gotówkowa (GOT)',
      netTransfer: 'Przelew na konto (ROR)',
      pensionEmployee: 'Składka emerytalna pracownika',
      disabilityEmployee: 'Składka rentowa pracownika',
      sicknessEmployee: 'Składka chorobowa pracownika — kreska znaczy, że nie dotyczy',
      healthContribution: 'Składka zdrowotna',
      taxAdvance: 'Zaliczka na podatek',
      taxDeductibleCosts: 'Koszty uzyskania przychodu',
      taxRelief: 'Ulga podatkowa',
      taxRelinquished: 'Zaniechany podatek',
    },
    /** Nagłówki pasm grupujących kolumny z kwotami. */
    /**
     * Grupy umów, na jakie podzielone są wiersze.
     *
     * Nazwa samej grupy to `agreements.form.*` — używamy jej ponownie, zamiast
     * pisać drugi raz. Drugie brzmienie „UoP” byłoby drugim miejscem do pilnowania.
     */
    group: {
      count_one: '{{count}} wiersz',
      count_few: '{{count}} wiersze',
      count_many: '{{count}} wierszy',
      count_other: '{{count}} wiersza',
      collapse: 'Zwiń grupę {{form}}',
      expand: 'Rozwiń grupę {{form}}',
      subtotal: 'Razem — {{form}}',
    },
    /** Forma umowy przy nazwisku. B2B bez tego czyta się jak brak wypłaty. */
    agreementFormEnded: 'Forma umowy: {{form}} — umowa nie obowiązuje w tym miesiącu',
    status: {
      open: 'Otwarty',
      closed: 'Zamknięty',
    },
    summary: {
      label: 'Podsumowanie miesiąca',
      netPay: 'Wypłaty (Netto)',
      taxAdvance: 'Podatki (US)',
      contributions: 'Składki (ZUS)',
      // „Faktury B2B", nie samo „Faktury". Kwota za wynajem jest wykazana osobno
      // w wierszu poniżej, więc dwie sąsiednie pozycje muszą się różnić nazwą —
      // inaczej czyta się je jak to samo policzone dwa razy.
      invoices: 'Faktury B2B',
      rentedReimbursement: 'Zwrot za wynajem',
      payB: 'Wypłaty B',
      payBBonus: 'Premie B',
      serviceCharge: 'Service Charge',
      ready: 'Sprawdzone wiersze',
      readyOf: '{{ready}} z {{active}}',
    },
    /** Otwarcie wyliczenia wiersza. Z nazwiskiem — kolumna samych „rozwiń” nic nie mówi. */
    expand: 'Pokaż pasek wynagrodzenia: {{name}}',
    collapse: 'Ukryj pasek wynagrodzenia: {{name}}',
    expandColumn: 'Pasek wynagrodzenia',
    /** Ołówek przy nazwisku: ten wiersz poprawiono ręcznie po imporcie. */
    overridden: 'Zmieniono ręcznie: {{fields}}',
    payslip: {
      label: 'Pasek wynagrodzenia — {{name}}',
      contributions: 'Składki pracodawcy',
      cost: 'Koszt',
      // Nie samo „Godziny”: karta nosiłaby tę samą nazwę co jedyna pozycja w niej.
      hours: 'Czas pracy',
      norm: 'Norma miesięczna',
      /** Dlaczego norma tej osoby nie jest normą miesiąca — niepełny etat, dzień 7 h, albo oba. */
      normBasis: 'Przeliczona: {{etat}} etatu',
      normBasis_disabled: 'Przeliczona: {{etat}} etatu, dzień 7 h',
      normDiff: 'Wobec normy',
      imported: 'Import: {{when}}',
      notImported: 'Bez importu',
      employeeZus: 'Składki pracownika',
      tax: 'Podatek i netto',
      elements: 'Składniki',
      payB: 'Wynagrodzenie B',
      payBSuggested: 'Sugestia',
      payBLocked: 'Kwota zablokowana — nie nadpisuje się przy odświeżeniu.',
      rented: 'Wynajem',
      rentedBase: 'Podstawa do zwrotu',
      // Zdanie mówi, co z czym pomnożono i co z tego wyszło. Kolumna liczb tego
      // nie powie, bo znak odwraca się dopiero na końcu.
      rentedExplain:
        'Koszt {{base}} powiększony o marżę {{margin}} daje {{reimbursement}}. Tę kwotę zwraca firma wynajmująca, dlatego w kolumnie „Faktury" jest ujemna — to przychód, nie koszt.',
      rentedSource:
        'Marżę ustawia się na karcie pracownika. Zmiana obejmuje wszystkie otwarte miesiące; miesiąc zamknięty zachowuje wartość, z którą został rozliczony.',
      payBExplain: {
        none: 'Brak schematu B i brak godzin ponad normę — sugestia wynosi 0.',
        fixed_compensation_month:
          'Stała kompensata miesięczna: {{value}} − netto {{netPay}} = {{amount}}.',
        fixed_bonus_month: 'Stała premia miesięczna: {{amount}}.',
        fixed_compensation_hour:
          'Stała kompensata godzinowa: {{value}} × {{hours}} h = {{target}}, minus netto {{netPay}} = {{amount}}.',
        /** Przed mnożeniem, gdy nieobecności sprawiają, że godziny różnią się od przepracowanych. */
        hoursFrom:
          'Godziny do wypłaty: przepracowane {{worked}} h + urlop {{holidayDays}} × 8 h = {{holidayHours}} h + chorobowe/opieka {{sickCareDays}} × 8 h × 80% = {{sickCareHours}} h, razem {{total}} h.',
        overtime:
          'Ponad normę ({{hours}} h − norma {{monthNorm}} h = {{excess}} h) × stawka godzinowa {{hourlyRate}} (z umowy, {{rateUnit}}) = {{amount}}.',
      },
      /**
       * Wyliczenie wynagrodzenia B, jedna pozycja w wierszu.
       *
       * Zastępuje zdania z `payBExplain` tam, gdzie jest to faktycznie rachunek.
       * Zdania zostają: są nadal używane dla stałej premii i braku schematu, gdzie
       * tabela byłaby nagłówkiem nad jednym wierszem.
       */
      payBWorking: {
        worked: 'Godziny przepracowane',
        holiday: 'Urlop ({{days}} × 8 h)',
        sickCare: 'Chorobowe / opieka ({{days}} × 8 h × 80%)',
        hoursTotal: 'Godziny do wypłaty',
        rate: 'Kompensata godzinowa',
        product: '{{rate}} × {{hours}}',
        target: 'Kwota docelowa',
        netPay: 'Netto',
        result: 'Wynagrodzenie B',
        hoursAgainstNorm: 'Godziny przepracowane (norma {{norm}})',
        excess: 'Ponad normę',
        hourlyRate: 'Stawka godzinowa',
      },
    },
    hours: {
      /** Przepracowane minus opłacone przez kadry. Obie strony to czas w pracy. */
      overtime: 'Nadgodziny (RCP − kadry)',
      holiday_one: 'Urlop ({{count}} dzień)',
      holiday_few: 'Urlop ({{count}} dni)',
      holiday_many: 'Urlop ({{count}} dni)',
      holiday_other: 'Urlop ({{count}} dnia)',
      sickCare_one: 'Chorobowe / opieka ({{count}} dzień × 80%)',
      sickCare_few: 'Chorobowe / opieka ({{count}} dni × 80%)',
      sickCare_many: 'Chorobowe / opieka ({{count}} dni × 80%)',
      sickCare_other: 'Chorobowe / opieka ({{count}} dnia × 80%)',
      payable: 'Godziny do wypłaty',
      /** Dni weekendowe i świąteczne objęte nieobecnością, za które się nie płaci. */
      skipped_one: 'Nie liczono: {{count}} dzień przypadający na weekend lub święto.',
      skipped_few: 'Nie liczono: {{count}} dni przypadających na weekend lub święto.',
      skipped_many: 'Nie liczono: {{count}} dni przypadających na weekend lub święto.',
      skipped_other: 'Nie liczono: {{count}} dnia przypadającego na weekend lub święto.',
      cellLabel:
        '{{name}}: ewidencja {{timesheet}} h, kadry {{imported}} h, różnica {{diff}}',
      timesheetOnly: '{{name}}: ewidencja {{timesheet}} h',
    },
    normMark: {
      disabled: '7 h',
      hint: 'Ta osoba ma w tym miesiącu {{norm}} h, nie standardową normę — {{basis}}.',
    },
    /*
     * Miesiąc zamknięty. Komunikat mówi, czego teraz nie można i co zrobić, żeby
     * było można — „miesiąc zamknięty” samo w sobie zostawia człowieka przed
     * wygaszonym ekranem bez następnego kroku.
     */
    lock: {
      closed: 'Miesiąc jest zamknięty — kwoty są tylko do odczytu. Aby coś zmienić, otwórz miesiąc ponownie.',
      closedBy:
        'Miesiąc zamknął(-ęła) {{name}} — kwoty są tylko do odczytu. Aby coś zmienić, otwórz miesiąc ponownie.',
      // Zapis odrzucony już po wejściu na ekran: ktoś zamknął miesiąc w
      // międzyczasie. Osobne zdanie, bo tylko na tę przyczynę użytkownik może
      // zareagować.
      refused: 'Miesiąc został w międzyczasie zamknięty. Zmiana nie została zapisana.',
      // Zapis odrzucony, bo wiersz jest już zatwierdzony — najczęściej przez kogoś
      // innego albo w innej karcie. Zdanie mówi, jak to odwrócić, bo cofnięcie
      // zatwierdzenia robi ten sam użytkownik na tym samym ekranie.
      entryRefused:
        'Ten wiersz jest zatwierdzony, więc kwoty są tylko do odczytu. Cofnij zatwierdzenie, aby coś zmienić.',
    },
    export: {
      open: 'Eksportuj',
      running: 'Przygotowuję…',
    },
    import: {
      open: 'Importuj z kadr',
      title: 'Import wypłat — {{month}}',
      what: 'Wczytaj listę PDF albo arkusz z systemu kadrowego. Nic nie zostanie zapisane, dopóki nie potwierdzisz.',
      file: 'Plik z kadr',
      fileHint: 'PDF z Optimy albo XLSX / CSV, do 2 MB.',
      reading: 'Czytam plik…',
      closed: 'Miesiąc jest zamknięty — podgląd działa, ale zapis nie. Najpierw otwórz miesiąc ponownie.',
      matched_one: 'Dopasowano: {{count}} wiersz',
      matched_few: 'Dopasowano: {{count}} wiersze',
      matched_many: 'Dopasowano: {{count}} wierszy',
      matched_other: 'Dopasowano: {{count}} wiersza',
      matchedNote: 'Te wiersze zostaną zapisane po potwierdzeniu. Zmień osobę albo odrzuć wiersz, jeśli dopasowanie jest złe.',
      matchedEmpty: 'Żaden wiersz z pliku nie został dopasowany do pracownika.',
      locked_one: 'Zatwierdzone, nie zaimportowano: {{count}} wiersz',
      locked_few: 'Zatwierdzone, nie zaimportowano: {{count}} wiersze',
      locked_many: 'Zatwierdzone, nie zaimportowano: {{count}} wierszy',
      locked_other: 'Zatwierdzone, nie zaimportowano: {{count}} wiersza',
      lockedNote:
        'Te osoby mają już zatwierdzony wiersz w tym miesiącu. Import ich nie zmienia. Jeśli chcesz nowe kwoty, najpierw wycofaj zatwierdzenie na wierszu.',
      lockedEmpty: 'Żaden zatwierdzony wiersz nie blokuje importu.',
      lockedDone_one: '{{count}} wiersz pominięto, bo jest zatwierdzony.',
      lockedDone_few: '{{count}} wiersze pominięto, bo są zatwierdzone.',
      lockedDone_many: '{{count}} wierszy pominięto, bo są zatwierdzone.',
      lockedDone_other: '{{count}} wiersza pominięto, bo jest zatwierdzony.',
      matchedBy: {
        payrollId: 'po numerze kadrowym',
        name: 'po imieniu i nazwisku',
        manual: 'przypisane ręcznie',
      },
      assign: 'Przypisz do',
      discard: 'Odrzuć',
      unassigned: 'Nikt — nie zostanie zapisany',
      source: 'Lista {{symbol}} za {{from}} – {{to}}',
      sourceMismatch: 'Ten plik jest za inny miesiąc niż ten na ekranie.',
      payerName: 'Płatnik w Optimie: {{name}}',
      company: 'Firma',
      companyPick: 'Wybierz firmę…',
      companyNeeded: 'Powiąż tę listę Optima z firmą',
      companyNeededHint:
        'Nazwa płatnika nie jest jeszcze powiązana. Wybierz firmę raz — zapisze się przy tej firmie w Administracja → Firmy, a następny import tego płatnika dopasuje się sam.',
      companyLinked: 'Firma: {{name}}',
      companyRequired: 'Wybierz firmę przed potwierdzeniem importu.',
      form: 'Umowa w tym pliku',
      formDerived: 'Z pliku odczytano: {{form}}. Zmień, jeśli to błąd.',
      formUnknown: 'Plik nie podaje rodzaju umowy. Wybierz go.',
      overwrites: 'nadpisze istniejący wiersz',
      unmatched_one: 'Bez dopasowania: {{count}} wiersz',
      unmatched_few: 'Bez dopasowania: {{count}} wiersze',
      unmatched_many: 'Bez dopasowania: {{count}} wierszy',
      unmatched_other: 'Bez dopasowania: {{count}} wiersza',
      unmatchedNote: 'Przypisz każdy wiersz do osoby albo go odrzuć. Odrzucone nie zostaną zapisane.',
      unmatchedEmpty: 'Każdy wiersz z pliku ma swojego pracownika.',
      unnamedRow: 'Wiersz bez nazwiska i numeru',
      reason: {
        'no-match': 'nie znaleziono takiego pracownika',
        // Nigdy nie zgadujemy: poprawką jest numer kadrowy przy pracowniku.
        'ambiguous-name': 'dwie osoby o tym samym nazwisku — uzupełnij numer kadrowy',
        'no-identifier': 'wiersz nie podaje ani numeru kadrowego, ani nazwiska',
        // Nie zgadujemy, na którą umowę wpisać kwoty: poprawką jest kolumna "Umowa" w pliku.
        'form-mismatch': 'ta osoba nie ma takiej umowy w tym miesiącu — uzupełnij kolumnę "Umowa"',
      },
      confirm_one: 'Zapisz {{count}} wiersz',
      confirm_few: 'Zapisz {{count}} wiersze',
      confirm_many: 'Zapisz {{count}} wierszy',
      confirm_other: 'Zapisz {{count}} wiersza',
      applying: 'Zapisuję…',
      done_one: 'Zapisano {{count}} wiersz, w tym nadpisano {{overwritten}}.',
      done_few: 'Zapisano {{count}} wiersze, w tym nadpisano {{overwritten}}.',
      done_many: 'Zapisano {{count}} wierszy, w tym nadpisano {{overwritten}}.',
      done_other: 'Zapisano {{count}} wiersza, w tym nadpisano {{overwritten}}.',
      cancel: 'Anuluj',
      close: 'Zamknij',
    },
    close: {
      open: 'Zamknij miesiąc',
      title: 'Zamknij {{month}}',
      // Zamknięcie blokuje nie tylko wypłaty, ale całą ewidencję tego miesiąca.
      // To jest ta część, której nikt się nie spodziewa, więc stoi na początku.
      what: 'Zamknięcie blokuje wszystkie zmiany w tym miesiącu — także dni pracy, nieobecności i korekty.',
      readiness: 'Sprawdzone wiersze: {{ready}} z {{active}}.',
      allReady: 'Wszyscy pracownicy z umową w tym miesiącu mają sprawdzony wiersz.',
      blocking_one: 'Zamknięcie blokuje {{count}} osoba',
      blocking_few: 'Zamknięcie blokują {{count}} osoby',
      blocking_many: 'Zamknięcie blokuje {{count}} osób',
      blocking_other: 'Zamknięcie blokuje {{count}} osoby',
      blockingNote: 'Sama liczba nie mówi, kogo szukać — dlatego lista jest z nazwiskami.',
      notTicked: 'wiersz niesprawdzony',
      // To jest inny przypadek niż powyżej i inna poprawka: tu trzeba wiersz
      // dopiero utworzyć albo zaimportować.
      noEntry: 'brak wiersza',
      confirm_one: 'Zamknij miesiąc — {{count}} wiersz',
      confirm_few: 'Zamknij miesiąc — {{count}} wiersze',
      confirm_many: 'Zamknij miesiąc — {{count}} wierszy',
      confirm_other: 'Zamknij miesiąc — {{count}} wiersza',
      closing: 'Zamykam…',
      done: 'Miesiąc został zamknięty.',
      cancel: 'Anuluj',
    },
    reopen: {
      open: 'Otwórz ponownie',
      title: 'Otwórz ponownie {{month}}',
      what: 'Ponowne otwarcie cofa zamknięcie i znów pozwala zmieniać kwoty oraz ewidencję tego miesiąca.',
      // Powód jest wymagany i to jest cała idea tego okna: sam wpis „kto”
      // niewiele mówi bez „dlaczego”.
      reason: 'Powód ponownego otwarcia',
      reasonHint_one: 'Co najmniej {{count}} znak. Powód trafia do rejestru zmian i przeczyta go człowiek.',
      reasonHint_few: 'Co najmniej {{count}} znaki. Powód trafia do rejestru zmian i przeczyta go człowiek.',
      reasonHint_many: 'Co najmniej {{count}} znaków. Powód trafia do rejestru zmian i przeczyta go człowiek.',
      reasonHint_other: 'Co najmniej {{count}} znaku. Powód trafia do rejestru zmian i przeczyta go człowiek.',
      confirm: 'Otwórz miesiąc ponownie',
      reopening: 'Otwieram…',
      done: 'Miesiąc został otwarty ponownie.',
      cancel: 'Anuluj',
    },
    restore: {
      open: 'Przywróć miesiąc',
      title: 'Przywróć {{month}}',
      what: 'Usuwa niezatwierdzone wiersze wypłat z tego miesiąca i tworzy puste wiersze na podstawie dzisiejszych pracowników i umów. Zatwierdzone wiersze zostają bez zmian. Operacja nieodwracalna.',
      closed: 'Miesiąc jest zamknięty — przywracanie nie działa. Najpierw otwórz miesiąc ponownie.',
      entryCount: 'Wiersze do usunięcia',
      entryNote: 'Znikają importy i ręczne poprawki na niezatwierdzonych wierszach',
      keptCount: 'Zatwierdzone wiersze, które zostają',
      keptNote: 'Zostają bez zmian, razem z kwotami',
      expectedCount: 'Wiersze do odtworzenia',
      expectedNote: 'Aktywni pracownicy z umową obowiązującą w tym miesiącu',
      warning_one: 'Przywrócenie usuwa {{count}} wiersz wraz z kwotami. Operacja nieodwracalna.',
      warning_few: 'Przywrócenie usuwa {{count}} wiersze wraz z kwotami. Operacja nieodwracalna.',
      warning_many: 'Przywrócenie usuwa {{count}} wierszy wraz z kwotami. Operacja nieodwracalna.',
      warning_other: 'Przywrócenie usuwa {{count}} wiersza wraz z kwotami. Operacja nieodwracalna.',
      confirm_one: 'Usuń {{count}} wiersz i przywróć',
      confirm_few: 'Usuń {{count}} wiersze i przywróć',
      confirm_many: 'Usuń {{count}} wierszy i przywróć',
      confirm_other: 'Usuń {{count}} wiersza i przywróć',
      running: 'Przywracam…',
      done: 'Usunięto {{deleted}} wierszy i odtworzono {{created}}.',
      doneKept_one: 'Zachowano {{count}} zatwierdzony wiersz.',
      doneKept_few: 'Zachowano {{count}} zatwierdzone wiersze.',
      doneKept_many: 'Zachowano {{count}} zatwierdzonych wierszy.',
      doneKept_other: 'Zachowano {{count}} zatwierdzonego wiersza.',
      cancel: 'Anuluj',
      close: 'Zamknij',
    },
  },
  recalculate: {
    open: 'Przelicz miesiąc',
    title: 'Przelicz {{month}}',
    what: 'Odtwarza dni pracy z odczytów karty. Operacja nieodwracalna.',
    scoped: 'Tylko pracownicy pasujący do filtrów na liście obecności.',
    storedDays: 'Zapisane dni w tym miesiącu',
    autoDays: 'Dni z czytnika',
    autoNote: 'Te mogą zostać usunięte lub zmienione',
    manualDays: 'Dni wpisane ręcznie',
    manualNote: 'Te pozostaną bez zmian',
    dayOffDays: 'Nieobecności',
    workLogs: 'Odczyty karty',
    employees: 'Pracownicy',
    warning_one: 'Przeliczenie może usunąć lub zmienić {{count}} dzień z czytnika. Nie da się tego cofnąć.',
    warning_few: 'Przeliczenie może usunąć lub zmienić {{count}} dni z czytnika. Nie da się tego cofnąć.',
    warning_many: 'Przeliczenie może usunąć lub zmienić {{count}} dni z czytnika. Nie da się tego cofnąć.',
    warning_other: 'Przeliczenie może usunąć lub zmienić {{count}} dnia z czytnika. Nie da się tego cofnąć.',
    confirm_one: 'Przelicz {{count}} dzień',
    confirm_few: 'Przelicz {{count}} dni',
    confirm_many: 'Przelicz {{count}} dni',
    confirm_other: 'Przelicz {{count}} dnia',
    running: 'Przeliczanie…',
    done_one: 'Odtworzono {{count}} dzień.',
    done_few: 'Odtworzono {{count}} dni.',
    done_many: 'Odtworzono {{count}} dni.',
    done_other: 'Odtworzono {{count}} dnia.',
    cancel: 'Anuluj',
    close: 'Zamknij',
  },
  warning: {
    no_exit: 'Brak wyjścia',
    is_working: 'W pracy',
    long_hours: 'Długi dzień',
    short_hours: 'Krótki dzień',
    conflict: 'Konflikt z nieobecnością',
    overnight: 'Zmiana przez północ',
  },
  form: {
    required: 'To pole jest wymagane',
    choose: '— wybierz —',
  },
  reference: {
    id: 'ID',
    name: 'Nazwa',
    usage: 'W użyciu',
    unused: 'nieużywane',
    availableIn: 'Dostępne w działach',
    availableInHint: 'Zaznacz działy, w których można używać tego stanowiska.',
    availableEverywhere: 'Dostępne w każdym dziale.',
    everywhere: 'wszędzie',
    usageCount_one: '{{count}} umowa',
    usageCount_few: '{{count}} umowy',
    usageCount_many: '{{count}} umów',
    usageCount_other: '{{count}} umowy',
    empty: 'Brak pozycji.',
    searchPlaceholder: 'Fragment nazwy',
    editTitle: 'Zmień nazwę: {{name}}',
    confirmDelete: 'Usunąć „{{name}}”?',
    companiesMoved: 'Firmy są w module Administracja.',
    companiesLink: 'Przejdź do firm',
    kind: {
      departments: 'Działy',
      positions: 'Stanowiska',
    },
    add: {
      departments: 'Dodaj dział',
      positions: 'Dodaj stanowisko',
    },
  },
  employees: {
    id: 'ID',
    name: 'Pracownik',
    firstName: 'Imię',
    lastName: 'Nazwisko',
    payrollId: 'Nr kadrowy',
    payrollIdHint: 'Akronim z Optimy — np. 0075/E. Uzupełniany przy pierwszym imporcie, który dopasuje tę osobę.',
    activate: 'Przywróć',
    // Komunikat mówi, czego przywrócenie NIE robi. Karta bywa już wydana komu
    // innemu, a zakończona umowa jest faktem z przeszłości — bez tego zdania
    // pracownik spodziewa się, że osoba wróci do ewidencji, a nie wróci.
    activated:
      '{{name}} przywrócony(a). Umowa i karta nie wracają — dodaj nową umowę, żeby osoba pojawiła się w ewidencji i na liście płac.',
    rented: 'Pracownik wynajęty',
    // Zdanie mówi, co się dzieje z kosztem, a nie tylko czym jest znacznik:
    // ujemna kwota w kolumnie „Faktury" bez tego wyjaśnienia wygląda na błąd.
    rentedHint:
      'Cały koszt tej osoby zwraca inna firma, powiększony o marżę. Zwrot pojawia się jako kwota ujemna w kolumnie „Faktury" na liście płac. Dotyczy tylko umów o pracę i zleceń.',
    rentedMargin: 'Marża (%)',
    rentedMarginHint:
      'Np. 10 oznacza, że koszt 8 000 zł zostanie zwrócony jako 8 800 zł. Zmiana marży nie zmienia miesięcy już rozliczonych — każdy wiersz zachowuje marżę, z którą powstał.',
    disabled: 'Pracownik z orzeczeniem',
    disabledHint:
      'Dzień pracy trwa 7 godzin: norma miesięczna to 7/8 standardowej, a każdy dzień nieobecności liczy się jako 7 h. Obowiązuje od poniższej daty — wcześniejsze miesiące zachowują dotychczasowe wartości.',
    disabledFrom: 'Od',
    disabledFromHint: 'Pierwszy miesiąc, w którym obowiązuje dzień 7-godzinny.',
    disabledTo: 'Do',
    disabledToHint: 'Zostaw puste, dopóki orzeczenie obowiązuje.',
    countsHours: 'Godziny są rejestrowane',
    countsHoursHint:
      'Wyłącz dla osoby bez karty. Nikt jej nie skanuje, więc godziny na liście płac wypełniane są z normy wynikającej z umowy, pomniejszonej o nieobecności — do czasu, aż import z kadr poda rzeczywistą wartość.',
    department: 'Dział',
    position: 'Stanowisko',
    card: 'Karta',
    status: 'Status',
    active: 'Aktywny',
    inactive: 'Nieaktywny',
    allStatuses: 'Wszyscy',
    noAgreement: 'bez aktualnej umowy',
    departmentBadge: 'Dział: {{departments}}',
    departmentsBadge: 'Działy: {{departments}}',
    noCard: 'brak karty',
    empty: 'Nie znaleziono pracowników.',
    searchPlaceholder: 'Imię lub nazwisko',
    add: 'Dodaj pracownika',
    addHint: 'Pierwsza umowa jest wymagana. Kartę dodasz na stronie pracownika.',
    editTitle: 'Edytuj dane pracownika',
    backToList: 'Wróć do listy',
    cardHint: 'Numer karty zbliżeniowej używanej w czytnikach.',
    cardNumber: 'Numer z karty',
    cardNumberHint: 'Dziesięć cyfr, tak jak na karcie.',
    cardHex: 'Numer z czytnika',
    issueCard: 'Przypisz kartę',
    issueCardHint: 'Wpisz dziesięciocyfrowy numer z karty. Nic nie jest zapisywane w czytniku.',
    saveCard: 'Zapisz kartę',
    replaceCard: 'Zmień kartę',
    removeCard: 'Usuń kartę',
    confirmRemoveCard: 'Odebrać kartę?',
    pickerHint: 'Wpisz co najmniej dwie litery.',
    month: {
      title: 'Czas pracy',
      description:
        'Dni tego pracownika w wybranym miesiącu. Kliknij dzień, aby go poprawić, lub pusty dzień, aby go dodać.',
      empty: 'Brak zapisów w tym miesiącu.',
      logUnused: '{{type}} {{time}} — nie użyty do dnia pracy',
      logNextDay: '{{type}} {{time}} następnego ranka — zmiana nocna',
      logCorrected: '{{type}} {{time}} — poprawiona godzina rozliczenia tego dnia',
      // Indeks górny przy godzinie, więc musi zostać krótki.
      nextDayMark: '+1',
    },
    addWorkDay: 'Dodaj dzień',
    // Już nie „ręczny wpis”: okno dodaje też nieobecność, a nieobecność nie
    // jest ręcznym dniem pracy.
    addWorkDayHint: 'Wpis ręczny dla: {{name}}. Nie zostanie usunięty przez przeliczenie miesiąca.',
    workDayDate: 'Data',
    workDayEndHint: 'Puste oznacza dzień bez wyjścia. Wcześniejsza godzina niż wejście oznacza nockę.',
  },
  agreements: {
    title: 'Umowy',
    change: 'Zmień umowę',
    addSecond: 'Druga umowa',
    addSecondTitle: 'Dodaj drugą umowę',
    addSecondHint:
      'Nowa umowa obok obecnej — obecna zostaje bez zmian. Dozwolone tylko w innej firmie. Godziny pozostają przy dotychczasowej umowie.',
    changeTitle: 'Zmień umowę',
    changeHint:
      'Obecna umowa kończy się dzień przed tą datą. Nowa zaczyna się od niej. Popraw służy do korekty wiersza bez nowego okresu.',
    changeDateHint:
      'Nie wcześniej niż początek obecnej umowy. Obecna umowa kończy się dzień wcześniej.',
    correct: 'Popraw',
    editTitle: 'Popraw umowę',
    hint: 'Umowa decyduje o stawce i o tym, w których działach liczony jest czas pracy.',
    period: 'Okres',
    openEnded: 'bez końca',
    rate: 'Stawka',
    rateHint: 'Brutto dla UoP i zlecenia, wartość faktury dla B2B.',
    etat: 'Etat',
    etatHint:
      'Część pełnego etatu. Skaluje tylko normę godzin — w polu Stawka wpisz rzeczywiste wynagrodzenie, nie pełnoetatowe.',
    isPrimary: 'Godziny',
    isPrimaryHint:
      'Umowa, do której liczone są przepracowane godziny. Pracownik może mieć dwie umowy naraz, ale w różnych firmach — godziny należą tylko do jednej z nich.',
    isPrimaryYes: 'Liczone do tej umowy',
    isPrimaryNo: 'Liczone do innej umowy',
    perHour: 'zł/h',
    perMonth: 'zł/mc',
    dateFrom: 'Od',
    dateTo: 'Do',
    dateToHint: 'Puste oznacza umowę bez daty końca.',
    departments: 'Działy',
    departmentsHint:
      'Koszt dzieli się między działy proporcjonalnie do wag. Dwie wagi po 1 to podział po połowie; 0,7 i 0,3 to podział 70/30.',
    addDepartment: 'Dodaj dział',
    positionNotInDepartments:
      'Stanowisko {{position}} nie jest zwykle używane w: {{departments}}. Można to mimo to zapisać.',
    positionElsewhere: '{{name}} (inny dział)',
    weight: 'Waga',
    formLabel: 'Forma umowy',
    formHint: 'Decyduje o narzucie składek pracodawcy. B2B nie ma narzutu. Inne nie może mieć stawki.',
    form: {
      uop: 'UoP',
      zlecenie: 'Zlecenie',
      b2b: 'B2B',
      inne: 'Inne',
    },
    rateUnitLabel: 'Okres stawki',
    rateUnitHint: 'Czy stawka podana jest za godzinę, czy za miesiąc.',
    empty: 'Brak umów.',
    confirmDelete: 'Usunąć tę umowę?',
    payBSchemaLabel: 'Typ wynagrodzenia B',
    payBValueLabel: 'Wartość',
    payBSchemaHint: 'Wybierz typ wynagrodzenia B lub pozostaw puste.',
    payBValueHint: 'Kwota wynagrodzenia B w złotych.',
    payBSchema: {
      none: '— brak —',
      fixed_compensation_month: 'Stałe wynagrodzenie za miesiąc',
      fixed_bonus_month: 'Stała premia za miesiąc',
      fixed_compensation_hour: 'Stałe wynagrodzenie za godzinę',
    },
    payBSchemaHelp: {
      fixed_compensation_month: 'Stałe wynagrodzenie naliczane miesięcznie.',
      fixed_bonus_month: 'Stała premia wypłacana co miesiąc.',
      fixed_compensation_hour: 'Stałe wynagrodzenie naliczane za każdą przepracowaną godzinę.',
    },
  },
  daysOff: {
    add: 'Dodaj nieobecność',
    editTitle: 'Edytuj nieobecność',
    hint: 'Nieobecność obejmuje zakres dni i liczy się jako 8 godzin dziennie.',
    period: 'Okres',
    days: 'Dni',
    dayCount_one: '{{count}} dzień',
    dayCount_few: '{{count}} dni',
    dayCount_many: '{{count}} dni',
    dayCount_other: '{{count}} dnia',
    type: 'Rodzaj',
    month: 'Miesiąc',
    allMonths: 'Wszystkie',
    from: 'Od',
    to: 'Do',
    toHint: 'Ten dzień też jest wolny.',
    empty: 'Brak nieobecności.',
    confirmDelete: 'Usunąć nieobecność: {{name}}?',
  },
  cardReaders: {
    add: 'Dodaj czytnik',
    editTitle: 'Edytuj czytnik',
    id: 'ID',
    location: 'Lokalizacja',
    address: 'Adres',
    ip: 'Adres IP',
    port: 'Port',
    active: 'Aktywne',
    activeHint: 'Nieaktywne czytniki nie są odpytywane.',
    inactive: 'Nieaktywne',
    allStates: 'Wszystkie',
    connected: 'Połączony',
    disconnected: 'Bez połączenia',
    never: 'nigdy',
    lastSynced: 'Ostatnia synchronizacja',
    lastLog: 'Ostatni odczyt',
    state: 'Stan',
    empty: 'Brak czytników.',
    searchPlaceholder: 'Fragment lokalizacji',
    synchronize: 'Synchronizuj',
    synchronizeTitle: 'Synchronizacja: {{name}}',
    synchronizeHint:
      'Pobiera brakujące odczyty z czytnika. Puste daty oznaczają wszystko od ostatniej pełnej synchronizacji.',
    synchronizing: 'Synchronizacja…',
    synchronizingHint: 'Łączę się z czytnikiem {{address}}. To może potrwać.',
    synchronizeDone_one: 'Zapisano {{count}} odczyt.',
    synchronizeDone_few: 'Zapisano {{count}} odczyty.',
    synchronizeDone_many: 'Zapisano {{count}} odczytów.',
    synchronizeDone_other: 'Zapisano {{count}} odczytu.',
    from: 'Od',
    fromHint: 'Puste: od ostatniej pełnej synchronizacji.',
    to: 'Do',
    toHint: 'Puste: do teraz.',
    badRange: 'Nieprawidłowy zakres dat.',
    confirmDelete: 'Usunąć czytnik „{{name}}”?',
    username: 'Użytkownik',
    usernameHint: 'Konto w urządzeniu. „admin”, o ile nikt go nie zmienił.',
    password: 'Hasło',
    passwordStored: 'Ten czytnik ma zapisane hasło (ustawione {{date}}). Wpisz nowe, aby je zmienić.',
    passwordShared: 'Bez własnego hasła — ten czytnik używa wspólnego hasła z konfiguracji serwera.',
    passwordClear: 'Używaj wspólnego hasła',
    passwordClearHint: 'Usuwa własne hasło czytnika i przywraca hasło z konfiguracji serwera.',
    // Przesyłanie odczytów — czytnik odpytywany albo wysyłający sam.
    ingest: 'Przesyłanie',
    ingestTitle: 'Przesyłanie odczytów: {{name}}',
    ingestHint:
      'Czytnik może być odpytywany przez serwer albo sam wysyłać każdy odczyt. Zmieniaj po jednym urządzeniu.',
    mode: 'Tryb',
    modePull: 'Odpytywanie',
    modePullHint: 'Serwer pobiera z czytnika cały plik odczytów co 10 minut. Tak działają dziś wszystkie czytniki.',
    modeShadow: 'Próbny',
    modeShadowHint:
      'Czytnik wysyła odczyty, ale zapisujemy je tylko do porównania — liczy się nadal odpytywanie. Stan przejściowy na jeden dzień.',
    modePush: 'Wysyłanie',
    modePushHint:
      'Odczyty z czytnika są jedynym źródłem; zaplanowane odpytywanie go pomija. Pełna synchronizacja raz w nocy, ręczna też działa.',
    modeCollector: 'Kolektor',
    modeCollectorHint:
      'Czytnik czyta kolektor w obiekcie, w sieci lokalnej, i przysyła odczyty pod swoim źródłem danych. Serwer się z czytnikiem nie łączy.',
    source: 'Źródło danych',
    sourceHint: 'Źródło rodzaju „Czytniki kart”, którego token ma kolektor. Źródła dodaje się w Administracja → Źródła danych.',
    sourcePick: 'Wybierz źródło',
    sourceNone: 'Brak źródeł rodzaju „Czytniki kart”. Dodaj źródło w Administracja → Źródła danych.',
    sourceOff: '{{name}} (wyłączone)',
    collectorReads: 'kolektor: {{name}}',
    collectorId: 'W collector.toml, w bloku [[reader]] tego czytnika:',
    collectorLastPush: 'Ostatnia przesyłka kolektora',
    collectorNoConnect: 'Ten czytnik czyta kolektor. Serwer się z nim nie łączy.',
    ingestUrl: 'Adres do wpisania w czytniku',
    urlHint: 'Wpisz w czytniku: Network → HTTP Client → Resource. Potem włącz HTTP Client.',
    urlNoBase:
      'Serwer nie zna swojego publicznego adresu (READER_PUSH_BASE_URL), więc pokazujemy tylko ścieżkę.',
    urlAfterMode: 'Adres pojawi się po zapisaniu trybu próbnego lub wysyłania.',
    copy: 'Kopiuj',
    copied: 'Skopiowano',
    copyUrl: 'Kopiuj adres',
    lastPush: 'Ostatnia przesyłka',
    quiet: 'Ten czytnik nic nie przysłał od ponad {{minutes}} minut. Sprawdź, czy ma włączony HTTP Client i poprawny adres.',
    quietShort: 'cisza',
    regenerate: 'Nowy token',
    regenerateConfirm:
      'Wygenerować nowy token dla „{{name}}”? Dotychczasowy adres przestanie działać, aż wpiszesz nowy w czytniku.',
    regenerateHint: 'Stary adres przestaje działać natychmiast — czytnik zamilknie do wpisania nowego.',
    test: 'Testuj',
    testTitle: 'Test połączenia: {{name}}',
    testHint: 'Odczytuje {{address}} i pokazuje, co zwrócił. Nic nie jest zapisywane.',
    testing: 'Łączę się z czytnikiem…',
    testAgain: 'Testuj ponownie',
    testOk: 'Czytnik odpowiedział w {{ms}} ms.',
    testFailure: {
      unreachable: 'Brak odpowiedzi spod tego adresu.',
      unauthorized: 'Czytnik odrzucił te dane logowania.',
      'not-a-reader': 'Coś odpowiedziało, ale to nie jest czytnik kart.',
      error: 'Nie udało się wykonać testu.',
    },
    testUser: 'Logowanie jako',
    testSource: {
      device: 'własne hasło czytnika',
      environment: 'wspólne hasło',
    },
    testMode: 'Tryb',
    testReadOnly: 'Tylko odczyt — ten serwer nigdy nie zapisuje do czytnika.',
    testDevice: 'Urządzenie',
    testModel: 'Model',
    testFirmware: 'firmware {{version}}',
    testClock: 'Zegar urządzenia',
    testClockUnknown: 'nie podany przez ten firmware',
    testSkew: '{{seconds}} s względem serwera',
    testStoredLogs: 'Odczytów w pamięci',
    testCardsOnDevice: 'Kart zapisanych w urządzeniu',
    testLog: 'Plik dziennika',
    testLogRows: 'Wierszy',
    testLogWindow: 'Zakres',
    testWouldStore: 'Nowe odczyty',
    testWouldStoreValue_one: '{{count}} zostałby zapisany',
    testWouldStoreValue_few: '{{count}} zostałyby zapisane',
    testWouldStoreValue_many: '{{count}} zostałoby zapisanych',
    testWouldStoreValue_other: '{{count}} zostałoby zapisanych',
    testMatched: 'Rozpoznane karty',
    testMatchedValue_one: '{{count}} należy do pracownika',
    testMatchedValue_few: '{{count}} należą do pracowników',
    testMatchedValue_many: '{{count}} należy do pracowników',
    testMatchedValue_other: '{{count}} należy do pracowników',
    testUnmatched: 'Bez przypisanego pracownika: {{cards}}',
    testRaw: 'Pokaż surowe wiersze dziennika',
    testRawStatus: 'Pokaż, co zwróciło urządzenie',
    testLogMissing: 'Nie udało się odczytać pliku dziennika.',
  },
  history: {
    title: 'Historia zmian',
    description: 'Kto i kiedy zmienił dane pracownika lub jego umowy.',
    empty: 'Brak zapisanych zmian.',
    hidden: 'zmiana ukryta',
    action: {
      create: 'Utworzono',
      update: 'Zmieniono',
      delete: 'Usunięto',
    },
    entity: {
      'work-day': 'dzień pracy',
      'work-log': 'odczyt karty',
      employee: 'dane pracownika',
      agreement: 'umowa',
      device: 'czytnik',
      'position-departments': 'działy stanowiska',
    },
    // Nazwy pól w historii, których formularz nie nazywa jednoznacznie poza
    // swoim kontekstem („Od” pod orzeczeniem, „Wartość” pod wynagrodzeniem B).
    field: {
      disabledFrom: 'Orzeczenie od',
      disabledTo: 'Orzeczenie do',
      payBValue: 'Kwota wynagrodzenia B',
    },
  },
  workLogs: {
    subtitle: 'Surowe odczyty kart — do sprawdzania czytnika albo jednego dnia.',
    time: 'Data i godzina',
    clock: 'Godzina',
    clockHint: 'Format GG:MM. Data odczytu pozostaje bez zmian.',
    direction: 'Zdarzenie',
    entry: 'Wejście',
    exit: 'Wyjście',
    reader: 'Czytnik',
    handEntered: 'Wpis ręczny',
    workDay: 'Dzień pracy',
    noWorkDay: 'brak',
    noWorkDayHint:
      'Ten odczyt nie otwiera ani nie zamyka żadnego dnia pracy — albo dzień powstał z innych odczytów, albo nie powstał wcale.',
    openDay: 'Pokaż dzień',
    empty: 'Brak odczytów dla tych filtrów.',
    from: 'Od dnia',
    to: 'Do dnia',
    deleted: 'Usunięte',
    deletedHidden: 'Ukryj usunięte',
    deletedShown: 'Pokaż usunięte',
    editTitle: 'Edytuj odczyt',
    editHint: '{{date}}, {{name}}. Zmiana godziny może przenieść dzień pracy.',
    badTime: 'Godzina musi mieć format GG:MM.',
    confirmDelete: 'Usunąć odczyt {{name}} z godziny {{time}}? Dzień pracy może zostać przeliczony.',
    recalculate: 'Popraw wejścia i wyjścia',
    recalculateTitle: 'Popraw oznaczenia wejść i wyjść',
    recalculateWhat:
      'Ustala kierunek każdego odczytu na nowo: odczyt jest wyjściem, jeśli następuje po wejściu w ciągu 14 godzin. Zmienia to, co da się potem odtworzyć w dniach pracy.',
    recalculateScoped: 'Tylko dla wybranego pracownika.',
    rangeBackwards: 'Data „od” nie może być późniejsza niż „do”.',
    previewLogs: 'Odczyty w zakresie',
    previewLogsNote: 'Każdy może zmienić kierunek',
    previewEmployees: 'Pracownicy',
    previewWorkDays: 'Dni pracy w zakresie',
    previewWorkDaysNote: 'Te nie zmieniają się teraz — dopiero przy przeliczeniu miesiąca',
    recalculateConfirm_one: 'Przelicz {{count}} odczyt',
    recalculateConfirm_few: 'Przelicz {{count}} odczyty',
    recalculateConfirm_many: 'Przelicz {{count}} odczytów',
    recalculateConfirm_other: 'Przelicz {{count}} odczytu',
    recalculateRunning: 'Przeliczanie…',
    recalculateDone_one: 'Zmieniono kierunek {{count}} odczytu.',
    recalculateDone_few: 'Zmieniono kierunek {{count}} odczytów.',
    recalculateDone_many: 'Zmieniono kierunek {{count}} odczytów.',
    recalculateDone_other: 'Zmieniono kierunek {{count}} odczytu.',
  },
  account: {
    title: 'Konto',
    passwordTitle: 'Hasło',
    passwordHint: 'Hasło jest zmieniane w Cognito, nie w tej aplikacji.',
    currentPassword: 'Obecne hasło',
    changePassword: 'Zmień hasło',
    changing: 'Zmienianie…',
    passwordChanged: 'Hasło zostało zmienione.',
    wrongPassword: 'Obecne hasło jest nieprawidłowe.',
    appearance: 'Wygląd',
    appearanceHint: 'Zapisane na Twoim koncie — działają na każdym urządzeniu, na którym się zalogujesz.',
    locale: {
      pl: 'Polski',
      en: 'English',
    },
    themeSystemNote: 'Motyw systemowy zmienia się razem z ustawieniem systemu.',
    savedFilters: 'Zapisane filtry',
    savedFiltersHint:
      'Ewidencja, pracownicy, nieobecności, odczyty kart, wypłaty i czytniki pamiętają Twoje ostatnie filtry i sortowanie — na Twoim koncie, więc na każdym urządzeniu. Link z filtrami w adresie zawsze ma pierwszeństwo.',
    forgetFilters: 'Zapomnij zapisane filtry',
    forgetFiltersQuestion: 'Usunąć zapisane filtry i sortowanie ze wszystkich ekranów?',
    forgetFiltersConfirm: 'Usuń',
    filtersForgotten: 'Zapisane filtry zostały usunięte. Listy otworzą się z ustawieniami domyślnymi.',
    access: 'Uprawnienia',
    accessHint: 'Tak, jak widzi je serwer — z GET /v1/me, nie z tokenu odczytanego w przeglądarce.',
    groups: 'Grupy',
    noGroups: 'brak',
    departments: 'Działy',
    noDepartments: 'żaden',
    allDepartments: 'wszystkie',
    canSeeCosts: 'Dostęp do kosztów',
    readOnly: 'Dostęp',
    readOnlyHint: 'Tylko podgląd — bez możliwości zmian',
  },
  modules: {
    switch: 'Przełącz aplikację',
    pickPrompt: 'Wybierz aplikację',
    pickRemembered: 'Zapamiętamy Twój wybór na Twoim koncie. Zmienisz go, klikając logo.',
    noneTitle: 'Nie masz dostępu do żadnej aplikacji',
    noneBody:
      'Twoje konto działa, ale nie przypisano mu jeszcze żadnej aplikacji. Poproś administratora o dostęp.',
    hr: {
      name: 'Kadry',
      description: 'Czas pracy, pracownicy, płace',
    },
    hotel: {
      name: 'Hotel',
      description: 'Obłożenie, ADR, RevPAR i księga',
    },
    gastronomy: {
      name: 'Gastronomia',
      description: 'Restauracja, kawiarnia, grill, pizzeria, imprezy, jazda konna',
    },
    catering: {
      name: 'Stołówki',
      description: 'Stołówki pracownicze: posiłki, karta pracownicza i paragony',
    },
    cashier: {
      name: 'Kasa',
      description: 'Raport dnia, historia raportów i kasa główna',
    },
    admin: {
      name: 'Administracja',
      description: 'Użytkownicy, uprawnienia i firmy',
    },
  },
  hotel: {
    nav: {
      today: 'Dziś',
      results: 'Wyniki',
      forward: 'Na przyszłość',
      settings: 'Ustawienia',
    },
    filter: {
      label: 'Obiekt',
      allObjects: 'Wszystkie obiekty',
    },
    money: {
      label: 'Netto czy brutto',
      nett: 'Netto',
      gross: 'Brutto',
      vatNote: 'Wpisujesz {{mode}}. {{otherMode}}: {{amount}} (VAT 8 %).',
    },
    metrics: {
      occupancy: 'Obłożenie',
      adr: 'ADR',
      revpar: 'RevPAR',
      trevpor: 'TRevPOR',
      trevpar: 'TRevPAR',
      alos: 'ALOS',
      // Każde wyjaśnienie podaje mianownik, bo to jedyna różnica między tymi
      // czterema — i to właśnie skrót ukrywa.
      explain: {
        adr: 'Przychód z pokoi ÷ pokoje sprzedane. Średnia cena sprzedanego pokoju.',
        trevpor:
          'Przychód total (pokoje + gastronomia) ÷ pokoje sprzedane. Ile zostawia jeden sprzedany pokój.',
        revpar:
          'Przychód z pokoi ÷ pokoje dostępne. Spada, gdy cena jest niska ALBO gdy pokoje stoją puste.',
        trevpar:
          'Przychód total (pokoje + gastronomia) ÷ pokoje dostępne. Najszersza miara — liczy też puste pokoje.',
        alos: 'Sprzedane doby ÷ liczba pobytów. Średnia długość jednego pobytu w dobach.',
      },
      yoy: 'Rok wcześniej: {{value}}',
      yoyRevenue: 'Przychód rok wcześniej: {{value}}',
    },
    grid: {
      sold: 'Sprzedany',
      free: 'Wolny',
      shortBlock: 'Blokada krótka',
      softBlock: 'Blokada miękka',
      longBlock: 'Blokada długa',
      rent: 'Najem',
      manual: 'Nadpisane ręcznie',
      ungrouped: 'Bez obiektu',
      editNight: 'Ustaw cenę pokoju {{room}}',
      editManual: 'Zmień ręczną cenę pokoju {{room}}',
      editTitle: 'Pokój {{room}}',
      editHint: 'Zmiana tylko tej doby ({{date}}).',
      editRateHint: 'Puste przy imprezie — bez kwoty w ADR.',
      status: {
        sold: 'Sprzedany',
        free: 'Wolny',
        blocked: 'Blokada',
        absent: 'Brak',
      },
    },
    qualify: {
      gratis: 'gratis / użytek własny',
      impreza: 'faktura za imprezę',
      noc: 'cena noclegu',
    },
    today: {
      title: 'Dziś',
      subtitle: 'Siatka pokoi i metryki poranne',
      unpricedPill: '{{count}} bez ceny',
      dayRevenue: 'Przychód doby',
      cash: 'Kasa',
      cashNote: 'Kasa wg daty dokumentu; otwarte pobyty mogą się różnić.',
      cashAllGroupsNote: 'Dla wszystkich obiektów, nie tylko wybranego — kasa nie dzieli się na grupy.',
      arrivals: 'Przyjazdy',
      departures: 'Wyjazdy',
      roomsPersons: 'pokoje / osoby',
      breakfasts: 'Śniadania',
      dinners: 'Obiadokolacje',
      breakfastsPayable_one: '{{count}} płatne jutro rano',
      breakfastsPayable_few: '{{count}} płatne jutro rano',
      breakfastsPayable_many: '{{count}} płatnych jutro rano',
      breakfastsPayable_other: '{{count}} płatnych jutro rano',
      mealsAdultChild: 'dorośli / dzieci',
      sellableDenom: 'z {{count}} sprzedażowych',
      bridgeTitle: 'Mostek RevPAR',
      bridgeHint: 'Zmiana r/r: cena a obłożenie',
      bridgeStart: 'Rok temu',
      bridgeEnd: 'Ta doba',
      bridgeOcc: 'Efekt obłożenia',
      bridgeAdr: 'Efekt ceny (ADR)',
      bandTitle: 'Te same dni tygodnia',
      bandHint: 'RevPAR z ostatnich ośmiu takich samych dni',
      bandMedian: 'mediana {{value}}',
      bandPrior: '8 poprzednich',
      bandSelected: 'wybrany dzień',
      revenueSplit: 'Podział przychodu',
      revenueSplitTotal: 'Razem',
      source: 'Źródło',
      value: 'Wartość',
      share: 'Udział',
      perSold: 'Na sprzedaną dobę',
      rev: {
        rooms: 'Pokoje',
        breakfast: 'Śniadanie',
        dinner: 'Obiadokolacja',
        gastro: 'Gastronomia',
        bar: 'Bar',
        other: 'Inne',
      },
      historyTitle: 'Historia',
      historyHint: 'Porównaj do trzech dni',
      historySelected: 'Wybrane',
      historyYoy: 'Rok temu',
      historyYoy2: 'Dwa lata temu',
      historyCalendar: 'Rok kalendarzowy temu',
      colDay: 'Dzień',
      colOcc: 'Obł.',
      colRevenueTotal: 'Przychód total',
      chartOccupancy: 'Obłożenie',
      chartRevpar: 'RevPAR',
      openFolioNote: '{{amount}} na otwartych foliach — jeszcze nie w przychodzie doby',
      grid: 'Siatka pokoi',
      emptyGrid: 'Brak danych o pokojach dla tego dnia.',
      priceGaps: 'Doby bez ceny — uzupełnienie ręczne',
      priceGapsHint:
        'Siatka mówi, że pokój był sprzedany, ale nie ma kwoty. Docelowo część przyjdzie z Imprez — na razie decyzja ręczna.',
      priceGapsBulk: 'Zastosuj do wszystkich {{count}} dób w tym dniu:',
      priceGapsRatePh: 'zł / dobę',
      priceGapsApply: 'Zastosuj',
      priceGapsSave: 'Zapisz',
      priceGapsNeedRate: 'Cena noclegu wymaga kwoty.',
      priceGapsDirty_one: '{{count}} zmiana bez zapisu',
      priceGapsDirty_few: '{{count}} zmiany bez zapisu',
      priceGapsDirty_many: '{{count}} zmian bez zapisu',
      priceGapsDirty_other: '{{count}} zmiany bez zapisu',
      priceGapsColRoom: 'Pokój',
      priceGapsColPax: 'Os.',
      priceGapsColStay: 'Pobyt',
      priceGapsColRes: 'Rezerwacja',
      priceGapsColKind: 'Kwalifikacja',
      priceGapsColRate: 'Stawka',
      priceGapsColApply: 'Zastosuj do…',
      priceGapsStayNights_one: '{{count}} noc',
      priceGapsStayNights_few: '{{count}} noce',
      priceGapsStayNights_many: '{{count}} nocy',
      priceGapsStayNights_other: '{{count}} nocy',
      priceGapsResSummary: '{{nights}} dób w {{rooms}} pokojach',
      priceGapsResOpen_one: '{{count}} bez ceny',
      priceGapsResOpen_few: '{{count}} bez ceny',
      priceGapsResOpen_many: '{{count}} bez ceny',
      priceGapsResOpen_other: '{{count}} bez ceny',
      priceGapsNoRes: 'brak rezerwacji',
      priceGapsApplyStay: 'pobyt ({{count}})',
      priceGapsApplyRes: 'rezerwacja ({{count}})',
      priceGapsSuggestNote:
        'Podpowiedź w polu ceny to mediana doby tego pokoju w tym miesiącu — punkt wyjścia, nie propozycja do zatwierdzenia w ciemno.',
      priceGapsEventNote:
        'Jedna z tych rezerwacji obejmuje {{count}} pokoi bez ceny. To wygląda na imprezę rozliczaną osobną fakturą. Przycisk „rezerwacja” ustawia wszystkie jej doby naraz.',
    },
    results: {
      title: 'Wyniki',
      subtitle: 'Metryki za wybrany okres',
      attributed: 'Przychód zawsze przypisany',
      periodRevenue: 'Przychód okresu',
      nightsUnit: 'dób',
      breakfastCoverage: 'Śniadania',
      dinnerCoverage: 'Obiadokolacje',
      mealCoverageHint: 'pokrycie osób',
      unpricedQueue: 'Kolejka dób bez ceny',
      unpricedQueueHint: 'Dni, w których wciąż są sprzedane doby bez kwoty',
      unpricedCount_one: '{{count}} doba bez ceny',
      unpricedCount_few: '{{count}} doby bez ceny',
      unpricedCount_many: '{{count}} dób bez ceny',
      unpricedCount_other: '{{count}} doby bez ceny',
      bridgeEnd: 'Ten okres',
      weekdayEyebrow: 'Rytm tygodnia',
      weekdayTitle: 'Obłożenie wg dnia tygodnia',
      weekdayHint: 'Średnie obłożenie w wybranym okresie',
      weekdayEmpty: 'Brak dób z obłożeniem w tym okresie.',
      weekdayDays_one: '{{count}} dzień',
      weekdayDays_few: '{{count}} dni',
      weekdayDays_many: '{{count}} dni',
      weekdayDays_other: '{{count}} dnia',
      occRevparTitle: 'Obłożenie i RevPAR',
      occRevparHint: 'Dzienne obłożenie (słupki) i RevPAR (linia) w okresie',
      sourcesEyebrow: 'Źródło rezerwacji',
      sourcesTitle: 'Rezerwacje wg pola źródłowego',
      sourcesHint: 'Starty pobytu w okresie, bez anulacji',
      sourcesBlank: '(nie zapisano)',
      sourcesEmpty: 'Brak danych o źródłach w tym okresie.',
      sourcesNote:
        'Ciemniejszy odcinek to rezerwacje, które mają identyfikator Profitroom — czyli przyszły kanałem online niezależnie od tego, co wpisała recepcja.',
      sourcesTip: '{{count}} rezerwacji · {{profitroom}} z id Profitroom',
    },
    forward: {
      title: 'Na przyszłość',
      asOf: 'Stan rezerwacji na {{date}}',
      otbOcc: 'Obłożenie OTB',
      books: 'Doby w księgach',
      ofSellable: 'z {{count}} sprzedawalnych',
      gapToFinal: 'do zeszłorocznego poziomu: {{count}}',
      pickup1: 'Pickup 1 dzień',
      pickup3: 'Pickup 3 dni',
      pickup7: 'Pickup 7 dni',
      nights: 'dób',
      cancellations: 'Anulacje',
      cancellationsHint: 'rezerwacji historycznie',
      priorFinal: 'rok temu ostatecznie {{value}}',
      horizon: 'Horyzont',
      horizonDays_one: '{{count}} dzień',
      horizonDays_few: '{{count}} dni',
      horizonDays_many: '{{count}} dni',
      horizonDays_other: '{{count}} dnia',
      calendarTitle: 'Kalendarz rezerwacji',
      calendarHint: 'obłożenie zarezerwowane na kolejne {{count}} dni, na mianowniku sprzedawalnym',
      calendarEmpty: 'Brak księgi w tym horyzoncie.',
      calendarCell: '{{date}}: {{occ}} %, za {{ahead}} dni',
      occRevparTitle: 'Prognoza RevPAR',
      occRevparHint: 'Obłożenie z księgi (%) i prowizoryczny RevPAR na {{count}} dni',
      attention: 'Dni wymagające uwagi',
      attentionHint: 'Rezerwacji mniej niż rok temu w tym samym momencie',
      attentionEmpty: 'Brak dat istotnie odstających w tym horyzoncie.',
      attentionNote: 'Lista robocza: tu ma sens obniżka, pakiet albo kampania.',
      attentionAhead_one: 'za {{count}} dzień',
      attentionAhead_few: 'za {{count}} dni',
      attentionAhead_many: 'za {{count}} dni',
      attentionAhead_other: 'za {{count}} dnia',
      attentionCompare: 'teraz {{now}} · rok temu {{prior}}',
      attentionRooms_one: '{{count}} pok.',
      attentionRooms_few: '{{count}} pok.',
      attentionRooms_many: '{{count}} pok.',
      attentionRooms_other: '{{count}} pok.',
      leadTimeTitle: 'Wyprzedzenie rezerwacji',
      leadTimeHint: 'Ile dni przed przyjazdem rezerwują goście',
      leadTimeNote:
        'Sterowanie ceną ma sens na trzy tygodnie do przodu — a OTB z definicji nie zobaczy jednej piątej gości.',
      leadTimeEmpty: 'Brak danych o wyprzedzeniu.',
      leadTimeCount: 'Rezerwacji',
      leadTimeBucket: '{{range}} dni przed przyjazdem',
    },
    settings: {
      title: 'Ustawienia',
      readOnly: 'Nie masz uprawnień do zmiany ustawień hotelu.',
      previewFailed: 'Nie udało się policzyć podglądu zmian.',
      subtitle: 'Długoterminowy wynajem, blokady i stawki wyżywienia',
      draftPending: 'Masz niezapisane zmiany. Zapisz albo cofnij.',
      save: 'Zapisz',
      undo: 'Cofnij',
      draft: 'Szkic',
      addDraft: 'Dodaj do szkicu',
      roomGroups: 'Grupy pokoi',
      roomGroupsHint: 'Obiekty do filtrowania — nazwa i kolejność',
      rooms: 'Pokoje',
      roomsHint: 'Konfiguracja pokoi — nazwa, grupa i identyfikator z Izzy',
      groupName: 'Nazwa grupy',
      sortOrder: 'Kolejność',
      roomName: 'Nazwa pokoju',
      izzyId: 'Identyfikator Izzy',
      validFrom: 'Ważne od',
      validTo: 'Ważne do',
      alwaysValid: 'Zawsze',
      orphanRoom: 'Bez grupy',
      pickRoom: '— wybierz pokój —',
      rents: 'Wynajem',
      rentsHint: 'Pokój w obłożeniu / RevPAR — cena z umowy (miesiąc ÷ dni)',
      blocks: 'Długie blokady',
      blocksHint:
        'Wyłącza pokój ze sprzedaży. Blokada miękka ustępuje sprzedaży — pokój wraca do statystyk w dobach, w których został sprzedany.',
      blockKindLabel: 'Rodzaj',
      blockKind_long: 'Twarda',
      blockKind_soft: 'Miękka',
      meals: 'Stawki wyżywienia',
      mealsHint: 'Obowiązują od daty — nie przepisują historii',
      room: 'Pokój',
      from: 'Od',
      to: 'Do',
      rate: 'Stawka',
      unit: 'Jednostka',
      unitMonth: 'miesiąc',
      unitNight: 'noc',
      reason: 'Powód',
      breakfast: 'Śniadanie',
      dinner: 'Kolacja',
      childFactor: 'Współczynnik dziecka',
      previewTitle: 'Podgląd szkicu',
      previewHint: 'Ostatnie 30 dni z ustawieniami ze szkicu',
      previewOcc: 'Obł.',
      previewRevpar: 'RevPAR',
      previewSellable: 'Do sprzedaży',
    },
  },
  gastronomy: {
    events: {
      revenueShort: 'Przychód',
      salesHint: 'Pełna wartość imprez: faktury i rachunki przed odjęciem zaliczek.',
      advancesHint: 'Zaliczki wzięte w tym czasie, przed imprezą.',
      revenueHint: 'Przychód = sprzedaż + zaliczki wzięte − zaliczki rozliczone na fakturach końcowych.',
      aheadHint_one: 'Dokument wystawiony {{count}} dzień przed imprezą — zaliczka albo faktura z wyprzedzeniem.',
      aheadHint_few: 'Dokument wystawiony {{count}} dni przed imprezą — zaliczka albo faktura z wyprzedzeniem.',
      aheadHint_many: 'Dokument wystawiony {{count}} dni przed imprezą — zaliczka albo faktura z wyprzedzeniem.',
      aheadHint_other: 'Dokument wystawiony {{count}} dnia przed imprezą — zaliczka albo faktura z wyprzedzeniem.',
      sales: 'Sprzedaż',
      advances: 'Zaliczki',
      streamsTitle: 'Sprzedaż według strumieni',
      streamsHint: 'Faktury i rachunki bez zaliczek: pełna wartość imprez. Strumień z opisu pozycji.',
      plusTaken: '+ zaliczki wzięte',
      minusSettled: '− zaliczki rozliczone',
      revenue: '= przychód',
      documentsTitle: 'Dokumenty ({{count}})',
      documentsHint: 'Każdy dokument z pozycjami — opis pozycji mówi, czym była impreza. Data imprezy z opisu albo z zaliczki.',
      noDocuments: 'Brak dokumentów.',
      eventOn: 'impreza {{date}}',
      settles: 'rozlicza: {{documents}}',
      settledBy: 'rozliczona przez: {{documents}}',
      kind: {
        gastronomy8: 'Usługi gastronomiczne 8%',
        gastronomy23: 'Usługi gastronomiczne 23%',
        services23: 'Usługi 23% (sale, zajęcia)',
        hotel: 'Usługi hotelowe',
        advanceTaken: 'Zaliczka wzięta',
        advanceSettled: 'Zaliczka rozliczona',
      },
    },
    payments: {
      title: 'Płatności',
      hintDay: 'Udział w przychodzie; zmiana wobec typowego udziału — mediany z {{weeks}} tygodni.',
      hintPeriod: 'Udział w przychodzie; zmiana wobec udziału w tym samym okresie rok wcześniej.',
      none: 'Brak płatności w tym dniu.',
      bills_one: '{{formatted}} rachunek',
      bills_few: '{{formatted}} rachunki',
      bills_many: '{{formatted}} rachunków',
      bills_other: '{{formatted}} rachunku',
      internal: 'Rozliczenie wewnętrzne: {{amount}} — poza przychodem',
      group: {
        employeeCard: 'Karta pracownicza',
        card: 'Karta',
        cash: 'Gotówka',
        transfer: 'Przelew',
        online: 'Online (Profitroom, pyszne.pl)',
        prepaid: 'Przedpłata i karty podarunkowe',
        room: 'Na pokój (IzzyHotel)',
        other: 'Inne',
        none: 'Bez płatności',
      },
    },
    table: {
      showAll: 'Pokaż wszystkie ({{total}})',
      showLess: 'Pokaż mniej',
    },
    nav: {
      today: 'Dziś',
      results: 'Wyniki',
      forward: 'Na przyszłość',
      controls: 'Kontrola',
      settings: 'Ustawienia',
    },
    money: {
      label: 'Netto czy brutto',
      gross: 'Brutto',
      nett: 'Netto',
    },
    // Zapasowe nazwy punktów, gdy odpowiedź podaje tylko kod.
    outlets: {
      none: 'Bez przypisania',
      restaurant: 'Restauracja',
      cafe: 'Kawiarnia',
      grill: 'Grill',
      pizzeria: 'Pizzeria',
      events: 'Imprezy',
      riding: 'Stadnina / rekreacja',
    },
    eventKind: {
      wedding: 'Wesele',
      communion: 'Komunia',
      birthday: 'Urodziny',
      reception: 'Przyjęcie',
      meeting: 'Spotkanie',
      catering: 'Catering',
      stay: 'Pobyt',
      other: 'Inne',
    },
    freshness: {
      final: 'Dane ostateczne',
      provisional: 'Dane wstępne — mogą się jeszcze zmienić',
      lastPush: 'Ostatnia przesyłka {{at}}',
      never: 'Kolektor jeszcze nic nie przesłał',
    },
    period: {
      reversed: 'Data „od” jest późniejsza niż data „do”.',
      tooLong: 'Okres może mieć najwyżej {{days}} dni.',
    },
    chart: {
      empty: 'Brak przychodu w tym zakresie.',
    },
    today: {
      title: 'Dziś',
      subtitle: 'Dzień punktu sprzedaży na tle tego samego dnia tygodnia w 8 poprzednich tygodniach',
      subtitle_catering: 'Dzień stołówki na tle tego samego dnia tygodnia w 8 poprzednich tygodniach',
      empty: 'Brak rachunków i dokumentów w tym dniu.',
      outlets: 'Punkty sprzedaży',
      outlets_catering: 'Stołówki',
      revenue: 'Przychód',
      bills: 'Rachunki',
      noDetail: 'Brak punktu z przypisaniem, który miałby sprzedaż w tym dniu.',
      noDetail_catering: 'Żadna stołówka nie miała sprzedaży w tym dniu.',
      yearAgo: 'rok temu',
      correctionHere:
        'Skorygowano później: {{document}} ({{outlet}}), wystawiona {{date}}, zmienia ten dzień o {{amount}}.',
      correctionAway:
        'Korekta {{document}} ({{outlet}}) wystawiona tego dnia liczy się w dniu sprzedaży {{date}}: {{amount}}.',
      correctionOutlet:
        'Korekta {{document}} wprowadzona w serii punktu {{series}} liczy się dla punktu, którego sprzedaż koryguje — {{outlet}}: {{amount}}.',
      correctionSeries: 'Wprowadzona w serii punktu {{series}}.',
      replacementHere:
        'Faktura wystawiona ponownie: {{document}} ({{outlet}}), {{date}}, w miejsce faktury z tego dnia — liczona tutaj: {{amount}}.',
      replacementAway:
        'Faktura {{document}} ({{outlet}}) wystawiona tego dnia ponownie, w miejsce faktury z {{date}}, liczy się w dniu sprzedaży: {{amount}}.',
      goToDay: 'Pokaż {{date}}',
      weatherTitle: 'Temperatura najwyższa; w nawiasie najniższa i najwyższa ({{min}} – {{max}})',
      sunshine: 'Słońce {{hours}} h',
      precipitation: 'opady {{mm}} mm',
      bandTitle: 'Przychód na tle 8 tygodni',
      bandHint: 'Ten sam dzień tygodnia w 8 poprzednich tygodniach. Mediana z dni, w których punkt był otwarty.',
      bandHint_catering: 'Ten sam dzień tygodnia w 8 poprzednich tygodniach. Mediana z dni, w których stołówka była otwarta.',
      openWeeks: 'Dni otwarte do porównania: {{weeks}} z 8',
      noWeeks: 'Punkt nie był otwarty w żadnym z 8 poprzednich takich dni.',
      noWeeks_catering: 'Stołówka nie była otwarta w żadnym z 8 poprzednich takich dni.',
      median: 'Mediana',
      againstMedian: 'wobec mediany',
      billsLine: 'Rachunki: {{bills}} · mediana {{median}}',
      hoursTitle: 'Przychód według godzin',
      hoursHint: 'Według godziny zamknięcia rachunku. Linia to mediana tej godziny w ten sam dzień tygodnia, z {{weeks}} poprzednich takich dni z rachunkami.',
      hoursNoWeeks: 'Według godziny zamknięcia rachunku. Brak tygodni z rachunkami do porównania.',
      hourLabel: 'Godz. {{hour}}:00',
      thisDay: 'Ten dzień',
      menuTitle: 'Struktura menu',
      menuHint:
        'Udział kategorii w sprzedaży z rachunków; kliknij kategorię, żeby zobaczyć jej grupy Izzy, a grupę — jej produkty. Znacznik to typowy udział: mediana z {{weeks}} tygodni.',
      menuGroup: 'Kategoria',
      menuShare: 'Udział',
      menuTypical: 'Typowo',
      menuDiff: 'Różnica',
      menuPoints: '{{value}} pp',
      menuNotToday: 'dziś brak',
      menuNoCategory: 'Bez kategorii',
      menuShowGroups: 'Grupy Izzy: {{count}}',
      menuProducts: 'produkty: {{count}}',
      menuView: 'Widok',
      menuViewBars: 'Słupki',
      menuViewPie: 'Wykres kołowy',
      menuPieHint: 'Udział kategorii w sprzedaży z rachunków',
      menuPieTypical: 'typowo {{value}}',
      productsTitle: 'Bestsellery',
      productsHint: 'Ilość dnia wobec typowej ilości: mediany z {{weeks}} tygodni.',
      change: 'Zmiana',
      missingTitle: 'Zwykle się sprzedaje, dziś nie',
      missingHint: 'Sprzedawane w ponad połowie tygodni, w tym dniu ani razu.',
      missingLine: 'typowo {{quantity}} szt. · {{value}}',
      documentsOnly: 'W tym dniu punkt nie miał rachunków. Przychód pochodzi z dokumentów, więc godzin, menu i bestsellerów nie ma.',
      documentsOnly_catering: 'W tym dniu stołówka nie miała rachunków. Przychód pochodzi z dokumentów, więc godzin, menu i bestsellerów nie ma.',
    },
    results: {
      title: 'Wyniki',
      subtitle: 'Okres punktu sprzedaży na tle tego samego okresu rok wcześniej (364 dni, te same dni tygodnia)',
      subtitle_catering: 'Okres stołówki na tle tego samego okresu rok wcześniej (364 dni, te same dni tygodnia)',
      empty: 'Brak przychodu w tym okresie.',
      noDetail: 'Brak punktu z przypisaniem, który miałby sprzedaż w tym okresie.',
      noDetail_catering: 'Żadna stołówka nie miała sprzedaży w tym okresie.',
      dailyTitle: 'Przychód w czasie',
      dailyHint: 'Słupki — ten okres; linia przerywana — te same dni tygodnia rok wcześniej',
      grouping: 'Grupowanie',
      byDay: 'Dzień',
      byWeek: 'Tydzień',
      byMonth: 'Miesiąc',
      lastYear: 'Rok wcześniej',
      menuHint:
        'Udział kategorii w sprzedaży z rachunków; kliknij kategorię, żeby zobaczyć jej grupy Izzy, a grupę — jej produkty. Znacznik to udział w tym samym okresie rok wcześniej.',
      productsHint: 'Ilość w okresie wobec ilości w tym samym okresie rok wcześniej.',
      thisPeriod: 'Ten okres',
      typical: 'Rok temu',
      missingTitle: 'Rok temu się sprzedawało, teraz nie',
      missingHint: 'Sprzedawane w tym samym okresie rok wcześniej, teraz ani razu.',
      missingLine: 'rok temu {{quantity}} szt. · {{value}}',
    },
    otherRemarks: {
      title: 'Inne z uwagą',
      hint: 'Pozycje produktów rozbijanych wg uwag, których uwaga nie pasuje do żadnego wariantu — w strukturze menu to „inne”.',
      hintAssign:
        'Pozycje produktów rozbijanych wg uwag, których uwaga nie pasuje do żadnego wariantu — w strukturze menu to „inne”. Wybierz wariant: uwaga dołączy do jego słów, a każda taka pozycja, także wstecz, będzie liczona w nim.',
      product: 'Produkt',
      remark: 'Uwaga',
      variant: 'Wariant',
      assign: 'Przypisz „{{text}}” do wariantu',
      noVariants: 'brak wariantów — dodaj je w Ustawieniach',
      saving: 'Zapisywanie…',
    },
    menuMix: {
      noGroup: 'Bez grupy',
      empty: 'Brak sprzedaży w tym okresie.',
    },
    items: {
      open: 'Dokumenty dnia',
      title: 'Dokumenty dnia — {{outlet}}',
      count: 'rachunki: {{bills}}, dokumenty: {{documents}}',
      none: 'Brak rachunków i dokumentów.',
      order: 'Kolejność',
      byTime: 'Według godziny',
      byAmount: 'Według kwoty',
      prev: 'Poprzednia',
      next: 'Następna',
      page: 'Strona {{page}} z {{pages}}',
      time: 'Godz.',
      document: 'Dokument / rachunek',
      payment: 'Płatność',
      issued: 'Na dokumencie',
      revenue: 'Przychód {{mode}}',
      bill: 'Rachunek {{number}}',
      table: 'stół {{table}}',
      covers: 'goście: {{covers}}',
      movedFrom: 'z kasy: {{outlet}}',
      internal: 'w tym rozliczenie wewnętrzne {{amount}} — nie przychód',
      correction: 'korekta',
      correctionMoved: 'korekta wystawiona {{date}}, liczona w dniu sprzedaży',
      replacement: 'faktura wystawiona ponownie',
      replacementMoved: 'faktura wystawiona ponownie {{date}}, liczona w dniu sprzedaży',
      noLines: 'Brak pozycji.',
    },
    heatmap: {
      title: 'Dzień tygodnia × godzina',
      hint: 'Rachunki według godziny zamknięcia',
      metric: 'Miara',
      bills: 'Rachunki',
      revenue: 'Przychód',
      weekday: 'Dzień tygodnia',
      empty: 'Brak rachunków w tym okresie.',
      peak: 'Najciemniejsze pole: {{value}}',
    },
    products: {
      product: 'Produkt',
      quantity: 'Ilość',
      empty: 'Brak sprzedaży.',
      otherVariant: 'inne',
    },
    forward: {
      title: 'Na przyszłość',
      subtitle: 'Rezerwacje stolików i imprezy w przód',
      takenOn: 'Stan na {{date}}',
      noSnapshot: 'Brak danych — kolektor nie przesłał jeszcze rezerwacji ani imprez.',
      pickup: 'Nowe rezerwacje, {{days}} d',
      coversCount_one: '{{count}} osoba',
      coversCount_few: '{{count}} osoby',
      coversCount_many: '{{count}} osób',
      coversCount_other: '{{count}} osoby',
      onBooks: 'Rezerwacje w przód',
      cancelled: 'Anulowane',
      deposits: 'Zadatki na imprezy',
      depositsHint: 'Suma zadatków na przyszłe imprezy',
      reservationsTitle: 'Nakrycia według dni',
      reservationsHint: 'Zarezerwowane i anulowane nakrycia, wszystkie punkty razem',
      reservationsEmpty: 'Brak rezerwacji w przód.',
      daysTitle: 'Rezerwacje dzień po dniu',
      daysHint: 'Anulowane: rezerwacje / osoby',
      date: 'Dzień',
      reservations: 'Rezerwacje',
      coversBooked: 'Nakrycia',
      coversCancelled: 'Anulowane nakrycia',
      eventsTitle: 'Imprezy według miesięcy',
      eventsHint:
        'Z zadatków: rodzaj i datę imprezy podaje opis dokumentu. Pod liczbą — suma zadatków. KALH — rezerwacje z kalendarza imprez.',
      eventsEmpty: 'Brak zadatków na przyszłe imprezy.',
      month: 'Miesiąc',
      total: 'Razem',
      bookings: 'KALH',
    },
    controls: {
      title: 'Kontrola',
      subtitle: 'Co zmieniono na kasie i kto na to pozwolił',
      authorisedBy: 'Autoryzował',
      outlet: 'Punkt',
      outlet_catering: 'Stołówka',
      count: 'Liczba',
      lines: 'Pozycje',
      value: 'Wartość',
      reason: 'Powód',
      bills: 'Rachunki',
      noAuthoriser: '(nie zapisano)',
      noReason: '(bez powodu)',
      deletesTitle: 'Usunięcia pozycji',
      deletesHint: 'Usunięcia wysłanych pozycji za zgodą, według osoby i punktu',
      deletesHint_catering: 'Usunięcia wysłanych pozycji za zgodą, według osoby i stołówki',
      deletesEmpty: 'Brak autoryzowanych usunięć.',
      newLineDeletes:
        'Usunięcia pozycji jeszcze niewysłanych: {{value}}. To zwykła praca — nie wymaga kontroli.',
      priceChangesTitle: 'Zmiany cen',
      priceChangesHint: 'Zmiany ceny pozycji, według osoby autoryzującej',
      priceChangesEmpty: 'Brak zmian cen.',
      voidsTitle: 'Storna',
      voidsHint:
        'Anulowane pozycje już wydrukowane, w cenie sprzedaży. Bez literówek: ilość ponad 20 lub cena ponad 1000 zł.',
      voidsEmpty: 'Brak storn.',
      discountsTitle: 'Rabaty',
      discountsHint: 'Rabaty na rachunkach według powodu; wartość liczona od ceny cennikowej',
      discountsEmpty: 'Brak rabatów.',
      cancelledTitle: 'Anulowane rachunki',
      cancelledHint: 'Według powodu zapisanego na kasie',
      cancelledEmpty: 'Brak anulowanych rachunków.',
      correctionsTitle: 'Korekty liczone przy sprzedaży',
      correctionsHint:
        'Korekta zmienia przychód dnia i punktu sprzedaży, której dotyczy, a nie dnia wystawienia ani serii, w której ją wprowadzono. Księgi liczą ją w dniu wystawienia.',
      correctionsHint_catering:
        'Korekta zmienia przychód dnia i stołówki sprzedaży, której dotyczy, a nie dnia wystawienia. Księgi liczą ją w dniu wystawienia.',
      countsFor: 'Liczona dla',
      replacement: 'wystawiona ponownie w miejsce skorygowanej',
      enteredIn: 'wprowadzona w serii: {{outlet}}',
      correctionsEmpty: 'Brak takich korekt w tym okresie.',
      correctionsDifference:
        'Ten okres różni się od ksiąg o {{difference}}: weszło {{into}} korekt z innych okresów, wyszło {{out}}.',
      issuedOn: 'Wystawiona',
      document: 'Dokument',
      saleDay: 'Liczona w dniu',
      otherMonth: 'inny miesiąc',
      change: 'Zmiana',
      internalTitle: 'Rozliczenia wewnętrzne (WEWN)',
      internalTitle_catering: 'Inne rozliczenia wewnętrzne (WEWN)',
      internalHint:
        'To nie jest przychód: rachunki rozliczone wewnętrznie, na przykład posiłki pracowników. Pokazane do kontroli.',
      internalHint_catering:
        'To nie jest przychód: rachunki rozliczone wewnętrznie z innego powodu niż karta pracownicza. Karta pracownicza jest przychodem, więc jej tu nie ma.',
      internalEmpty: 'Brak rozliczeń wewnętrznych.',
      internalEmpty_catering: 'Brak innych rozliczeń wewnętrznych.',
      unpaidTitle: 'Rachunki bez płatności',
      unpaidHint:
        'Rachunki zamknięte bez żadnej płatności: literówka, storno albo posiłek niezapłacony. Nie są przychodem. Wartość jak na kasie.',
      unpaidEmpty: 'Brak rachunków bez płatności.',
      unpaidLargest: 'Największe. Kliknij numer, aby zobaczyć pozycje.',
      date: 'Dzień',
      bill: 'Rachunek',
    },
    settings: {
      title: 'Ustawienia',
      subtitle: 'Gdzie liczy się przychód: punkty, punkty sprzedaży, serie i kategorie menu',
      readOnly: 'Tylko podgląd. Zmieniać może kierownik generalny lub administrator.',
      duplicateName: 'Ten punkt ma już kategorię o tej nazwie.',
      unplacedPointsOfSale: 'Punkty sprzedaży bez punktu: {{names}}',
      unplacedSeries: 'Serie bez punktu: {{names}}',
      unassignedGross: 'Przychód bez punktu w ostatnich 365 dniach: {{gross}}',
      mappingTitle: 'Punkty sprzedaży i serie',
      mappingHint:
        'Dla którego punktu liczy się przychód z każdego punktu sprzedaży Izzy i każdej serii dokumentów. Zmiana zapisuje się od razu.',
      pointsOfSale: 'Punkty sprzedaży',
      series: 'Serie dokumentów',
      posId: 'Nr',
      seriesCode: 'Kod',
      izzyName: 'Nazwa w Izzy',
      outlet: 'Punkt',
      outletFor: 'Punkt dla: {{name}}',
      noOutlet: '— brak —',
      outOfGastronomy: '— poza gastronomią —',
      canteen: 'Stołówka — celowo poza gastronomią',
      outletsTitle: 'Punkty',
      outletsHint: 'Nazwa, rodzaj i kolejność punktów na wszystkich ekranach gastronomii.',
      outletName: 'Nazwa',
      outletCode: 'Kod',
      isFood: 'Gastronomia',
      isFoodFor: 'Gastronomia: {{name}}',
      recordsCovers: 'Zapisuje nakrycia',
      recordsCoversFor: 'Zapisuje nakrycia: {{name}}',
      outletInUse_one: 'Liczy się tu {{count}} punkt sprzedaży, seria lub reguła — najpierw przenieś ją do innego punktu.',
      outletInUse_few: 'Liczą się tu {{count}} punkty sprzedaży, serie lub reguły — najpierw przenieś je do innego punktu.',
      outletInUse_many: 'Liczy się tu {{count}} punktów sprzedaży, serii lub reguł — najpierw przenieś je do innego punktu.',
      outletInUse_other: 'Liczy się tu {{count}} punktu sprzedaży, serii lub reguły — najpierw przenieś je do innego punktu.',
      deleteOutletConfirm: 'Oznaczyć „{{name}}” jako usunięty? Zniknie z ekranów i list; można go przywrócić.',
      deletedOutlets: 'Usunięte punkty',
      deletedOn: 'usunięty {{date}}',
      restoreOutlet: 'Przywróć',
      newOutletName: 'Nazwa nowego punktu',
      newOutletCode: 'Kod, np. bar-plaza',
      outletCodeHint: 'Kod: małe litery, cyfry i myślniki. Jest w adresach ekranów i nie zmienia się po utworzeniu.',
      addOutlet: 'Dodaj punkt',
      duplicateOutletCode: 'Punkt o kodzie „{{code}}” już jest — może wśród usuniętych.',
      moveUp: 'Przesuń wyżej: {{name}}',
      moveDown: 'Przesuń niżej: {{name}}',
      menuTitle: 'Kategorie menu',
      menuHint:
        'Kategorie każdego punktu i grupy Izzy w nich. Grupa zabiera ze sobą podgrupy, chyba że podgrupa ma własną kategorię. Pojedynczy produkt można przypisać osobno.',
      categoriesTitle: 'Kategorie',
      categoryName: 'Nazwa kategorii',
      sales365: 'Sprzedaż 365 dni',
      share: 'Udział',
      uncategorised: 'Bez kategorii',
      uncategorisedShow: '— pokaż, co tu wpada',
      uncategorisedHint:
        'Grupy Izzy bez kategorii i produkty bez grupy — ogólne kody Izzy na dokumentach, jak zadatek czy „usługa gastronomiczna”. Produkt bez grupy trafia do kategorii tylko przez własne przypisanie.',
      uncategorisedWhat: 'Grupa lub produkt',
      uncategorisedGroup: 'grupa',
      uncategorisedProduct: 'produkt bez grupy',
      uncategorisedNone: 'Nic więcej nie wpada bez kategorii.',
      noCategoryYet: '— bez kategorii —',
      deleteCategoryConfirm: 'Usunąć „{{name}}”? Jej grupy i produkty wrócą do „Bez kategorii”.',
      newCategory: 'Nowa kategoria',
      addCategory: 'Dodaj kategorię',
      groupsTitle: 'Grupy Izzy',
      groupsHint: 'Kategoria grupy obejmuje jej podgrupy. „Dziedziczy” — grupa liczy się tam, gdzie najbliższa grupa nadrzędna z kategorią.',
      searchGroup: 'Szukaj grupy',
      onlySold: 'Tylko grupy sprzedawane w tym punkcie',
      group: 'Grupa',
      groupSalesHint: 'Brutto z rachunków w tym punkcie w ostatnich 365 dniach, razem z podgrupami.',
      category: 'Kategoria',
      categoryFor: 'Kategoria dla: {{name}}',
      inherits: '— dziedziczy —',
      inherited: 'z grupy nadrzędnej: {{category}}',
      inheritedFrom: 'Z grupy „{{group}}”',
      noGroups: 'Brak grup do pokazania.',
      expand: 'Rozwiń: {{name}}',
      collapse: 'Zwiń: {{name}}',
      noGroup: 'Produkty bez grupy Izzy',
      noGroupHint: 'Ogólne kody Izzy na dokumentach — zadatek, zaliczka, „usługa gastronomiczna” — bez grupy w Izzy.',
      noGroupRule: 'kategoria i punkt tylko dla pojedynczego produktu — rozwiń',
      showProducts: 'Pokaż produkty: {{name}}',
      hideProducts: 'Ukryj produkty: {{name}}',
      productsTitle: 'Produkty przypisane osobno',
      productsHint: 'Produkt z własną kategorią lub punktem liczy się tam, niezależnie od swojej grupy. Ustawisz go, rozwijając jego grupę w drzewie.',
      product: 'Produkt',
      followsGroup: '— jak grupa —',
      countsFor: 'Liczy się w',
      countsForHint:
        'Punkt, w którym liczy się sprzedaż tej grupy z kas tego punktu — np. minigolf sprzedany w kawiarni liczy się w rekreacji. Rachunek dzieli się według pozycji. Podgrupy idą za grupą, chyba że mają własną regułę.',
      countsForFor: 'Liczy się w: {{name}}',
      followsParentOutlet: '— jak nadrzędna —',
      followsGroupOutlet: '— jak grupa —',
      staysHere: '{{name}} (tutaj)',
      categoryThere: 'kategoria w punkcie {{outlet}}',
      productMovesTitle: 'Produkty liczone w innym punkcie',
      movedIn: 'też z kas innych punktów',
      backToGroup: 'Wróć do grupy',
      noProductPlacements: 'Żaden produkt nie jest przypisany osobno.',
      noProducts: 'Nic nie znaleziono.',
      splitTitle: 'Produkty rozbijane wg uwag',
      splitHint:
        'Produkt, który obsługa nabija jako jeden, a danie wpisuje w uwagach — np. „Danie specjalne” z „ossobuco” albo „udziec z sarny”. Każdy wariant to danie i słowa, które je oznaczają; uwaga trafia do pierwszego wariantu, którego słowo zawiera. Na ekranach Dziś i Wyniki każdy wariant jest osobnym produktem.',
      splitSearch: 'Szukaj produktu po nazwie lub kodzie',
      splitAdd: 'Rozbijaj wg uwag',
      splitNone: 'Żaden produkt nie jest rozbijany.',
      splitLines: 'Ostatnie {{days}} dni: {{lines}} pozycji, bez wariantu: {{unmatched}}',
      splitRemove: 'Przestań rozbijać',
      splitRemoveQuestion: 'Liczyć „{{name}}” znów jako jeden produkt? Warianty zostaną usunięte.',
      splitVariant: 'Wariant',
      splitWords: 'Słowa w uwadze',
      splitWordsFor: 'Słowa dla wariantu {{name}}',
      splitWordsHint:
        'Słowa po przecinku. Wielkość liter i polskie znaki nie mają znaczenia; wystarczy fragment: „osob” łapie „ossobuco” i „osobuko”. Kolejność ma znaczenie — bardziej szczegółowy wariant daj wyżej.',
      splitDropVariant: 'Usuń wariant {{name}}',
      splitAddVariant: 'Dodaj wariant',
      splitNoVariants: 'Brak wariantów — każda pozycja liczy się jako „inne”.',
      splitInvalid: 'Każdy wariant potrzebuje nazwy i co najmniej jednego słowa.',
      splitDuplicate: 'Nazwy wariantów muszą się różnić.',
      splitRemarks: 'Uwagi z ostatnich {{days}} dni',
      splitOnlyUnmatched: 'Tylko bez wariantu',
      splitRemark: 'Uwaga',
      splitRemarkLines: 'Pozycje',
      splitRemarkGross: 'Brutto',
      splitNoRemarks: 'Brak uwag w tym czasie.',
      splitBlank: '(bez uwagi)',
      splitAssign: 'Przypisz „{{text}}” do wariantu',
      splitAssignTo: 'dodaj do: {{name}}',
      splitAssignNew: 'nowy wariant',
      // A canteen site is the outlet on catering's settings (`context: 'catering'`).
      duplicateName_catering: 'Ta stołówka ma już kategorię o tej nazwie.',
      unplacedPointsOfSale_catering: 'Punkty sprzedaży bez stołówki: {{names}}',
      unplacedSeries_catering: 'Serie bez stołówki: {{names}}',
      unassignedGross_catering: 'Przychód bez stołówki w ostatnich 365 dniach: {{gross}}',
      mappingHint_catering:
        'Dla której stołówki liczy się przychód z każdego punktu sprzedaży Izzy i każdej serii dokumentów. Zmiana zapisuje się od razu.',
      outlet_catering: 'Stołówka',
      outletFor_catering: 'Stołówka dla: {{name}}',
      outletsTitle_catering: 'Stołówki',
      outletsHint_catering: 'Nazwa i kolejność stołówek na wszystkich ekranach stołówek.',
      outletInUse_catering_one: 'Liczy się tu {{count}} punkt sprzedaży lub seria — najpierw przenieś je do innej stołówki.',
      outletInUse_catering_few: 'Liczą się tu {{count}} punkty sprzedaży lub serie — najpierw przenieś je do innej stołówki.',
      outletInUse_catering_many: 'Liczy się tu {{count}} punktów sprzedaży lub serii — najpierw przenieś je do innej stołówki.',
      outletInUse_catering_other: 'Liczy się tu {{count}} punktu sprzedaży lub serii — najpierw przenieś je do innej stołówki.',
      deleteOutletConfirm_catering: 'Oznaczyć „{{name}}” jako usuniętą? Zniknie z ekranów i list; można ją przywrócić.',
      deletedOutlets_catering: 'Usunięte stołówki',
      deletedOn_catering: 'usunięta {{date}}',
      newOutletName_catering: 'Nazwa nowej stołówki',
      newOutletCode_catering: 'Kod, np. ronal-w06',
      addOutlet_catering: 'Dodaj stołówkę',
      duplicateOutletCode_catering: 'Stołówka o kodzie „{{code}}” już jest — może wśród usuniętych.',
      menuHint_catering:
        'Kategorie każdej stołówki i grupy Izzy w nich. Grupa zabiera ze sobą podgrupy, chyba że podgrupa ma własną kategorię. Pojedynczy produkt można przypisać osobno.',
      onlySold_catering: 'Tylko grupy sprzedawane w tej stołówce',
      groupSalesHint_catering: 'Brutto z rachunków w tej stołówce w ostatnich 365 dniach, razem z podgrupami.',
      noGroupRule_catering: 'kategoria tylko dla pojedynczego produktu — rozwiń',
      productsHint_catering:
        'Produkt z własną kategorią liczy się w niej, niezależnie od swojej grupy. Ustawisz go, rozwijając jego grupę w drzewie.',
    },
  },
  catering: {
    nav: {
      today: 'Dziś',
      results: 'Wyniki',
      controls: 'Kontrola',
      settlement: 'Rozliczenie',
      settings: 'Ustawienia',
    },
    payments: {
      internal: 'Inne rozliczenia wewnętrzne',
      internalNote: 'poza przychodem',
      internalHint:
        'Rozliczenie wewnętrzne z innego powodu niż karta pracownicza. Nie jest przychodem, więc nie wchodzi w udziały powyżej.',
    },
    settlement: {
      title: 'Rozliczenie',
      subtitle: 'Miesiąc stołówki na karcie pracowniczej: raport kart dla pracodawcy i kwoty do faktur dla każdej firmy.',
      site: 'Stołówka',
      siteNoCard: '{{name}} (bez karty pracowniczej)',
      previousMonth: 'Poprzedni miesiąc',
      nextMonth: 'Następny miesiąc',
      noAccess: 'Nie możesz rozliczać stołówek. Robi to kierownik stołówek.',
      noSites: 'Stołówki nie mają jeszcze żadnej stołówki.',
      reportTitle: 'Raport kart',
      reportHint: 'Posiłki miesiąca na karcie pracowniczej, według kart, dla pracodawcy. Zawiera numery kart i numery pracowników.',
      download: 'Pobierz raport kart (.xlsx)',
      downloading: 'Pobieranie…',
      cards: 'Karty',
      reportTotal: 'Suma raportu',
      lines: 'Pozycje posiłków na kartę',
      unrated_one: '{{count}} pozycja posiłku na kartę nie ma stawki VAT. Jej części nie da się rozbić na netto i VAT.',
      unrated_few: '{{count}} pozycje posiłków na kartę nie mają stawki VAT. Ich części nie da się rozbić na netto i VAT.',
      unrated_many: '{{count}} pozycji posiłków na kartę nie ma stawki VAT. Ich części nie da się rozbić na netto i VAT.',
      unrated_other: '{{count}} pozycji posiłków na kartę nie ma stawki VAT. Ich części nie da się rozbić na netto i VAT.',
      noCardSales: 'W tym miesiącu ta stołówka nie wydała posiłku na kartę pracowniczą.',
      mixTitle: 'Sprzedaż na kartę według stawek VAT',
      mixRate: 'Stawka VAT',
      mixGross: 'Sprzedaż na kartę, brutto',
      mixShare: 'Udział',
      noRate: 'Bez stawki',
      companiesTitle: 'Firmy, którym pracodawca wystawia faktury',
      companiesHint: 'Wpisz każdą firmę i kwotę brutto podaną przez pracodawcę. Razem powinny dać sumę raportu.',
      companiesOffered: 'Firmy z poprzedniego miesiąca. Wpisz dla każdej kwotę brutto za ten miesiąc.',
      savedAt: 'Zapisano {{at}}.',
      company: 'Firma',
      companyOf: 'Firma {{n}}',
      gross: 'Brutto',
      grossOf: 'Brutto firmy {{n}}',
      remove: 'Usuń',
      removeOf: 'Usuń firmę {{n}}',
      typedTotal: 'Firmy razem',
      typedDifference: 'Firmy różnią się od sumy raportu o {{difference}} zł.',
      add: 'Dodaj firmę',
      undo: 'Cofnij',
      invalidRows: 'Każdy wiersz potrzebuje firmy (raz) i kwoty brutto z najwyżej dwoma miejscami po przecinku.',
      invoicesTitle: 'Kwoty do faktur',
      invoicesHint: 'Brutto każdej firmy rozbite na stawki VAT według sprzedaży miesiąca na kartę. Stawki sumują się do brutto firmy, netto i VAT do brutto stawki.',
      difference: 'Zapisane firmy różnią się od sumy raportu o {{difference}} zł. Sprawdź kwoty pracodawcy.',
      noCompanies: 'Dla tego miesiąca nie zapisano jeszcze firm.',
      rate: 'Stawka VAT',
      nett: 'Netto',
      vat: 'VAT',
      total: 'Razem',
      allCompanies: 'Wszystkie firmy',
    },
    settings: {
      title: 'Ustawienia',
      subtitle: 'Bazy, które czytają stołówki, gdzie liczy się przychód — stołówki, punkty sprzedaży, serie i kategorie menu — i powody rozliczenia, które są kartą pracowniczą.',
      readOnly: 'Tylko podgląd — zmiany wprowadza kierownik stołówek.',
      cardTitle: 'Karta pracownicza — {{name}}',
      cardHint:
        'Powody rozliczenia wewnętrznego tej bazy. Zaznaczone to karta pracownicza: ich kwota jest przychodem, fakturowanym pracodawcy. Pozostałe nie są przychodem.',
      isCard: 'Karta pracownicza',
      isCardOf: 'Karta pracownicza: {{name}}',
      reason: 'Powód',
      code: 'Kod',
      bills: 'Rachunki w roku',
      noReasons: 'Ta baza nie ma powodów rozliczenia wewnętrznego.',
      undo: 'Cofnij',
    },
  },
  navBar: {
    more: 'Więcej',
    waiting_one: '{{count}} czeka',
    waiting_few: '{{count}} czekają',
    waiting_many: '{{count}} czeka',
    waiting_other: '{{count}} czeka',
  },
  cashier: {
    history: {
      documentsSince: 'Możesz szukać dokumentów od {{date}}.',
    },
    documentKind: {
      receipt: 'Paragon',
      invoice: 'Faktura',
      correction: 'Korekta',
    },
    documents: {
      noLines: 'Brak pozycji dla tego dokumentu.',
    },
    closeFlow: {
      drawerStart: 'Stan kasy',
      drawerStartHint: 'na start dnia, po przekazaniu',
      takingsToday: 'Utarg gotówkowy dnia',
      filter: 'Filtruj',
      filterActive: 'Filtruj ({{n}})',
      clearFilters: 'Wyczyść filtry',
      addAnother: 'Dodaj kolejny raport',
      settleSummary: 'Utarg gotówkowy {{amount}}',
      printerSuggestion: 'z systemu: {{total}} brutto',
      unassignedSeries_one: 'Seria {{series}} nie ma przypisanej drukarki — ustaw w Ustawieniach.',
      unassignedSeries_few: 'Serie {{series}} nie mają przypisanej drukarki — ustaw w Ustawieniach.',
      unassignedSeries_many: 'Serie {{series}} nie mają przypisanej drukarki — ustaw w Ustawieniach.',
      unassignedSeries_other: 'Serie {{series}} nie mają przypisanej drukarki — ustaw w Ustawieniach.',
      takingsTitle: 'Utarg gotówkowy dnia',
      countTitle: 'Gotówka w kasie',
      countWhole: 'całą gotówkę w kasie, także drobne',
      countFirst: 'Różnica pokaże się po policzeniu.',
      countAfterSlips: 'Różnica pokaże się po wpisaniu wydruków.',
      handoverSection: 'Przekazanie do kasy głównej',
      handoverAmount: 'Do przekazania',
      bagNumber: 'Numer worka (opcjonalnie)',
      bagLine: 'worek {{bag}}',
      handoverAccepted: 'przyjęte przez kasę główną',
      handoverWaiting: 'czeka na przyjęcie w kasie głównej',
      noMainDesk: 'Ten punkt nie ma ustawionej kasy głównej — raport złożysz bez przekazania.',
      commentHint: 'Krótko: co się stało, np. „pomyłka przy reszcie”.',
      afterSubmitHandover: 'Po złożeniu kierownik sprawdzi raport, a {{amount}} poczeka na przyjęcie w kasie głównej.',
      afterSubmitOwn: 'Po złożeniu dzień jest zamknięty.',
      afterSubmitOwnHandover: 'Po złożeniu dzień jest zamknięty, a {{amount}} poczeka na przyjęcie w kasie głównej.',
      ownAccepts: 'Jesteś kierownikiem tego punktu — raport zostanie od razu zatwierdzony.',
      submitHandover: 'Złóż raport i przekaż {{amount}}',
      blocker: {
        slips: 'Najpierw wpisz wydruki: raport fiskalny i terminal.',
        blocked: 'Nie możesz jeszcze złożyć raportu — zobacz komunikat na górze strony.',
        invalid: 'Popraw zaznaczone pola, potem złożysz raport.',
        count: 'Wpisz, ile gotówki policzono.',
        comment: 'Wyjaśnij różnice w komentarzu.',
      },
      doneAccepted: 'Raport złożony i zatwierdzony.',
      doneHandover: 'Przekazanie {{amount}} czeka na przyjęcie w kasie głównej.',
      progress: 'Kroki zamknięcia dnia',
      stepDone: '(gotowe)',
      stepLocked: '(najpierw wydruki)',
      step: {
        settle: 'Rozliczenie',
        slips: 'Wydruki',
      },
      short: {
        settle: 'Rozliczenie',
        slips: 'Wydruki',
      },
      next: 'Dalej: {{step}}',
      toNextShort: 'Następny: {{date}}',
      fiscalSummary_one: '{{count}} raport · {{total}}',
      fiscalSummary_few: '{{count}} raporty · {{total}}',
      fiscalSummary_many: '{{count}} raportów · {{total}}',
      fiscalSummary_other: '{{count}} raportu · {{total}}',
      terminalSummary_one: '{{count}} terminal · {{total}}',
      terminalSummary_few: '{{count}} terminale · {{total}}',
      terminalSummary_many: '{{count}} terminali · {{total}}',
      terminalSummary_other: '{{count}} terminala · {{total}}',
      problem: {
        terminalTotal: '{{device}}: wpisz sumę z wydruku.',
        missing: 'Brak raportu: {{device}}.',
        printedAt: '{{device}}: wpisz czas wydruku.',
        total: '{{device}}: wpisz brutto razem z wydruku.',
        rateSum: 'Stawki dają {{sum}}, wydruk {{total}}. Sprawdź netto i VAT.',
        vat: '{{device}}, stawka {{rate}}: VAT powinien wynosić ok. {{expected}}.',
      },
      noRates: 'Bez stawek.',
      rateSumOff: 'Stawki dają {{sum}}, a wydruk {{total}}. Sprawdź netto i VAT z wydrukiem.',
      expectedNegative: 'Wynik jest poniżej zera: przekazano więcej gotówki, niż było w kasie.',
      cashMatches: 'Zgadza się',
      cashMatchesHint: 'W kasie jest tyle, ile powinno być.',
      cashShort: 'Brakuje {{amount}}',
      cashOver: 'Nadwyżka {{amount}}',
      cashRecount: 'Policz jeszcze raz. Jeśli się potwierdzi, wyjaśnij w komentarzu.',
      showCardPayments: 'Pokaż płatności kartą ({{n}})',
      closeDay: 'Zamknij {{date}}',
      staleHint: 'Poczekaj, aż przyjdą nowe dane z izzy, i wróć tutaj.',
      paperTitle: 'Faktury i korekty: masz kopie na papierze?',
      afterSubmit: 'Po złożeniu kierownik sprawdzi raport. Zmienisz go tylko, jeśli go zwróci.',
      doneTitle: 'Raport złożony.',
      doneHint: 'Kierownik go sprawdzi.',
      summaryTitle: 'Podsumowanie dnia',
      cashLine: 'Gotówka: nie policzono',
      cashLineOk: 'Gotówka się zgadza',
      cashLineDiff: 'Różnica w gotówce: {{amount}}',
      cashFigures: 'Policzono {{counted}} · powinno być {{expected}}',
      explanation: 'Wyjaśnienie kasjera',
      noExplanation: 'Bez wyjaśnienia.',
      changesTitle: 'Zmiany w wersji {{number}} (od wersji {{previous}})',
      noChanges: 'Kasjer nic nie zmienił.',
      changeCounted: 'Policzono: {{from}} → {{to}}',
      changeFiscal: 'Raporty fiskalne: {{from}} → {{to}} (raportów: {{fromCount}} → {{toCount}})',
      changeFiscalDetail: 'Raporty fiskalne: zmienione stawki lub czas wydruku, suma bez zmian ({{total}})',
      changeTerminalDetail: 'Terminale: zmieniony czas wydruku, suma bez zmian ({{total}})',
      changeTerminal: 'Terminale: {{from}} → {{to}} (raportów: {{fromCount}} → {{toCount}})',
      change: {
        dropped: 'Usunięte z dnia',
        restored: 'Przywrócone',
        pulledIn: 'Dołączone do dnia',
        unpulled: 'Cofnięte dodanie',
        checked: 'Zaznaczone „na papierze”',
        unchecked: 'Odznaczone „na papierze”',
        paymentChanged: 'Zmieniona forma płatności',
        paymentUndone: 'Cofnięta zmiana formy płatności',
      },
      return: 'Zwróć',
      returnHint: 'Kasjer zobaczy powód przy tym dniu.',
      acceptNext: 'Zatwierdź i następny',
      dropFor: 'Usuń z dnia: {{ref}}',
      restoreFor: 'Przywróć: {{ref}}',
      unpullFor: 'Cofnij dodanie: {{ref}}',
      toHandovers: 'Przyjmij utargi w kasie głównej',
    },
    transferFlow: {
      bagShort: 'worek {{bag}}',
      noBagShort: 'bez worka',
      corrected: 'Poprawione po przyjęciu',
      correctedLine: 'Przyjęto {{was}} ({{name}}, {{at}}). Teraz {{amount}}, różnica {{difference}}.',
      keepApart: 'Kasa główna nie liczy tej kwoty, dopóki jej nie przyjmiesz ponownie.',
      dayWhole: 'Utarg dnia przyjmujesz tylko w całości. Zwróć dzień: kasjer go poprawi, a to samo przekazanie wróci z właściwą kwotą.',
      dayNotYours: 'Nie możesz zwrócić tego dnia. Poproś kierownika punktu o poprawę raportu.',
      returnReasonLabel: 'Co się nie zgadza (wymagane)',
      returnNote: 'Policzono {{counted}}, przekazanie {{amount}}. {{text}}',
      returnDay: 'Zwróć dzień do poprawy',
      countQuestion: 'Ile policzono?',
      refuseWhole: 'Przekazanie przyjmujesz tylko w całości. Odrzuć je: nadawca wyśle gotówkę ponownie, z kwotą, którą policzyliście razem.',
      refuseNotYours: 'Nie możesz odrzucić tego przekazania. Poproś kasjera głównego.',
      refuse: 'Odrzuć przekazanie',
      acceptAmount: 'Przyjmij {{amount}}',
      sendTitle: 'Przekaż gotówkę: {{name}}',
      moveTitle: 'Przenieś z: {{name}}',
      floatTitle: 'Zasil punkt z: {{name}}',
      toHandOver: 'Do przekazania',
      where: 'Dokąd?',
      from: 'Skąd',
      to: 'Dokąd',
      bagOptional: 'Numer worka (opcjonalnie)',
      noteHint: 'Inna kwota niż do przekazania wymaga notatki.',
      next: 'Dalej',
      back: 'Popraw',
      confirmTitle: 'Sprawdź przed wysłaniem',
      sendAmount: 'Wyślij {{amount}}',
      sendFinal: 'Wysłanego przekazania nie można cofnąć. Kasjer główny policzy je przy odbiorze.',
      moveFinal: 'Przeniesienia nie można cofnąć. Jest księgowane od razu, bez liczenia.',
      countOff: 'Policzono {{counted}}, powinno być {{amount}}: {{gap}}.',
      missing: 'brakuje {{amount}}',
      surplus: 'jest więcej o {{amount}}',
    },
    denominations: {
      title: 'Policz nominałami',
      short: 'Nominałami',
      hint: 'Ile sztuk każdego banknotu; monety jako jedna kwota.',
      coins: 'Monety',
      total: 'Razem',
      countOf: 'Liczba banknotów {{note}}',
      less: 'Jeden banknot {{note}} mniej',
      more: 'Jeden banknot {{note}} więcej',
      use: 'Użyj {{amount}}',
      notStored: 'Pomocnik do liczenia. Zapisujemy tylko sumę.',
    },
    nav: {
      closeShort: 'Raport',
      reportsShort: 'Historia',
      close: 'Raport dnia',
      reports: 'Historia raportów',
      overview: 'Przegląd',
      main: 'Kasa główna',
      settings: 'Ustawienia',
    },
    desk: 'Punkt kasowy',
    inactiveDesk: '{{name}} (nieaktywny)',
    noSalesDesks: 'Brak punktów kasowych do pokazania.',
    notAvailable: 'Nie masz uprawnień do modułu Kasa.',
    invalidAmount: 'Wpisz kwotę, np. 12,50.',
    mainDesk: 'Kasa główna',
    stashLabel: '{{company}} — kasa {{stash}}',
    inactiveMark: '(nieaktywna)',
    state: {
      none: 'Brak raportu',
      draft: 'Szkic',
      submitted: 'Złożony',
      returned: 'Zwrócony',
      accepted: 'Zatwierdzony',
    },
    level: {
      warning: 'Ostrzeżenie',
      error: 'Błąd',
      blocking: 'Blokuje',
    },
    check: {
      FISCAL_NOT_VALID: 'Raport fiskalny: brak albo się nie sumuje.',
      TERMINAL_NOT_VALID: 'Brak raportu z terminala.',
      SYSTEM_CASH_NOT_VALID: 'Gotówka: wydruki {{declared}}, izzy {{system}}.',
      SYSTEM_CARD_NOT_VALID: 'Karta: wydruki {{declared}}, izzy {{system}}.',
      SYSTEM_TOTAL_NOT_VALID: 'Razem: wydruki {{declared}}, izzy {{system}}.',
      SMALL_CASH_DIFF: 'Mała różnica w gotówce: policzono {{counted}}, powinno być {{expected}}.',
      LARGE_CASH_DIFF: 'Duża różnica w gotówce: policzono {{counted}}, powinno być {{expected}}.',
      PREVIOUS_REPORT_NOT_APPROVED: 'Poprzedni dzień czeka na zatwierdzenie przez kierownika.',
      NO_PREVIOUS_REPORT: 'To pierwszy raport tego punktu.',
      MISSING_REPORT: 'Najpierw zamknij dni ze sprzedażą bez raportu: {{days}}.',
      FISCAL_DATE_MISMATCH: 'Raport fiskalny ma inną datę niż ten dzień.',
      SYSTEM_DATA_STALE: 'Dane z izzy ({{asOf}}) są starsze niż raport fiskalny ({{printedAt}}).',
      DOCUMENT_COUNTED_TWICE: 'Dokument policzony też w innym raporcie: {{documents}}. Cofnij dodanie albo poproś o poprawienie tamtego dnia.',
      FISCAL_MISSING: 'Brak raportu fiskalnego: {{devices}}.',
      TERMINAL_MISSING: 'Brak raportu z terminala: {{devices}}.',
    },
    slips: {
      hint: 'Przepisz kwoty z wydruków: z raportu fiskalnego brutto razem, z terminala sumę z zamknięcia dnia. Wydruk zerowy: wpisz 0. Stawki VAT uzupełni system.',
      fiscalTitle: 'Raporty fiskalne',
      terminalTitle: 'Terminale',
      terminalHint: 'suma z zamknięcia dnia',
      printed: 'wydruk {{at}}',
      timeChange: 'zmień',
      timeDone: 'gotowe',
      addReport: 'Dodaj kolejny raport: {{device}}',
      rates: 'Stawki VAT:',
      ratesFromSystem: 'Stawki VAT z systemu:',
      ratesAgree: 'dają brutto razem',
      ratesSumOff: 'dają {{sum}}, a brutto razem to {{total}}',
      ratesVatOff: 'VAT jednej stawki się nie zgadza',
      editRates: 'Zmień stawki',
      checkRates: 'Sprawdź stawki',
      showRates: 'Pokaż stawki',
      hideRates: 'Zwiń stawki',
      ratesHint: 'Stawki z systemu, ze sprzedaży na tej drukarce. Popraw tylko te, które różnią się od wydruku.',
    },
    payments: {
      change: 'Zmień formę płatności',
      changeFor: 'Zmień formę płatności: {{ref}}',
      undo: 'Cofnij zmianę formy płatności',
      undoFor: 'Cofnij zmianę formy płatności: {{ref}}',
      undoShort: 'cofnij',
      title: 'Forma płatności dokumentu na {{amount}}',
      izzy: 'W izzy: {{payments}}.',
      onlyHere: 'Zmiana dotyczy tylko tego rozliczenia; w izzy dokument zostaje bez zmian.',
      methods: 'Forma płatności',
      split: 'Podziel',
      remaining: 'Do rozdzielenia: {{amount}}',
      addsUp: 'Razem tyle, ile dokument.',
      unreadable: 'Popraw kwotę, której nie da się odczytać.',
      same: 'Tak samo jak w izzy: to nie jest zmiana.',
      reason: 'Dlaczego (wymagane, zobaczy kierownik)',
      apply: 'Zmień',
      was: 'w izzy: {{payments}}',
      none: 'bez płatności',
      kind: {
        cash: 'gotówka',
        card: 'karta',
        transfer: 'przelew',
      },
    },
    handover: {
      wholeTakings: 'cały utarg gotówkowy dnia',
      acceptedBy: 'przyjęte: {{name}}, {{at}}',
      correctedFrom: 'Poprawione po przyjęciu: było {{amount}}, przyjęte przez {{name}}, {{at}}. Czeka na ponowne przyjęcie.',
      reaccept: 'Przyjęte wcześniej: {{was}}. Po złożeniu to samo przekazanie: {{amount}} ({{difference}}), do ponownego przyjęcia w kasie głównej.',
      inTransit: 'Poprzednie złożenie wysłało {{was}}. Po złożeniu to samo przekazanie zmieni kwotę na {{amount}}.',
      stillWaiting: 'Przekazanie z poprzedniego złożenia czeka na przyjęcie w kasie głównej.',
      nothing: 'Utarg gotówkowy nie jest powyżej zera: dziś nic nie przekazujesz.',
    },
    dayBar: {
      version: 'Wersja {{number}}',
      nextVersion: 'Wersja {{number}} w przygotowaniu',
      submitted: 'Złożono',
      accepted: 'Zatwierdzono',
      returned: 'Zwrócono',
      reopened: 'Otwarto ponownie',
      whatHappens: 'Co się stanie',
      laterClosed: 'Ten dzień zmienisz dopiero po otwarciu późniejszego zamkniętego dnia: {{date}}. Gotówka dnia przechodzi na dni po nim.',
      reopenTip: 'Otworzyć można tylko ostatni zamknięty dzień punktu. Powód trafi do historii dnia.',
      acceptTip: 'Zatwierdzasz jako kierownik punktu. Przekazanie czeka na przyjęcie w kasie głównej.',
      reopenHint: 'Kasjer zobaczy powód przy tym dniu. Jeśli poprawa zmieni utarg, przekazanie wróci do ponownego przyjęcia w kasie głównej.',
      windowLabel: 'Okno sprzedaży dnia',
    },
    finish: {
      checkTitle: 'Sprawdzenie',
      checkHint: 'Co mówią izzy, terminal i raport fiskalny. Tu nic nie wpisujesz.',
      title: 'Do zrobienia',
      progress: 'Krok {{step}} z {{total}}',
      hint: 'Po kolei, od góry. Świeci to, co teraz.',
      count: 'Policz gotówkę',
      paper: 'Faktury i korekty na papierze',
      paperHint: 'Zaznacz te, których kopię masz. Nie wstrzymuje złożenia.',
      paperCount: '{{checked}} z {{total}}',
      comment: 'Komentarz',
      finish: 'Przekaż gotówkę i złóż raport',
      finishNoHandover: 'Złóż raport',
      now: 'teraz',
      optional: 'opcjonalnie',
      required: 'wymagany',
      added: 'dodany',
      afterSlips: 'po wpisaniu wydruków',
      afterCount: 'po policzeniu',
      last: 'ostatni krok',
      waits: 'czeka',
      countedAgrees: 'policzono {{amount}} · zgadza się',
      countedDiffers: 'policzono {{amount}} · różnica {{difference}}',
    },
    dayPanel: {
      label: 'Panel dnia',
      documents: 'Dokumenty dnia',
      history: 'Historia i komentarze',
      title: 'Dokumenty i historia',
      open: 'Dokumenty i historia ({{n}})',
      noHistory: 'Historia pojawi się po pierwszym zapisie dnia.',
      notSubmitted: 'Jeszcze nie złożono.',
    },
    summary: {
      slips: 'Wydruki',
      fiscalSlip: '{{device}}, raport fiskalny',
      terminalSlip: '{{device}}, terminal',
      paymentChanges_one: 'Zmieniona forma płatności: {{count}} dokument',
      paymentChanges_few: 'Zmieniona forma płatności: {{count}} dokumenty',
      paymentChanges_many: 'Zmieniona forma płatności: {{count}} dokumentów',
      paymentChanges_other: 'Zmieniona forma płatności: {{count}} dokumentu',
      paper: 'Faktury i korekty na papierze: {{ticked}} z {{count}}',
      showDetails: 'Pokaż szczegóły',
      hideDetails: 'Zwiń szczegóły',
      allCards: 'wszystkie karty',
      fiscalSales: 'sprzedaż fiskalna',
      fromSlips: 'z wydruków',
    },
    reopen: {
      titleReopened: 'Dzień otwarty ponownie: wersja {{number}}',
      titleReturned: 'Dzień zwrócony do poprawy: wersja {{number}}',
      reason: '{{name}}, {{at}}: „{{text}}”',
      handoverAccepted: 'Przekazanie z tego dnia jest już przyjęte w kasie głównej: {{amount}}. Jeśli poprawa zmieni utarg gotówkowy, to samo przekazanie dostanie nową kwotę i wróci do kasy głównej do ponownego przyjęcia; do tego czasu kasa główna nie liczy tej kwoty. Nowy wpis nie powstanie.',
      handoverSent: 'Przekazanie z tego dnia ({{amount}}) czeka w kasie głównej. Po złożeniu dostanie kwotę poprawionego utargu.',
    },
    settle: {
      waiting: 'Czeka na raport fiskalny i terminal',
      takingsPending: 'policzymy po wpisaniu wydruków',
      takingsSame: 'z wydruków · tyle samo co gotówka w izzy',
      takingsLess: 'z wydruków · o {{amount}} mniej niż gotówka w izzy',
      takingsMore: 'z wydruków · o {{amount}} więcej niż gotówka w izzy',
      more: '{{amount}} więcej',
      less: '{{amount}} mniej',
      showCashPayments: 'Pokaż płatności gotówką ({{n}})',
      candidates_one: 'Dokładnie {{amount}} {{method}} ma jeden dokument. Jeśli klient płacił inaczej, zmień jego formę płatności:',
      candidates_few: 'Dokładnie {{amount}} {{method}} mają {{count}} dokumenty. Jeśli klient płacił inaczej, zmień formę płatności właściwego:',
      candidates_many: 'Dokładnie {{amount}} {{method}} ma {{count}} dokumentów. Jeśli klient płacił inaczej, zmień formę płatności właściwego:',
      candidates_other: 'Dokładnie {{amount}} {{method}} ma {{count}} dokumentu. Jeśli klient płacił inaczej, zmień formę płatności właściwego:',
      byMethod: {
        cash: 'gotówką',
        card: 'kartą',
      },
      handoverWaits: 'Otworzy się, gdy wpiszesz, ile policzono.',
      handoverWaitsSlips: 'Otworzy się po wpisaniu wydruków i policzeniu gotówki.',
      insert: 'Wstaw:',
      quote: '„{{text}}”',
      slips: {
        title_both: 'Najpierw wpisz wydruki',
        title_fiscal: 'Najpierw wpisz raport fiskalny',
        title_terminal: 'Najpierw wpisz raport z terminala',
        body_both: 'Brakuje raportu fiskalnego ({{fiscal}}) i raportu z terminala ({{terminal}}). Bez nich nie policzymy utargu i nie złożysz raportu.',
        body_fiscal: 'Brakuje raportu fiskalnego: {{fiscal}}. Bez niego nie policzymy utargu i nie złożysz raportu.',
        body_terminal: 'Brakuje raportu z terminala: {{terminal}}. Bez niego nie policzymy utargu i nie złożysz raportu.',
        to_both: 'Wpisz wydruki',
        to_fiscal: 'Wpisz raport fiskalny',
        to_terminal: 'Wpisz raport z terminala',
      },
      agree: {
        title: 'Wszystko się zgadza',
        body: 'Terminal i raport fiskalny podają te same kwoty co izzy. Tyle gotówki przybyło w kasie.',
      },
      method: {
        title_card: 'Płatność kartą zapisana w izzy jako gotówka',
        body_card: 'Terminal ma {{amount}} więcej niż karty w izzy, a suma sprzedaży się zgadza. To ta sama sprzedaż, zapisana z inną formą płatności.',
        comment_card: 'Płatność kartą {{amount}} zapisana w izzy jako gotówka.',
        title_cash: 'Płatność gotówką zapisana w izzy jako karta',
        body_cash: 'Terminal ma {{amount}} mniej niż karty w izzy, a suma sprzedaży się zgadza. To ta sama sprzedaż, zapisana z inną formą płatności.',
        comment_cash: 'Płatność gotówką {{amount}} zapisana w izzy jako karta.',
        next_card: 'Znajdź ją wśród płatności gotówką i zmień jej formę płatności tutaj albo w izzy.',
        next_cash: 'Znajdź ją wśród płatności kartą i zmień jej formę płatności tutaj albo w izzy.',
      },
      fiscal: {
        title_less: 'Na raporcie fiskalnym brakuje {{amount}}',
        body_less: 'Izzy ma {{amount}} więcej sprzedaży fiskalnej niż drukarka. Karty się zgadzają, więc chodzi o gotówkę: paragon się nie wydrukował albo dokument jest z innego dnia.',
        next_less: 'Policz kasę: {{amount}} nadwyżki znaczy, że paragon się nie wydrukował.',
        comment_less: 'Na raporcie fiskalnym brakuje {{amount}} względem izzy.',
        title_more: 'Na raporcie fiskalnym jest {{amount}} więcej niż w izzy',
        body_more: 'Drukarka ma {{amount}} więcej sprzedaży niż izzy. Karty się zgadzają, więc chodzi o gotówkę: paragon wystawiony poza izzy albo dokument z innego dnia.',
        next_more: 'Jeśli dokument jest z innego dnia, dociągnij go do tego dnia.',
        comment_more: 'Na raporcie fiskalnym jest {{amount}} więcej niż w izzy.',
      },
      cardSale: {
        title_more: 'Sprzedaży kartą na {{amount}} nie ma w izzy',
        body_more: 'Terminal i raport fiskalny mają po {{amount}} więcej niż izzy. Gotówka się zgadza. Zwykle to dokument z innego dnia albo z innej kasy.',
        next_more: 'Dociągnij ten dokument albo opisz to niżej.',
        comment_more: 'Sprzedaż kartą {{amount}} jest na wydrukach, nie ma jej w izzy.',
        title_less: 'W izzy jest sprzedaż kartą na {{amount}}, której nie ma na wydrukach',
        body_less: 'Terminal i raport fiskalny mają po {{amount}} mniej niż izzy. Gotówka się zgadza. Zwykle to dokument z innego dnia.',
        next_less: 'Wyłącz go z tego dnia albo opisz to niżej.',
        comment_less: 'Sprzedaż kartą {{amount}} jest w izzy, nie ma jej na wydrukach.',
      },
      two: {
        title: 'Dwie różnice do wyjaśnienia',
        body: 'Terminal: {{terminal}} niż karty w izzy. Raport fiskalny: {{fiscal}} niż sprzedaż fiskalna w izzy.',
        next: 'Sprawdź każdą osobno, zacznij od terminala.',
      },
      flow: {
        title: 'Od izzy do utargu',
        izzy: 'Izzy',
        izzyHint: 'sprzedaż dnia według form płatności',
        fiscal: 'Fiskalne',
        nonFiscal: 'Niefiskalne',
        nonFiscalNote: 'Niefiskalne: {{list}}. Nie ma ich na raporcie fiskalnym; płacone kartą są na terminalu.',
        documents_one: '{{count}} dokument',
        documents_few: '{{count}} dokumenty',
        documents_many: '{{count}} dokumentów',
        documents_other: '{{count}} dokumentu',
        legendTerminal: 'porównujemy z terminalem',
        legendFiscal: 'porównujemy z raportem fiskalnym',
        terminal: 'Terminal',
        terminalHint: 'wszystkie karty: z paragonów i z faktur',
        terminalSlip: 'Zamknięcie dnia terminala',
        terminalIzzy: 'Karta w izzy, razem',
        fiscalTitle: 'Raport fiskalny',
        fiscalHint: 'tylko sprzedaż fiskalna, bez dokumentów niefiskalnych',
        fiscalSlip: 'Raport dobowy',
        fiscalIzzy: 'Sprzedaż fiskalna w izzy',
        cash: 'Utarg gotówkowy',
        cashHint: 'liczony z wydruków: raport fiskalny bez kart fiskalnych',
        cashFiscal: 'Raport fiskalny',
        cashCard: 'Karta fiskalna',
        cashCardHint: 'terminal {{terminal}} − niefiskalne {{nonFiscal}}',
        cashCardMissing: 'terminal: nie wpisano',
        cashMoneyless: 'Nie do kasy',
        cashNonFiscal: 'Gotówka niefiskalna',
        fromIzzy: 'z izzy',
        cashTakings: 'Utarg gotówkowy',
        cashIzzy: 'Gotówka w izzy',
        notEntered: 'nie wpisano',
        enterStep: 'wpisz w kroku {{n}}',
        agrees: 'zgadza się',
        moreThanIzzy: '{{amount}} więcej niż izzy',
        lessThanIzzy: '{{amount}} mniej niż izzy',
        waiting: 'czeka na wydruki',
        sameAsIzzy: 'tyle co w izzy',
        cause_terminal: 'Ta różnica wynika z terminala, wyżej. Nie szukaj jej osobno.',
        cause_fiscal: 'Ta różnica wynika z raportu fiskalnego, wyżej. Nie szukaj jej osobno.',
        cause_both: 'Ta różnica wynika z terminala i raportu fiskalnego, wyżej. Nie szukaj jej osobno.',
      },
    },
    split: {
      cash: 'Gotówka',
      card: 'Karta',
      transfer: 'Przelew',
      other: 'Nie do kasy (np. na pokój)',
      total: 'Razem',
    },
    balance: {
      expected: 'Powinno być w kasie',
      counted: 'Policzono',
      difference: 'Różnica',
    },
    documentTag: {
      correction: 'korekta',
      invoice: 'faktura',
      nonFiscal: 'niefiskalny',
      pulledIn: 'dołączony',
      otherSeries: 'seria innego punktu',
      settledBy: 'rozliczony w: {{desk}}',
      dropped: 'usunięty z dnia',
      changed: 'zmieniona płatność',
    },
    commentKind: {
      note: 'Komentarz',
      return: 'Zwrot',
      reopen: 'Ponowne otwarcie',
    },
    bookKind: {
      deposit: 'Wpłata do banku',
      withdrawal: 'Wypłata z banku',
      income: 'Przychód',
      expense: 'Rozchód',
      loan: 'Pożyczka',
      repayment: 'Zwrot pożyczki',
      funding: 'Zasilenie',
      defunding: 'Zwrot zasilenia',
      'loan-given': 'Pożyczka dla kasy A',
      'loan-returned': 'Zwrot pożyczki od kasy A',
      move: 'Przekazanie do B',
      'move-in': 'Przekazanie z B',
      'transfer-in': 'Przekazanie przyjęte',
      'transfer-out': 'Przekazanie',
      payout: 'Wypłata',
      'payout-return': 'Zwrot wypłaty',
    },
    paymentKind: {
      cash: 'Gotówka',
      card: 'Karta',
      transfer: 'Przelew',
      none: 'Nie do kasy',
    },
    close: {
      beforeOpening: 'Ten dzień jest przed pierwszym dniem punktu ({{date}}) — tylko podgląd. Pierwszy dzień zmienisz w Ustawieniach.',
      future: 'Ten dzień jeszcze się nie zaczął — tylko podgląd.',
      noDocumentsMatch: 'Żaden dokument nie pasuje do filtrów.',
      searchRef: 'Szukaj numeru',
      allKinds: 'Wszystkie',
      anyPayment: 'Dowolna',
      tags: 'Znaczniki',
      documentType: 'Typ',
      title: 'Raport dnia',
      subtitle: 'Raport fiskalny, terminal, potem rozliczenie i złożenie',
      day: 'Dzień',
      toNext: 'Następny dzień do zamknięcia: {{date}}',
      window: 'Sprzedaż od {{from}} do {{to}}',
      saveInvalid: 'Niezapisane — popraw zaznaczone pola.',
      saved: 'Zapisano {{time}}',
      saveFailed: 'Nie zapisano: {{message}}',
      saveFirst: 'Nie udało się zapisać szkicu. Spróbuj ponownie.',
      notYours: 'Nie zamykasz dni w tym punkcie — tylko podgląd.',
      inactive: 'nieaktywne',
      noPrinters: 'Ten punkt nie ma aktywnej drukarki.',
      noFiscal: 'Brak raportu z tej drukarki.',
      fiscalNumber: '{{name}} — raport {{number}}',
      printedAt: 'Czas wydruku',
      totalGross: 'Brutto razem',
      removeReport: 'Usuń',
      rate: 'Stawka',
      rateLabel: '{{letter}} {{percent}} %',
      nett: 'Netto',
      vat: 'VAT',
      gross: 'Brutto',
      nettFor: 'Netto, stawka {{rate}}',
      vatFor: 'VAT, stawka {{rate}}',
      vatOff: 'VAT powinien wynosić ok. {{expected}}',
      rateSum: 'Suma stawek',
      suggestionNoLines_one: '{{count}} dokument bez pozycji — sugestia niepełna.',
      suggestionNoLines_few: '{{count}} dokumenty bez pozycji — sugestia niepełna.',
      suggestionNoLines_many: '{{count}} dokumentów bez pozycji — sugestia niepełna.',
      suggestionNoLines_other: '{{count}} dokumentu bez pozycji — sugestia niepełna.',
      suggestionUnmapped: 'Stawka {{rate}} % nie ma litery na drukarce: {{gross}} brutto poza sugestią.',
      fillFromSystem: 'Uzupełnij z systemu',
      noTerminals: 'Ten punkt nie ma aktywnego terminala.',
      noTerminalReports: 'Brak raportu z tego terminala.',
      terminalTotal: 'Suma',
      systemAsOf: 'dane z izzy z {{time}}',
      noSystemData: 'Brak danych z izzy dla tego punktu.',
      documentsHint_one: '{{count}} dokument',
      documentsHint_few: '{{count}} dokumenty',
      documentsHint_many: '{{count}} dokumentów',
      documentsHint_other: '{{count}} dokumentu',
      noDocuments: 'Brak dokumentów sprzedaży w tym dniu.',
      documentsShown: '{{shown}} z {{count}}',
      paidWith: 'Płatność',
      document: 'Dokument',
      createdAt: 'Utworzono',
      drop: 'Usuń z dnia',
      dropReason: 'Powód usunięcia',
      restore: 'Przywróć',
      unpull: 'Cofnij dodanie',
      pullInTitle: 'Dodaj dokument z innego dnia lub serii',
      documentNumber: 'Numer dokumentu',
      documentNumberHint: 'np. 1/10 albo 12/9/K/2026',
      seriesOfDesk: 'Seria {{series}} — przypisana do: {{desk}}',
      seriesOfNoDesk: 'Seria {{series}} — nieprzypisana',
      searchLimit: 'Pokazano {{count}} najnowszych. Wpisz więcej numeru, by znaleźć starsze.',
      nothingFound: 'Nie znaleziono takiego dokumentu.',
      alreadyInDay: 'Już jest w tym dniu',
      pullIn: 'Dołącz',
      pullInReason: 'Powód dołączenia',
      checksPending: 'Zapisuję zmiany — to, co niżej, może się jeszcze zmienić.',
      submit: 'Złóż raport',
      return: 'Zwróć do kasjera',
      returnReason: 'Powód zwrotu',
      reopen: 'Otwórz ponownie',
      reopenReason: 'Powód ponownego otwarcia',
      revisions: 'Wersje',
      revisionLine: 'Wersja {{number}} — {{name}}, {{at}}',
      comments: 'Komentarze',
      noComments: 'Brak komentarzy.',
      addComment: 'Dodaj komentarz',
      sendComment: 'Wyślij komentarz',
    },
    reports: {
      notSubmitted: 'Nie złożono',
      floatTarget: 'Docelowo {{amount}}',
      title: 'Historia raportów',
      subtitle: 'Każdy dzień punktu: stan, gotówka, przekazanie i uwagi. Kliknij dzień, aby zobaczyć szczegóły.',
      subtitleAcceptor: 'Każdy dzień punktu: stan, gotówka, przekazanie i uwagi. Kliknij dzień, aby zobaczyć szczegóły i od razu go zatwierdzić.',
      allDesks: 'Wszystkie punkty',
      deskPending: '{{name}} · {{count}} do zatwierdzenia',
      filters: 'Które dni',
      filter: {
        pending: 'Do zatwierdzenia · {{n}}',
        notes: 'Z uwagami · {{n}}',
        all: 'Wszystkie dni · {{n}}',
      },
      empty: {
        pending: 'Nic nie czeka na zatwierdzenie.',
        notes: 'Żaden dzień nie ma uwag.',
        all: 'Brak dni w tym miesiącu.',
      },
      col: {
        day: 'Dzień',
        state: 'Stan',
        takings: 'Utarg gotówk.',
        difference: 'Różnica',
        handover: 'Przekazanie',
        after: 'W kasie',
        notes: 'Uwagi',
      },
      state: {
        accepted: 'Zatwierdzony',
        pending: 'Do zatwierdzenia',
        submitted: 'Złożony',
        returned: 'Zwrócony do poprawy',
        draft: 'W trakcie',
        missing: 'Brak raportu',
        notStarted: 'Nie rozpoczęty',
      },
      handover: {
        open: '—',
        openSub: 'dzień niezamknięty',
        held: 'Wstrzymane',
        heldSub: 'wróci po poprawie dnia',
        none: 'Bez przekazania',
        noneSub: 'nic do przekazania',
        sent: 'W drodze',
        sentSub: 'wysłano {{at}}',
        accepted: 'Przyjęte',
        acceptedSub: '{{name}}, {{at}}',
      },
      note: {
        returned: 'Do poprawy',
        explanation: 'Wyjaśnienie',
      },
      totalsOf: 'Razem: {{month}}',
      handedTotal: 'przekazano {{amount}}',
      bulk: 'Zatwierdź bez uwag · {{n}}',
      bulkQuestion_one: 'Zatwierdzić {{count}} dzień bez uwag?',
      bulkQuestion_few: 'Zatwierdzić {{count}} dni bez uwag?',
      bulkQuestion_many: 'Zatwierdzić {{count}} dni bez uwag?',
      bulkQuestion_other: 'Zatwierdzić {{count}} dnia bez uwag?',
      bulkConfirm: 'Zatwierdź',
      progress: 'Zatwierdzam {{done}} z {{total}}…',
      result: 'Zatwierdzono {{accepted}} z {{total}}.',
      failed: 'Nie zatwierdzono:',
      skipped: 'pominięty, bo wcześniejszy dzień tego punktu nie jest zatwierdzony',
      pendingElsewhere_one: '{{count}} dzień czeka na zatwierdzenie w innym miesiącu.',
      pendingElsewhere_few: '{{count}} dni czekają na zatwierdzenie w innym miesiącu.',
      pendingElsewhere_many: '{{count}} dni czeka na zatwierdzenie w innym miesiącu.',
      pendingElsewhere_other: '{{count}} dnia czeka na zatwierdzenie w innym miesiącu.',
      showMonth: 'Pokaż: {{month}}',
      outstanding: {
        title: 'Przekaż zaległą gotówkę',
        line: 'Punkt ma do przekazania {{amount}}.',
      },
      float: {
        banner: 'Kasa główna wysłała pogotowie kasowe: {{amount}}',
        bannerHint: 'policz i przyjmij, zanim zamkniesz dzień',
        accept: 'Przyjmij pogotowie',
        title: 'Przyjmij pogotowie kasowe',
        from: 'z: {{desk}}',
        should: 'Przekazanie na',
        footnote: 'Przyjęte pogotowie wejdzie do gotówki dnia, który zamykasz.',
        accepted: 'Przyjęto pogotowie: {{amount}}.',
        refused: 'Pogotowie odrzucone. Kasa główna wyśle je ponownie.',
      },
      drawer: {
        submittedBy: 'złożono: {{name}}, {{at}}',
        acceptedBy: 'zatwierdzono: {{name}}, {{at}}',
        version: 'Wersja {{number}}',
        expected: 'Gotówka wyliczona',
        counted: 'Gotówka policzona',
        after: 'W kasie po przekazaniu',
        handover: 'Przekazanie do kasy głównej',
        checks: 'Uwagi systemu',
        returnedBecause: 'Zwrócony ({{name}}): {{reason}}',
        explanation: 'Wyjaśnienie kasjera',
        comments: 'Komentarze',
        openTitle: 'Dzień jeszcze otwarty',
        openBody: 'Kasjer go jeszcze nie złożył.',
        returnedTitle: 'Dzień wrócił do kasjera',
        returnedBody: 'Kasjer go poprawia; złożony ponownie, wróci tu do zatwierdzenia.',
        accept: 'Zatwierdź',
        return: 'Zwróć do poprawy',
        returnWith: 'Zwróć z tym opisem',
        returnReason: 'Co poprawić (zobaczy kasjer)',
        then: 'Potem: {{day}}',
        nextOne: 'Następny: {{day}}.',
        nothingMore: 'Nic więcej nie czeka.',
        accepted: '{{day}} zatwierdzony.',
        returned: '{{day}} zwrócony do poprawy.',
        own: 'To Twój raport: zatwierdza go ktoś inny — cztery oczy.',
        fix: 'Popraw w raporcie dnia',
        fixReturned: 'Kierownik zwrócił dzień. Popraw go i złóż ponownie — przekazanie wróci z właściwą kwotą.',
        fixOpen: 'Dzień czeka na zamknięcie.',
        openReport: 'Otwórz cały raport',
      },
    },
    transfers: {
      amount: 'Kwota',
      bag: 'Numer worka',
      note: 'Notatka',
      noteRequired: 'Notatka (wymagana, bo kwota jest inna niż do przekazania)',
      noMainDesk: 'Ten punkt nie ma ustawionej kasy głównej, więc nie może wysłać gotówki. Poproś dyrektora o ustawienie jej w Ustawieniach.',
      bookedAtOnce: 'Przeniesienie między kasami głównymi jest księgowane od razu, bez liczenia.',
    },
    main: {
      title: 'Kasa główna',
      noMainDesk: 'Brak kasy głównej do pokazania.',
      open: 'Otwarty',
      closed: 'Zamknięty',
      bookTitle: 'Księga',
      lineCount_one: '{{count}} wpis',
      lineCount_few: '{{count}} wpisy',
      lineCount_many: '{{count}} wpisów',
      lineCount_other: '{{count}} wpisu',
      noLines: 'Brak wpisów w tym miesiącu.',
      total: 'Razem w miesiącu',
      lp: 'Lp.',
      date: 'Data',
      document: 'Dowód',
      kind: 'Rodzaj',
      description: 'Opis',
      counterparty: 'Kontrahent',
      in: 'Przychód',
      out: 'Rozchód',
      actions: 'Akcje',
      amount: 'Kwota',
      editLine: 'Zmień wpis',
      deleteLine: 'Usuń wpis',
      editDate: 'Zmień datę w kasie głównej',
      openPayouts: 'Otwórz wypłaty',
      openReport: 'Raport dnia {{day}}',
      kindTakings: 'Utarg z punktu',
      groupTakings: 'Grupuj utargi według punktu',
      expandGroup: 'Rozwiń utargi: {{desk}}',
      collapseGroup: 'Zwiń utargi: {{desk}}',
      takingsTotal_one: 'Utarg łącznie · {{count}} dzień',
      takingsTotal_few: 'Utarg łącznie · {{count}} dni',
      takingsTotal_many: 'Utarg łącznie · {{count}} dni',
      takingsTotal_other: 'Utarg łącznie · {{count}} dnia',
      bank: 'Bank · rachunek firmy',
      line: {
        takings: 'Utarg {{day}}',
        takingsBag: 'Utarg {{day}} · worek {{bag}}',
        bag: 'worek {{bag}}',
        transfer: 'Przekazanie',
        payroll: 'Lista płac {{period}} · {{parts}}',
      },
      balanceTitle: 'Stan kasy',
      balanceOn: 'Stan na {{date}}',
      balanceNow: 'Stan teraz',
      balanceAtEnd: 'Stan na koniec miesiąca',
      payoutsDone: 'Wypłaty za {{period}} · wszystko wydane',
      payoutsLink: 'Wypłaty za {{period}} · {{n}} do wydania',
      payoutsBack_one: '{{count}} zwrot',
      payoutsBack_few: '{{count}} zwroty',
      payoutsBack_many: '{{count}} zwrotów',
      payoutsBack_other: '{{count}} zwrotu',
      closedBy: 'Zamknięto: {{name}}, {{at}}.',
      closesFrom: 'Miesiąc jeszcze trwa. Zamkniesz go od {{date}}.',
      provisional: 'wstępny',
      provisionalHint: 'Wpis wstępny: po nim stan kasy spada poniżej zera — w kasie B poniżej tego, co może pożyczyć z kasy A. Dodaj wcześniejszy wpis, np. pożyczkę, który pokryje brak.',
      shortfall: 'Wpisy wstępne: brakuje {{amount}}. Dodaj wcześniejszy wpis, np. pożyczkę, który pokryje brak — wtedy przestaną być wstępne.',
      borrowedByB: 'Kasa B pożyczyła',
      cashInHand: 'W kasie faktycznie',
      closeShortfall: 'Miesiąca nie zamkniesz: są wpisy wstępne, brakuje {{amount}}.',
      closeMonth: 'Zamknij miesiąc',
      reopenMonth: 'Otwórz miesiąc ponownie',
      reopenWho: 'Otworzyć miesiąc może tylko dyrektor.',
      closeTitle: 'Zamknij: {{month}}',
      closeConfirm: 'Zatwierdź {{amount}} i zamknij',
      closeCarries: 'Ta kwota będzie stanem na początek następnego miesiąca.',
      closeInTransit_one: '{{count}} przekazanie ({{amount}}) czeka na przyjęcie. Po zamknięciu przyjmiesz je już w następnym miesiącu.',
      closeInTransit_few: '{{count}} przekazania ({{amount}}) czekają na przyjęcie. Po zamknięciu przyjmiesz je już w następnym miesiącu.',
      closeInTransit_many: '{{count}} przekazań ({{amount}}) czeka na przyjęcie. Po zamknięciu przyjmiesz je już w następnym miesiącu.',
      closeInTransit_other: '{{count}} przekazania ({{amount}}) czeka na przyjęcie. Po zamknięciu przyjmiesz je już w następnym miesiącu.',
      closeFinal: 'Po zamknięciu wpisów tego miesiąca nie zmienisz. Otworzyć miesiąc może tylko dyrektor.',
      closedTitle: 'Miesiąc zamknięty',
      closedBody: 'Stan {{amount}} przeszedł na początek następnego miesiąca. Wpisów tego miesiąca nie dodasz ani nie zmienisz.',
      addTitle: 'Nowy wpis',
      editTitle: 'Zmień wpis {{what}}',
      lineRef: 'Lp. {{lp}} · {{date}}',
      chip: {
        expense: 'Rozchód',
        income: 'Przychód',
        loan: 'Pożyczka',
        repayment: 'Zwrot pożyczki',
        funding: 'Zasilenie',
        defunding: 'Zwrot zasilenia',
        deposit: 'Wpłata do banku',
        withdrawal: 'Wypłata z banku',
        move: 'Przekazanie gotówki',
      },
      flow: { out: 'Wychodzi z kasy', in: 'Wchodzi do kasy' },
      voucherNumber: 'Numer {{symbol}} nada system.',
      voucherNumbered: 'Dowód {{number}} nadał system.',
      documentPlaceholder: 'np. FV/2026/513',
      counterpartyPlaceholder: 'np. ORLEN',
      lenderPlaceholder: 'np. Jan Kowalski',
      descriptionPlaceholder: 'Za co, krótko',
      add: 'Dodaj wpis',
      saveChanges: 'Zapisz zmiany',
      why: {
        document: 'Wpisz numer dowodu: bez niego wpisu nie dodasz.',
        amount: 'Wpisz kwotę.',
        counterparty: 'Wpisz, kto pożycza kasie pieniądze lub komu kasa je oddaje.',
        date: 'Data musi być w miesiącu: {{month}}.',
        target: 'Wybierz, do której kasy idzie gotówka.',
      },
      flash: {
        added: 'Dodano wpis: {{what}}.',
        saved: 'Zapisano zmiany: {{what}}.',
        deleted: 'Usunięto wpis: {{what}}.',
        dated: '{{what}} stoi teraz pod datą {{date}}.',
      },
      deleteTitle: 'Usunąć wpis {{what}}?',
      noCounterparty: 'Bez kontrahenta',
      deleteRises: 'Stan kasy wzrośnie o {{by}} i wyniesie {{balance}}.',
      deleteFalls: 'Stan kasy zmaleje o {{by}} i wyniesie {{balance}}.',
      keep: 'Zostaw',
      linked: {
        title: {
          takings: 'Utarg z punktu · {{name}}',
          in: 'Przekazanie od · {{name}}',
          out: 'Przekazanie do · {{name}}',
          payout: 'Wypłata · {{name}}',
          return: 'Zwrot wypłaty · {{name}}',
        },
        fromTransfer: 'Wpis z przekazania',
        fromPayroll: 'Wpis z listy płac',
        payroll: 'Lista płac',
        paid: 'Wydano',
        returned: 'Przyjęto zwrot',
        reportDate: 'Dzień utargu',
        bag: 'Worek',
        sent: 'Wysłano',
        accepted: 'Przyjęto',
        date: 'Data w kasie głównej',
        transferHint: 'Zmienia tylko dzień, pod którym przekazanie stoi w księdze. Kwotę, worek i dzień utargu zmienia się w przekazaniu.',
        payoutHint: 'Zmienia tylko dzień, pod którym wypłata stoi w księdze. Kwoty zmienia się w liście płac.',
        save: 'Zapisz datę',
      },
      toAccept_one: 'Do przyjęcia · {{count}} utarg',
      toAccept_few: 'Do przyjęcia · {{count}} utargi',
      toAccept_many: 'Do przyjęcia · {{count}} utargów',
      toAccept_other: 'Do przyjęcia · {{count}} utargu',
      move: {
        pick: '— wybierz —',
        hint: 'Gotówka z tej kasy B do innej kasy B albo do punktu jako pogotowie kasowe.',
        to: 'Do kasy',
        groupMain: 'Inna kasa B — od razu w obu księgach',
        groupSales: 'Pogotowie kasowe — punkt przyjmie u siebie',
        hintMain: 'Wpis w tej księdze z numerem KW; w księdze tamtej kasy B stoi od razu po drugiej stronie.',
        hintSales: 'Pogotowie: punkt przyjmie gotówkę u siebie; do tego czasu jest w drodze.',
        numbered: 'Numer KW nada system.',
        send: 'Wyślij gotówkę',
        sendAmount: 'Wyślij {{amount}}',
        flash: 'Wysłano {{amount}} do: {{desk}}.',
      },
    },
    payoutPart: {
      a: 'Wynagr. A',
      b: 'Wynagr. B',
      bonus: 'Premia B',
      sc: 'Service Charge',
    },
    payouts: {
      payrollMonth: 'Lista płac',
      previousMonth: 'Poprzedni miesiąc listy płac',
      nextMonth: 'Następny miesiąc listy płac',
      bookMonth: 'Wpisy idą do księgi: {{month}}',
      tabs: 'Które wypłaty',
      tab: {
        todo: 'Do rozliczenia · {{n}}',
        done: 'Wydane · {{n}}',
      },
      nothingTodo: 'Nic do rozliczenia. Gotowe wypłaty za {{month}} są wydane.',
      nothingDone: 'Jeszcze nic nie wydano.',
      employee: 'Pracownik',
      left: 'Zostało',
      flag: {
        notReady: 'Nie jest już Gotowe',
        changed: 'Zmieniono po wydaniu',
        partly: 'Część wydana',
      },
      changedTitle: '{{part}}: wydano {{paid}}, lista płac teraz {{now}}',
      paidTitle: '{{part}}: wydano {{date}} · {{number}}',
      paidMark: 'wydano',
      returnBadge: 'Zwrot do kasy',
      undo: 'Cofnij',
      inBook: 'Otwórz w księdze',
      undoQuestion: 'Cofnąć {{number}}?',
      flashUndone: 'Cofnięto {{number}} ({{name}}). Wpis zniknął z księgi.',
      back: 'Do zwrotu do kasy',
      people_one: '{{count}} osoba',
      people_few: '{{count}} osoby',
      people_many: '{{count}} osób',
      people_other: '{{count}} osoby',
      toPay: 'Do wydania',
      cashNow: 'W kasie B teraz',
      cashShort: 'W kasie B · za mało na wszystkie',
      waitingTitle: 'Czekają na „Gotowe” · {{n}}',
      waitingHint: 'Pojawią się do wydania, gdy ktoś oznaczy je w liście płac.',
      openPayroll: 'Otwórz listę płac',
      changedTitleNote: 'Lista płac zmieniła się po wydaniu',
      changedLine: '{{part}}: wydano {{paid}} ({{number}}), teraz {{now}}.',
      changedTopUp: 'Dopłać {{amount}}.',
      changedBack: 'Pracownik oddaje {{amount}} do kasy.',
      partsLegend: 'Co rozliczasz teraz',
      paidOn: 'wydano {{date}} · {{number}}',
      cannotPay: 'Nie możesz wydawać wypłat z tej kasy.',
      confirmPaid: 'Wydano {{amount}}',
      confirmBack: 'Przyjęto zwrot {{amount}}',
      flashPaid: 'Wydano {{amount}}: {{name}} · {{number}}.',
      flashBack: 'Przyjęto zwrot {{amount}}: {{name}} · {{number}}.',
      drawerTitle: 'Wypłaty gotówkowe',
      action: {
        pay: 'Wydaj',
        topUp: 'Dopłać',
        back: 'Zwrot',
      },
    },
    overview: {
      title: 'Przegląd',
      subtitle: 'Stan każdej kasy głównej na koniec każdego miesiąca roku',
      previousYear: 'Poprzedni rok',
      nextYear: 'Następny rok',
      yearTitle: 'Rok {{year}}',
      yearHint: 'Stan na koniec miesiąca. Kliknij miesiąc, aby zobaczyć szczegóły poniżej.',
      desk: 'Kasa',
      stash: 'kasa {{stash}}',
      subtotal: 'Razem {{company}}',
      total: 'Razem wszystkie kasy',
      yearEnd: 'Koniec roku',
      inTransit: 'W drodze teraz',
      monthTitle: 'Miesiąc: {{month}}',
      futureMonth: 'Ten miesiąc jeszcze się nie zaczął.',
      opening: 'Otwarcie',
      in: 'Wpływy',
      out: 'Wydatki',
      closing: 'Zamknięcie',
    },
    settings: {
      floatTarget: 'Stan po przekazaniu (docelowy)',
      floatTargetHint: 'Ile ma zostać w kasie po przekazaniu: 0 w restauracji, stały zapas na recepcji.',
      shareholder: 'Udziałowiec (pożyczki)',
      shareholderHint: 'Pożyczki kasy A pochodzą tylko od niego: wpisze się sam jako kontrahent pożyczki i jej zwrotu.',
      seriesPrinter: 'Drukarka',
      seriesPrinterFor: 'Drukarka dla serii {{series}}',
      noSeriesPrinter: '— nie ustawiono —',
      seriesNoPrinterWarning: 'Bez drukarki — sugestia VAT jej nie obejmie.',
      title: 'Ustawienia',
      subtitle: 'Punkty kasowe, ich źródła danych, serie i rodzaje płatności, drukarki i terminale, progi i kategorie księgi',
      readOnly: 'Tylko podgląd — zmiany wprowadza dyrektor lub administrator.',
      desksTitle: 'Punkty kasowe',
      desksHint: 'Punktu się nie usuwa, tylko dezaktywuje — jego raporty zostają.',
      deskName: 'Nazwa',
      dayStart: 'Początek dnia',
      dayStartHint:
        'Dokument należy do dnia swojej daty wystawienia; wystawiony przed tą godziną należy do dnia poprzedniego.',
      openingCash: 'Stan początkowy',
      openingCashHint: 'Gotówka przed pierwszym raportem.',
      openingDate: 'Pierwszy dzień',
      active: 'Aktywny',
      activeFor: 'Aktywny: {{name}}',
      position: 'Kolejność',
      activate: 'Aktywuj',
      deactivate: 'Dezaktywuj',
      addDesk: 'Dodaj punkt',
      editDesk: 'Edytuj punkt: {{name}}',
      deskTitle: 'Punkt: {{name}}',
      deskHint: 'Źródła danych, z których punkt pobiera dokumenty, a dla każdego serie i rodzaje płatności; oraz drukarki i terminale.',
      sourcesTitle: 'Źródła danych',
      sourcesHint: 'Dla każdego źródła: serie, które punkt rozlicza, i czym dla jego kasy jest każdy rodzaj płatności.',
      noDeskSources: 'Punkt nie pobiera jeszcze danych z żadnego źródła.',
      addSource: 'Dodaj źródło',
      addSourceLabel: '— wybierz źródło —',
      removeSource: 'Usuń źródło',
      removeSourceQuestion: 'Usunąć {{name}} z punktu? Jego serie i rodzaje płatności znikną z tego punktu.',
      seriesTitle: 'Serie dokumentów',
      noSeries: 'Brak serii.',
      seriesElsewhere: 'Przypisana do: {{desk}}',
      seriesMoves: 'Zostanie przeniesiona z: {{desk}}',
      saveSeries: 'Zapisz serie',
      printersTitle: 'Drukarki fiskalne',
      terminalsTitle: 'Terminale',
      deviceName: 'Nazwa',
      printerModel: 'Model',
      uniqueNumber: 'Numer unikatowy',
      tid: 'TID',
      addPrinter: 'Dodaj drukarkę',
      addTerminal: 'Dodaj terminal',
      paymentTypesTitle: 'Rodzaje płatności',
      paymentTypesOf: 'Rodzaje płatności: {{name}}',
      unmapped_one: '{{count}} rodzaj płatności nie jest przypisany.',
      unmapped_few: '{{count}} rodzaje płatności nie są przypisane.',
      unmapped_many: '{{count}} rodzajów płatności nie jest przypisanych.',
      unmapped_other: '{{count}} rodzaju płatności nie jest przypisane.',
      unmappedOption: '— nieprzypisany —',
      noPaymentTypes: 'Źródło nie przysłało jeszcze rodzajów płatności.',
      paymentType: 'Kod',
      paymentName: 'Nazwa',
      paymentKind: 'Dla kasy',
      paymentKindFor: 'Dla kasy: {{type}}',
      thresholdsTitle: 'Progi kontroli',
      thresholdsHint: 'W złotych.',
      threshold: {
        fiscalTolerance: 'Tolerancja raportu fiskalnego',
        systemTolerance: 'Tolerancja względem systemu',
        cashWarning: 'Różnica gotówki — ostrzeżenie',
        cashError: 'Różnica gotówki — błąd',
      },
      companiesTitle: 'Spółki i kasy główne',
      companiesHint: 'Spółki, których gotówkę prowadzi kasjer główny. Każda ma dwie kasy główne: A i B.',
      noMainDesks: 'Żadna spółka nie ma jeszcze kas głównych.',
      companyName: 'Spółka',
      company: 'Spółka',
      companyOff: 'wyłączona',
      addMainDesks: 'Dodaj kasy główne',
      addMainDesksHint: 'Powstają dwie kasy główne spółki: kasa A i kasa B. Spółki dodaje się w module Administracja.',
      everyCompanyHasMainDesks: 'Każda włączona spółka ma już kasy główne. Nową spółkę dodaje się w module Administracja.',
      companiesLink: 'Administracja → Firmy',
      noSources: 'Nie ma włączonego źródła danych, które punkt mógłby dodać. Źródła dodaje się w module Administracja.',
      sourcesLink: 'Administracja → Źródła danych',
      sourceOff: 'wyłączone — serie można tylko odpiąć',
      salesDesksTitle: 'Punkty sprzedaży',
      mainDesksTitle: 'Kasy główne',
      mainDesksHint: 'Kasy główne dodaje się dla spółki, po dwie: A i B.',
      mainDeskLabel: '{{company}} — kasa {{stash}}',
      stash: 'Kasa',
      target: 'Przekazuje do',
      targetHint: 'Kasa główna dowolnej spółki, do której ten punkt przekazuje gotówkę. Kasjer nie wybiera jej przy wysyłce.',
      noTarget: '— nie ustawiono —',
    },
    checkShort: {
      FISCAL_NOT_VALID: 'Fiskalny',
      TERMINAL_NOT_VALID: 'Terminal',
      SYSTEM_CASH_NOT_VALID: 'Gotówka ≠ izzy',
      SYSTEM_CARD_NOT_VALID: 'Karta ≠ izzy',
      SYSTEM_TOTAL_NOT_VALID: 'Suma ≠ izzy',
      SMALL_CASH_DIFF: 'Mała różnica',
      LARGE_CASH_DIFF: 'Duża różnica',
      PREVIOUS_REPORT_NOT_APPROVED: 'Poprzedni niezatwierdzony',
      NO_PREVIOUS_REPORT: 'Brak poprzedniego',
      MISSING_REPORT: 'Brak raportu za dzień',
      FISCAL_DATE_MISMATCH: 'Data wydruku',
      SYSTEM_DATA_STALE: 'Stare dane izzy',
      DOCUMENT_COUNTED_TWICE: 'Liczony dwa razy',
      FISCAL_MISSING: 'Brak fiskalnego',
      TERMINAL_MISSING: 'Brak terminala',
    },
    handovers: {
      report: {
        clear: 'Raport bez uwag',
        flagged: 'Raport z uwagami',
        difference: 'różnica gotówki {{amount}}',
        state: {
          draft: 'w trakcie',
          submitted: 'złożony, czeka na kierownika',
          returned: 'zwrócony do poprawy',
          accepted: 'zatwierdzony',
        },
        open: 'Otwórz raport',
      },
      title: 'Przyjmij utargi',
      waiting: 'do przyjęcia: {{count}} · {{amount}}',
      allIn: 'wszystko przyjęte',
      allInLong: 'Nic więcej nie czeka. Kafelek „Do przyjęcia” zniknie z księgi i wróci przy następnym przekazaniu.',
      day: 'dzień {{date}}',
      byHand: 'przekazanie ręczne',
      byHandBadge: 'Ręczne',
      settled: 'Załatwione teraz',
      outcome: {
        accepted: 'przyjęto',
        returned: 'dzień zwrócony do poprawy',
        refused: 'odrzucono, nadawca wyśle ponownie',
      },
      flash: '{{desk}}: {{what}}.',
      floats: 'Pogotowie w drodze do punktów',
      floatLine: '{{desk}} · wysłano {{at}} · czeka, aż punkt przyjmie',
      footnote: 'Przyjęty utarg trafia do księgi z numerem KP, pod dniem przyjęcia. Datę zmienisz w księdze ołówkiem przy wpisie.',
    },
  },
  dataSources: {
    filter: 'Źródło',
    title: 'Źródła danych',
    gastronomyHint:
      'Bazy Izzy Rest, które czyta gastronomia. Źródło dodaje się na Administracja → Źródła danych; tu decydujesz, czy gastronomia je przelicza. Punkty sprzedaży, serie, grupy i produkty poniżej należą do jednego źródła.',
    hotelHint:
      'Bazy Izzy Hotel, które czyta hotel — każda to osobny obiekt. Źródło dodaje się na Administracja → Źródła danych; tu decydujesz, czy hotel je przelicza. Pokoje, najmy i blokady poniżej należą do jednego źródła.',
    cateringHint:
      'Bazy Izzy Rest stołówek — każda to osobna stołówka u klienta. Źródło dodaje się na Administracja → Źródła danych; tu decydujesz, czy stołówki je przeliczają. Powody rozliczenia poniżej należą do jednego źródła.',
    none: 'Moduł nie czyta jeszcze żadnego źródła.',
    off: 'wyłączone',
    progress: 'przeliczone {{derived}} z {{staged}} dni',
    failed_one: '{{count}} dzień z błędem',
    failed_few: '{{count}} dni z błędem',
    failed_many: '{{count}} dni z błędem',
    failed_other: '{{count}} dnia z błędem',
    lastPush: 'ostatnia przesyłka {{at}}',
    never: 'jeszcze nic nie przysłało',
    remove: 'Usuń z listy',
    removeConfirm: 'Moduł przestanie czytać źródło {{name}}. Przeliczone dane zostaną, ale nie będą pokazywane. Usunąć?',
    addLabel: 'Źródło do dodania',
    addPlaceholder: 'Wybierz źródło',
    add: 'Dodaj do listy',
    noCandidates: 'Nie ma innych źródeł tego rodzaju — dodaje się je na Administracja → Źródła danych.',
  },
  admin: {
    role: {
      admin: 'Administrator',
      hr_manager: 'Kierownik HR',
      hr_administrator: 'Administrator HR',
      hr_viewer: 'Podgląd HR',
      department_supervisor: 'Kierownik działu',
      gastronomy_manager: 'Kierownik gastronomii',
      gastronomy_viewer: 'Podgląd gastronomii',
      catering_manager: 'Kierownik stołówek',
      catering_site_manager: 'Kierownik stołówki',
      catering_viewer: 'Podgląd stołówek',
      hotel_manager: 'Kierownik hotelu',
      hotel_viewer: 'Podgląd hotelu',
      cashier_admin: 'Administrator kas',
      cash_desk_manager: 'Kierownik kas',
      main_cashier: 'Kasjer główny',
      cashier: 'Kasjer',
      cashier_viewer: 'Podgląd kas',
    },
    /** One line per permission in the catalogue (`PERMISSIONS`). */
    permission: {
      cashierPayouts: 'Wypłaty gotówkowe z listy płac',
      adminUsers: 'Użytkownicy i role',
      adminSettings: 'Ustawienia wspólne: firmy, źródła danych, pogoda',
      adminLogs: 'Logi aplikacji: błędy, ostrzeżenia, żądania',
      hrRead: 'Podgląd kadr: pracownicy, ewidencja',
      hrEmployeesRead: 'Kartoteka pracownika: umowy, firma, karta',
      hrAttendanceCorrect: 'Korekty obecności',
      hrAttendanceCorrectPast: 'Poprawianie zakończonych miesięcy',
      hrEmployeesWrite: 'Pracownicy, umowy i karty: zmiany',
      hrHistoryRead: 'Historia zmian pracownika',
      hrCostsRead: 'Stawki i koszty',
      hrReportsRead: 'Raporty kadrowe',
      hrPayrollRead: 'Podgląd wypłat',
      hrPayrollWrite: 'Wypłaty: zmiany i import',
      hrPayrollClose: 'Zamknięcie i otwarcie miesiąca wypłat',
      hrReferenceWrite: 'Słowniki: działy, stanowiska',
      hrRecalculate: 'Przeliczenie miesiąca',
      hrReadersManage: 'Czytniki kart',
      hotelRead: 'Podgląd hotelu',
      hotelSettings: 'Ustawienia hotelu i kwalifikacja nocy',
      gastronomyRead: 'Podgląd gastronomii',
      gastronomySettings: 'Ustawienia gastronomii',
      gastronomyRemarks: 'Uwagi sprzedaży w wariantach',
      cateringRead: 'Podgląd stołówek',
      cateringSettle: 'Rozliczenie miesiąca stołówek',
      cateringSettings: 'Ustawienia stołówek',
      cashierRead: 'Podgląd punktów kasowych',
      cashierReport: 'Raport dnia i wysyłka gotówki',
      cashierAccept: 'Przyjęcie raportów i przekazań',
      cashierMainDesk: 'Prowadzenie kasy głównej',
      cashierOverview: 'Stan wszystkich kas głównych',
      cashierSettings: 'Ustawienia kas',
      cashierMonthReopen: 'Ponowne otwarcie miesiąca kasy',
      cashierAcceptOwn: 'Przyjęcie własnego raportu bez drugiej osoby',
    },
    scope: {
      costCentre: 'Działy',
      revenueCentre: 'Punkty gastronomii',
      canteen: 'Stołówki',
      cashDesk: 'Punkty kasowe',
      allCostCentres: 'wszystkie działy',
      someCostCentres: 'wybrane działy',
      allRevenueCentres: 'wszystkie punkty gastronomii',
      someRevenueCentres: 'wybrane punkty gastronomii',
      allCanteens: 'wszystkie stołówki',
      someCanteens: 'wybrane stołówki',
      allCashDesks: 'wszystkie punkty kasowe',
      someCashDesks: 'wybrane punkty kasowe',
      noCostCentres: 'Nie ma jeszcze żadnego działu.',
      noRevenueCentres: 'Nie ma jeszcze żadnego punktu gastronomii.',
      noCanteens: 'Nie ma jeszcze żadnej stołówki.',
      noCashDesks: 'Nie ma jeszcze żadnego punktu kasowego.',
    },
    roles: {
      new: 'Nowa rola',
      newTitle: 'Nowa rola',
      editTitle: 'Rola: {{name}}',
      viewTitle: 'Rola systemowa: {{name}}',
      create: 'Utwórz',
      name: 'Nazwa',
      kind: 'Rodzaj',
      system: 'systemowa',
      custom: 'własna',
      scopes: 'Zakres',
      holders: 'Osoby',
      empty: 'Brak ról.',
      view: 'Pokaż',
      systemDescription: 'Rola systemowa jest zdefiniowana w kodzie. Nie można jej zmienić ani usunąć.',
      editDescription: 'Zapis zastępuje całą listę uprawnień roli.',
      editHeldDescription_one: 'Tę rolę ma {{count}} osoba. Zapis zmieni jej uprawnienia od razu.',
      editHeldDescription_few: 'Tę rolę mają {{count}} osoby. Zapis zmieni ich uprawnienia od razu.',
      editHeldDescription_many: 'Tę rolę ma {{count}} osób. Zapis zmieni ich uprawnienia od razu.',
      editHeldDescription_other: 'Tę rolę ma {{count}} osoby. Zapis zmieni ich uprawnienia od razu.',
      heldBy_one: 'Ma ją {{count}} osoba. Najpierw odbierz rolę.',
      heldBy_few: 'Mają ją {{count}} osoby. Najpierw odbierz rolę.',
      heldBy_many: 'Ma ją {{count}} osób. Najpierw odbierz rolę.',
      heldBy_other: 'Ma ją {{count}} osoby. Najpierw odbierz rolę.',
      confirmDelete: 'Usunąć rolę {{name}}?',
      companyWide: 'cała firma',
      narrowedBy: 'zawężane: {{type}}',
      writes: 'zmienia dane',
      everywhere: 'wszędzie, bez zakresu',
      marksHelp:
        '„Cała firma”: uprawnienie działa wszędzie. „Zawężane”: działa tylko w zakresie, który wybierasz, gdy dajesz rolę osobie.',
      pickAPermission: 'Zaznacz co najmniej jedno uprawnienie.',
    },
    companies: {
      subtitle: 'Firmy, które zatrudniają w umowach i na listach płac i których gotówkę prowadzą kasy główne.',
      new: 'Nowa firma',
      newTitle: 'Nowa firma',
      editTitle: 'Firma: {{name}}',
      dialogDescription: 'Dane firmy są wspólne dla kadr, wypłat i kas.',
      create: 'Dodaj',
      name: 'Nazwa',
      taxId: 'NIP',
      address: 'Adres',
      optimaPayerName: 'Płatnik w Optimie',
      optimaPayerNameHint:
        'Dokładna nazwa firmy z nagłówka listy płac z Optimy. Łączy importy wypłat z tą firmą. Zostaw puste, jeśli nieużywane.',
      position: 'Kolejność',
      positionHint: 'Mniejsza liczba: wyżej na listach.',
      positionInvalid: 'Podaj liczbę całkowitą, 0 lub więcej.',
      active: 'Włączona',
      activeHint: 'Dla wyłączonej firmy nie dodaje się kas głównych. Umowy, wypłaty i kasy, które ją wskazują, zostają.',
      off: 'wyłączona',
      usage: 'Używana w',
      unused: 'nieużywana',
      agreements_one: '{{count}} umowa',
      agreements_few: '{{count}} umowy',
      agreements_many: '{{count}} umów',
      agreements_other: '{{count}} umowy',
      payrollEntries_one: '{{count}} wpis listy płac',
      payrollEntries_few: '{{count}} wpisy listy płac',
      payrollEntries_many: '{{count}} wpisów listy płac',
      payrollEntries_other: '{{count}} wpisu listy płac',
      desks_one: '{{count}} kasa główna',
      desks_few: '{{count}} kasy główne',
      desks_many: '{{count}} kas głównych',
      desks_other: '{{count}} kasy głównej',
      inUse: 'Firma jest używana. Zamiast usuwać, wyłącz ją.',
      confirmDelete: 'Usunąć firmę {{name}}?',
      empty: 'Brak firm.',
      notAvailable: 'Firmami zarządza tylko osoba z uprawnieniem „Ustawienia wspólne”.',
    },
    logs: {
      subtitle: 'Co zapisało API: błędy, ostrzeżenia i każde żądanie z ostatnich 30 dni. Najnowsze na górze.',
      level: 'Rodzaj',
      levelAll: 'Wszystko',
      levelError: 'Błędy',
      levelWarn: 'Ostrzeżenia',
      levelRequest: 'Żądania',
      badgeError: 'Błąd',
      badgeWarn: 'Ostrzeżenie',
      badgeRequest: 'Żądanie',
      status: 'Odpowiedź',
      statusAny: 'Dowolna',
      status4xx: 'Błąd wywołania (4xx)',
      status5xx: 'Błąd serwera (5xx)',
      searchPlaceholder: 'Treść, ścieżka, osoba',
      to: 'Do godziny',
      time: 'Czas',
      where: 'Gdzie',
      statusCode: 'Status',
      duration: 'Trwało',
      durationValue: '{{ms}} ms',
      user: 'Osoba',
      message: 'Treść',
      context: 'Moduł',
      stack: 'Stos wywołań',
      requestId: 'Identyfikator żądania',
      showRequest: 'Pokaż to żądanie',
      requestFilter: 'Żądanie {{id}}',
      clearRequest: 'Pokaż wszystkie wpisy',
      older: 'Wczytaj starsze',
      empty: 'Brak wpisów.',
      noMatch: 'Żaden wpis nie pasuje do filtrów.',
      notAvailable: 'Logi widzi tylko osoba z uprawnieniem „Logi aplikacji”.',
    },
    sources: {
      subtitle: 'Skąd dane trafiają do Overseera: każda baza izzy, jej token i ostatnia przesyłka.',
      new: 'Nowe źródło',
      newTitle: 'Nowe źródło danych',
      editTitle: 'Źródło: {{name}}',
      dialogDescription:
        'Rodzaj mówi, co przysyła źródło: bazę izzy albo odczyty czytników kart z obiektu. Źródeł jednego rodzaju może być włączonych wiele — dane każdego są osobno.',
      create: 'Dodaj',
      name: 'Nazwa',
      kind: 'Rodzaj',
      kindRest: 'Izzy Rest',
      kindHotel: 'Izzy Hotel',
      kindCardReader: 'Czytniki kart',
      active: 'Włączone',
      activeHint: 'Kolektor wyłączonego źródła nic nie wyśle: jego token jest odrzucany.',
      state: 'Stan',
      on: 'włączone',
      off: 'wyłączone',
      switchOn: 'Włącz',
      switchOff: 'Wyłącz',
      switchOffConfirm: 'Kolektor tego źródła przestanie być przyjmowany. Wyłączyć?',
      token: 'Token',
      tokenSetAt: 'wydany {{at}}',
      tokenNone: 'brak tokenu',
      lastPush: 'Ostatnia przesyłka',
      lastPushNever: 'jeszcze nic',
      lastPushStale: '{{at}} — ponad 24 h temu',
      collectorVersion: 'Wersja kolektora',
      tokenIssue: 'Wydaj token',
      tokenRotate: 'Wydaj nowy token',
      tokenRotateConfirm:
        'Poprzedni token przestanie działać natychmiast, a kolektor nic nie wyśle, dopóki nowy nie trafi do konfiguracji. Wydać?',
      issuedFor: 'Nowy token: {{name}}',
      tokenOnce: 'Zapisz go teraz — nie da się go pokazać drugi raz.',
      tokenCopied: 'Zapisane',
      usage: 'Używane w',
      unused: 'nieużywane',
      desks_one: '{{count}} punkt kasowy',
      desks_few: '{{count}} punkty kasowe',
      desks_many: '{{count}} punktów kasowych',
      desks_other: '{{count}} punktu kasowego',
      stagedDays_one: '{{count}} dzień od kolektora',
      stagedDays_few: '{{count}} dni od kolektora',
      stagedDays_many: '{{count}} dni od kolektora',
      stagedDays_other: '{{count}} dnia od kolektora',
      devices_one: '{{count}} czytnik kart',
      devices_few: '{{count}} czytniki kart',
      devices_many: '{{count}} czytników kart',
      devices_other: '{{count}} czytnika kart',
      derivedBy: 'Czyta',
      derivedByNone: 'żaden moduł — dane tylko przyjmowane',
      derivedByHint: 'Który moduł przelicza źródło, ustawia się w ustawieniach modułu.',
      inUse:
        'Punkty kasowe pobierają dane z tego źródła, czytniki kart do niego należą albo kolektor przysłał już dane. Zamiast usuwać, wyłącz je.',
      confirmDelete: 'Usunąć źródło {{name}}?',
      empty: 'Brak źródeł danych.',
      notAvailable: 'Źródłami danych zarządza tylko osoba z uprawnieniem „Ustawienia wspólne”.',
    },
    users: {
      person: 'Osoba',
      email: 'Adres e-mail',
      you: 'to Ty',
      access: 'Role',
      noRoles: 'brak ról',
      assignmentLine: '{{role}}: {{where}}',
      modules: 'Aplikacje',
      allModules: 'wszystkie (Administrator)',
      lastSeen: 'Ostatnie logowanie',
      neverSignedIn: 'jeszcze nie',
      invitationPending: 'zaproszenie czeka',
      account: 'Konto',
      active: 'Aktywne',
      disabled: 'Zablokowane',
      empty: 'Brak użytkowników.',
      noMatch: 'Nikt nie pasuje do wyszukiwania.',
      searchPlaceholder: 'Nazwa lub adres e-mail',
      notAvailable: 'Użytkownikami zarządza tylko Administrator.',
      invite: 'Zaproś',
      inviting: 'Zapraszanie…',
      inviteTitle: 'Zaproś użytkownika',
      inviteDescription:
        'Cognito wyśle tej osobie e-mail z hasłem tymczasowym. Nie zobaczysz tego hasła.',
      add: 'Dodaj',
      adding: 'Dodawanie…',
      addTitle: 'Dodaj użytkownika',
      addDescription:
        'Ten serwer nie wysyła zaproszeń. Osoba musi już mieć konto; uprawnienia zadziałają przy jej pierwszym logowaniu.',
      edit: 'Edytuj',
      editTitle: 'Uprawnienia: {{name}}',
      editDescription: 'Zapis zastępuje cały zestaw ról tej osoby.',
      displayName: 'Nazwa wyświetlana',
      displayNameHint: 'Pokazywana zamiast adresu e-mail. Puste pole: adres e-mail.',
      roles: 'Role',
      generalRoles: 'Ogólne',
      scopeOf: 'Zakres roli {{role}}: {{type}}',
      history: 'Historia danych',
      historyOf: 'Historia danych roli {{role}}',
      historyUnlimited: 'bez ograniczeń',
      historyLimitedBefore: 'bieżący miesiąc +',
      historyLimitedAfter: 'mies. wstecz',
      historyMonths: 'Liczba miesięcy wstecz',
      historyEvery: 'Wszystkie dane, od początku.',
      historySummary: 'historia: bieżący miesiąc + {{months}}',
      scopeList: '{{names}} ({{type}})',
      inactiveDesk: 'nieaktywny',
      mainDesk: 'kasa główna',
      deletedCanteen: 'usunięta',
      chooseScope: 'Wybierz: wszystkie albo wybrane.',
      pickAtLeastOne: 'Wybierz co najmniej jeden.',
      noModules: 'żadna — ta osoba zaloguje się i nie zobaczy nic',
      modulesFromRoles: 'Wynikają z uprawnień zaznaczonych ról.',
      selfWarning: 'To Twoje konto. Serwer nie pozwoli odebrać Ci roli Administrator.',
      resend: 'Wyślij zaproszenie ponownie',
      resent: 'Zaproszenie wysłane ponownie.',
      disable: 'Zablokuj',
      enable: 'Odblokuj',
      confirmDisable: 'Zablokować konto {{name}}? Ta osoba przestanie się logować.',
      confirmEnable: 'Odblokować konto {{name}}?',
      confirmDisableAccessOnly:
        'Zablokować dostęp {{name}}? Logowanie w Cognito zostaje włączone, ale ta osoba nie zobaczy nic.',
    },
  },
  /**
   * Weather, shown beside the gastronomy figures and set on Administracja → Pogoda.
   *
   * The nine condition names are the closed set the API sends. They are the
   * accessible name of an icon, so they are read aloud as-is — short noun
   * phrases, no sentence, no "pogoda:" prefix that would be repeated three times
   * across one table header.
   */
  weather: {
    condition_clear: 'Bezchmurnie',
    condition_partly: 'Częściowe zachmurzenie',
    condition_cloudy: 'Pochmurno',
    condition_fog: 'Mgła',
    condition_drizzle: 'Mżawka',
    condition_rain: 'Deszcz',
    condition_snow: 'Śnieg',
    condition_showers: 'Przelotne opady',
    condition_thunderstorm: 'Burza',
    settingsNav: 'Pogoda',
    settingsTitle: 'Lokalizacja pogody',
    settingsSubtitle: 'Miejsce, dla którego pobierana jest pogoda pokazywana przy wynikach gastronomii',
    locationName: 'Nazwa miejsca',
    latitude: 'Szerokość geograficzna',
    longitude: 'Długość geograficzna',
    latitudeHint: 'Od -90 do 90, np. 52,23',
    longitudeHint: 'Od -180 do 180, np. 21,01',
    latitudeRange: 'Podaj liczbę od -90 do 90.',
    longitudeRange: 'Podaj liczbę od -180 do 180.',
    nameRequired: 'Podaj nazwę miejsca.',
    noLocation: 'Nie ustawiono jeszcze lokalizacji — pogoda nie jest pokazywana przy wynikach gastronomii.',
    saved: 'Zapisano lokalizację pogody.',
    adminOnly: 'Lokalizacją pogody zarządza tylko osoba z uprawnieniem „Ustawienia wspólne”.',
  },
  period: {
    thisMonth: 'Ten miesiąc',
    lastMonth: 'Poprzedni miesiąc',
    last90: 'Ostatnie 90 dni',
    ytd: 'Od początku roku',
    from: 'Od',
    to: 'Do',
    prevMonth: 'Miesiąc wcześniej',
    nextMonth: 'Miesiąc później',
    date: 'Dzień',
    prevDay: 'Poprzedni dzień',
    nextDay: 'Następny dzień',
    toComparable: 'Przejdź do dnia porównawczego rok wcześniej: {{date}}',
    toComparableAhead: 'Przejdź do dnia, dla którego ten jest porównawczy: {{date}}',
  },
  common: {
    salesLines: {
      product: 'Produkt',
      group: 'Grupa',
      quantity: 'Ilość',
      price: 'Cena',
      vat: 'VAT',
      value: 'Wartość',
      cancelled: 'anulowana',
    },
    yes: 'Tak',
    no: 'Nie',
    close: 'Zamknij',
    cancel: 'Anuluj',
    dismiss: 'Zamknij powiadomienie',
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    edit: 'Edytuj',
    delete: 'Usuń',
    actions: 'Akcje',
    search: 'Szukaj',
    clear: 'Wyczyść',
    range: '{{from}}–{{to}} z {{total}}',
    // Cognito nie podaje liczby wszystkich kont, więc nie ma z czego liczyć
    // „z {{total}}”. Podana jest tylko liczba wierszy na tej stronie.
    previousPage: 'Poprzednia strona',
    nextPage: 'Następna strona',
    loading: 'Ładowanie…',
    retry: 'Spróbuj ponownie',
    never: 'Nigdy',
    refresh: 'Odśwież',
    refreshing: 'Odświeżanie…',
    error: 'Coś poszło nie tak',
    notFound: 'Nie znaleziono strony',
    notFoundDescription: 'Ten adres nie istnieje.',
    backToDashboard: 'Wróć do pulpitu',
    theme: 'Motyw',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',
    themeSystem: 'Systemowy',
    language: 'Język',
    skipToContent: 'Przejdź do treści',
    networkError: 'Brak połączenia z serwerem. Sprawdź sieć i spróbuj ponownie.',
    forbidden: 'Brak uprawnień do tych danych lub tej operacji.',
    forbiddenBecause: 'Brak uprawnień. {{reason}}',
    offline: 'Brak połączenia z internetem — zmiany nie zostaną zapisane.',
  },
} as const;
